import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FaArchive,
  FaBell,
  FaBook,
  FaBookOpen,
  FaBuilding,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaClipboardCheck,
  FaClone,
  FaCog,
  FaDatabase,
  FaLock,
  FaMapMarkedAlt,
  FaMoneyBillWave,
  FaPlus,
  FaRegCalendarAlt,
  FaRegSave,
  FaSchool,
  FaTable,
  FaTasks,
  FaUniversity,
  FaUserGraduate,
  FaUsers,
  FaWrench,
} from 'react-icons/fa';
import { axios } from '../../context/AuthContext';
import asderLogo from '../../images/asderrLogo.png';
import kareKursBulvarLogo from '../../images/karekursbluvar.png';
import { WEEKLY_PROGRAM_REFRESH_EVENT } from '../../services/weeklyProgramRefresh';

const API = '/haftalik-program';
const WRITTEN_EXAM_API = '/written-exams';
const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const DAY_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const COLORS = ['#2563EB', '#E8334A', '#10B981', '#F59E0B', '#7C3AED', '#0891B2', '#DB2777', '#475569', '#65A30D', '#EA580C'];

const emptyForm = {
  subject: { name: '', shortName: '', weeklyHours: 2, distributionFormat: '1+1', color: COLORS[0] },
  teacher: { name: '', shortName: '', branch: '', homeroomClass: '', club: '', email: '' },
};

const valueOf = (obj, keys, fallback = '') => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== '') return obj[key];
  }
  return fallback;
};

const getPayload = (response) => response?.data?.data ?? response?.data ?? {};
const asArray = (value) => Array.isArray(value) ? value : [];
const slotKey = (day, period) => `${day}_${period}`;

function boolValue(value, fallback = false) {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.toLocaleLowerCase('tr-TR').trim();
    if (['true', '1', 'evet', 'aktif', 'active'].includes(normalized)) return true;
    if (['false', '0', 'hayir', 'hayır', 'pasif', 'inactive'].includes(normalized)) return false;
  }
  return Boolean(value);
}

function sumDistributionFormat(value) {
  const clean = String(value || '').replace(/\s+/g, '');
  if (!/^\d+(\+\d+)*$/.test(clean)) return null;
  return clean.split('+').map(Number).reduce((total, part) => total + part, 0);
}

function extractTimetable(payload) {
  return normalizeTimetable(payload?.timetableResult || payload?.timetable_result || payload?.program || payload);
}

function extractPendingLessons(payload) {
  return asArray(payload?.pendingLessons || payload?.pending_lessons || payload?.bekleyenDersler || payload?.lessons || payload);
}

function normalizeAvailability(items) {
  const map = {};
  asArray(items).forEach((item) => {
    const key = valueOf(item, ['slotKey', 'slot_key', 'key'], '');
    if (!key) return;
    const rawStatus = String(valueOf(item, ['status', 'type', 'color', 'durum'], '')).toLowerCase();
    let status = rawStatus;
    if (item.available === true || item.isAvailable === true || rawStatus === 'available' || rawStatus === 'uygun') status = 'green';
    if (rawStatus === 'busy' || rawStatus === 'teacher_busy' || rawStatus === 'black' || rawStatus === 'dolu') status = 'black';
    if (rawStatus === 'blocked' || rawStatus === 'rule' || rawStatus === 'unavailable' || rawStatus === 'red' || rawStatus === 'kapali') status = 'red';
    map[key] = {
      ...item,
      status: ['green', 'black', 'red'].includes(status) ? status : 'red',
      message: valueOf(item, ['message', 'reason', 'aciklama'], ''),
    };
  });
  return map;
}

function normalizeClosedSlots(payload) {
  const source = payload?.data ?? payload ?? {};
  const slots = asArray(source.slots || source.kapaliSlotlar || source.kapali_slotlar).map(String);
  if (slots.length > 0) return slots;
  return Object.entries(source.byDay || source.by_day || {}).flatMap(([day, periods]) => (
    asArray(periods).map(period => slotKey(day, period))
  ));
}

function closedSlotSetFromMap(closedSlotMap, classId) {
  const value = closedSlotMap?.[String(classId)];
  if (!value) return new Set();
  if (value instanceof Set) return value;
  return new Set(asArray(value).map(String));
}

function isClosedSlotInMap(closedSlotMap, classId, key) {
  return closedSlotSetFromMap(closedSlotMap, classId).has(String(key));
}

function getDayConfig(settings, dayIndex) {
  return asArray(settings).find(day => Number(day.dayIndex) === Number(dayIndex));
}

function getClassDailyLimitRow(dailyLimits, classId) {
  return asArray(dailyLimits).find(item => String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')) === String(classId));
}

function getClassDayLimit(dailyLimits, classId, dayIndex) {
  const row = getClassDailyLimitRow(dailyLimits, classId) || {};
  const limits = row.limits || row.classDailyLimits || row.gunler || {};
  return limits?.[dayIndex] || limits?.[String(dayIndex)] || {};
}

function isSlotWithinClassDailyLimit(dailyLimits, settings, classId, dayIndex, period) {
  const day = getDayConfig(settings, dayIndex);
  if (!day || !day.isActive || Number(period) >= Number(day.periodCount || 0)) return false;
  const limit = getClassDayLimit(dailyLimits, classId, dayIndex);
  if (!limit || Object.keys(limit).length === 0) return true;
  const slotNo = Number(period) + 1;
  const start = Math.max(1, Number(valueOf(limit, ['start', 'baslangic', 'baslangicSlot'], 1)) || 1);
  const end = Math.max(start, Number(valueOf(limit, ['end', 'bitis', 'bitisSlot'], day.periodCount)) || Number(day.periodCount));
  return slotNo >= start && slotNo <= end;
}

function isSameSubjectDailyLimitMessage(message) {
  const normalized = normalizeLookupText(message);
  return normalized.includes('aynı sınıfa aynı gün aynı dersten en fazla 2 saat')
    || normalized.includes('ayni sinifa ayni gun ayni dersten en fazla 2 saat');
}

function normalizeLookupText(value) {
  return String(value ?? '').trim().toLocaleLowerCase('tr-TR');
}

function resolveEntityId(map, value, nameKeys = ['name'], shortKeys = ['shortName']) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (map[String(raw)]) return String(map[String(raw)].id ?? raw);
  const normalized = normalizeLookupText(raw);
  const match = Object.values(map || {}).find((item) => (
    [...shortKeys, ...nameKeys].some(key => normalizeLookupText(item?.[key]) === normalized)
  ));
  return match ? String(match.id) : '';
}

function normalizeTeacherIdForSave(value, teacherMap) {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  if (/öğretmen atanmadı|ogretmen atanmadi|atanmadı|atanmadi/i.test(raw)) return null;
  return resolveEntityId(teacherMap, raw, ['name', 'ad', 'teacherName'], ['shortName', 'short_name']) || null;
}

function buildSaveTimetableResult(timetableResult, { classMap, subjectMap, teacherMap }) {
  const next = JSON.parse(JSON.stringify(timetableResult || { assignments: {} }));
  next.assignments = {};
  Object.entries(timetableResult?.assignments || {}).forEach(([rawClassId, slots]) => {
    const classId = resolveEntityId(classMap, rawClassId, ['name', 'ad', 'className'], ['shortName', 'short_name']) || String(rawClassId);
    if (!classId) return;
    next.assignments[classId] = {};
    Object.entries(slots || {}).forEach(([key, cell]) => {
      const subjectId = resolveEntityId(
        subjectMap,
        valueOf(cell, ['subjectId', 'subject_id', 'dersId', 'ders_id', 'subjectShortName', 'shortName', 'subjectName', 'dersAdi'], ''),
        ['name', 'ad', 'dersAdi', 'subjectName'],
        ['shortName', 'short_name', 'kisaAd', 'kisa_ad', 'kod']
      );
      if (!subjectId) return;
      const teacherId = normalizeTeacherIdForSave(valueOf(cell, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id', 'teacherName'], ''), teacherMap);
      const teacherIds = teacherId
        ? [teacherId]
        : asArray(cell.teacherIds || cell.teacher_ids)
          .map(item => normalizeTeacherIdForSave(item, teacherMap))
          .filter(Boolean);
      const subject = subjectMap[String(subjectId)] || {};
      const teacher = teacherIds[0] ? teacherMap[String(teacherIds[0])] : null;
      next.assignments[classId][key] = {
        ...cell,
        classId,
        sinifId: classId,
        subjectId,
        dersId: subjectId,
        subjectShortName: subject.shortName || cell.subjectShortName || cell.shortName || '',
        shortName: subject.shortName || cell.subjectShortName || cell.shortName || '',
        subjectName: subject.name || cell.subjectName || '',
        teacherId: teacherIds[0] || null,
        ogretmenId: teacherIds[0] || null,
        teacherIds,
        teacherName: teacher?.name || (!teacherIds[0] ? 'Öğretmen atanmadı' : ''),
        teacherless: teacherIds.length === 0,
      };
    });
  });
  return next;
}

function fullBackendErrorMessage(error, fallback) {
  const body = error?.response?.data;
  if (!body) return error?.message || fallback;
  if (typeof body === 'string') return body;
  const message = body.message || body.error || body.detail || body.details;
  if (message && typeof message === 'string') return message;
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return fallback;
  }
}

function scheduleErrorMessage(error, fallback) {
  return error.response?.data?.message || error.response?.data?.error || fallback;
}

function buildDefaultSettings() {
  return DAY_NAMES.map((name, dayIndex) => ({
    dayIndex,
    name,
    shortName: DAY_SHORT[dayIndex],
    isActive: dayIndex < 5,
    periodCount: dayIndex < 5 ? 8 : 4,
    timeSlots: Array.from({ length: 18 }, (_, i) => ({
      start: i < 8 ? `${String(9 + Math.floor(i * 45 / 60)).padStart(2, '0')}:${String((i * 45) % 60).padStart(2, '0')}` : '00:00',
      end: i < 8 ? `${String(9 + Math.floor((i * 45 + 40) / 60)).padStart(2, '0')}:${String((i * 45 + 40) % 60).padStart(2, '0')}` : '00:00',
    })),
  }));
}

function normalizeSettings(raw) {
  const source = raw?.dailyConfigs || raw?.daily_configs || raw?.gunler || raw?.days || raw;
  if (!Array.isArray(source) || source.length === 0) return buildDefaultSettings();
  const byIndex = {};
  source.forEach((day, index) => {
    const dayIndex = Number(valueOf(day, ['dayIndex', 'day_index', 'gunIndex', 'gun_index'], index));
    byIndex[dayIndex] = day;
  });
  return buildDefaultSettings().map((fallback) => {
    const day = byIndex[fallback.dayIndex] || {};
    const rawSlots = asArray(valueOf(day, ['timeSlots', 'time_slots', 'saatler', 'slots'], []));
    const periodCount = Math.max(1, Math.min(18, Number(valueOf(day, ['periodCount', 'period_count', 'dersSayisi', 'slotCount'], rawSlots.length || fallback.periodCount))));
    return {
      dayIndex: fallback.dayIndex,
      name: valueOf(day, ['name', 'ad', 'gunAdi'], fallback.name),
      shortName: valueOf(day, ['shortName', 'short_name', 'kisaAd'], fallback.shortName),
      isActive: boolValue(valueOf(day, ['isActive', 'is_active', 'aktif', 'active'], fallback.isActive), fallback.isActive),
      periodCount,
      timeSlots: Array.from({ length: 18 }, (_, i) => {
        const slot = rawSlots[i] || fallback.timeSlots[i] || {};
        return {
          start: valueOf(slot, ['start', 'baslangic', 'bas', 'startTime'], '00:00'),
          end: valueOf(slot, ['end', 'bitis', 'bit', 'endTime'], '00:00'),
        };
      }),
    };
  });
}

function normalizeSubjects(items) {
  return asArray(items).map((item, index) => {
    const weeklyHours = Number(valueOf(item, [
      'weeklyHours',
      'weekly_hours',
      'haftalikDersSaati',
      'haftalik_ders_saati',
      'haftalikSaat',
      'haftalik_saat',
      'HDS',
      'hds',
    ], 0));
    const distributionFormat = valueOf(item, [
      'distributionFormat',
      'distribution_format',
      'placementFormat',
      'placement_format',
      'yerlesimBicimi',
      'yerlesim_bicimi',
      'yerleşimBiçimi',
      'yerlesmeBicimi',
      'yerlesme_bicimi',
      'yerleşmeBiçimi',
      'yerBicimi',
      'yer_bicimi',
      'yerlesim',
      'yer_bic',
      'yerBic',
      'yerBiç',
    ], weeklyHours > 0 ? String(weeklyHours) : '1');

    return {
      ...item,
      id: valueOf(item, ['id', '_id', 'subjectId', 'ders_id'], index + 1),
      name: valueOf(item, ['name', 'ad', 'dersAdi', 'ders_adi', 'subjectName'], ''),
      shortName: valueOf(item, ['shortName', 'short_name', 'kisaAd', 'kisa_ad', 'kod'], ''),
      weeklyHours,
      distributionFormat,
      color: valueOf(item, ['color', 'renk'], COLORS[index % COLORS.length]),
    };
  });
}

function normalizeClasses(items) {
  return asArray(items).map((item, index) => ({
    ...item,
    id: valueOf(item, ['id', '_id', 'classId', 'sinif_id'], index + 1),
    name: valueOf(item, ['name', 'ad', 'sinifAdi', 'sinif_adi'], ''),
    order: Number(valueOf(item, ['order', 'sira'], index + 1)),
  }));
}

function normalizeTeachers(items) {
  return asArray(items).map((item, index) => {
    const fullName = valueOf(item, ['name', 'adSoyad', 'ad_soyad'], `${valueOf(item, ['ad'], '')} ${valueOf(item, ['soyad'], '')}`.trim());
    return {
      ...item,
      id: valueOf(item, ['id', '_id', 'teacherId', 'ogretmen_id'], index + 1),
      name: fullName,
      shortName: valueOf(item, ['shortName', 'short_name', 'kisaAd', 'kisa_ad'], fullName.split(' ').map(part => part[0]).join('').slice(0, 3).toUpperCase()),
      branch: valueOf(item, ['branch', 'brans', 'branş'], ''),
      homeroomClass: valueOf(item, ['homeroomClass', 'sinifOgretmeni', 'sinif_ogretmeni'], ''),
      club: valueOf(item, ['club', 'sosyalKulubu', 'sosyal_kulubu'], ''),
      email: valueOf(item, ['email', 'eposta'], ''),
      unavailableSlots: asArray(valueOf(item, ['unavailableSlots', 'unavailable_slots', 'kapaliSaatler', 'kapali_saatler'], [])),
    };
  });
}

function normalizeAssignments(items) {
  return asArray(items).map((item, index) => ({
    ...item,
    id: valueOf(item, ['id', '_id'], index + 1),
    teacherId: String(valueOf(item, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '')),
    classId: String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')),
    subjectId: String(valueOf(item, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '')),
    weeklyHours: Number(valueOf(item, ['weeklyHours', 'weekly_hours', 'haftalikSaat', 'haftalik_saat', 'hds'], 0)),
  }));
}

function normalizeClassSubjects(items) {
  return asArray(items).map((item, index) => ({
    ...item,
    id: valueOf(item, ['id', '_id'], index + 1),
    classId: String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')),
    subjectId: String(valueOf(item, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '')),
    weeklyHours: Number(valueOf(item, ['weeklyHours', 'weekly_hours', 'haftalikSaat', 'hds'], 0)),
    teacherless: boolValue(valueOf(item, ['teacherless', 'teacher_less', 'ogretmensiz', 'öğretmensiz', 'planTeacherless', 'plan_teacherless'], false), false),
    planTeacherless: boolValue(valueOf(item, ['planTeacherless', 'plan_teacherless', 'teacherless', 'teacher_less', 'ogretmensiz', 'öğretmensiz'], false), false),
    distributionFormat: valueOf(item, [
      'distributionFormat',
      'distribution_format',
      'placementFormat',
      'placement_format',
      'yerlesimBicimi',
      'yerlesim_bicimi',
      'yerleşimBiçimi',
      'yerlesmeBicimi',
      'yerlesme_bicimi',
      'yerleşmeBiçimi',
      'yerBicimi',
      'yer_bicimi',
      'yerlesim',
      'yer_bic',
      'yerBic',
      'yerBiç',
    ], ''),
  }));
}

function normalizeTimetable(raw) {
  const timetable = raw?.timetableResult || raw?.timetable_result || raw?.program || raw || {};
  return {
    ...timetable,
    assignments: timetable?.assignments && typeof timetable.assignments === 'object' ? timetable.assignments : {},
  };
}

function entityName(map, id, fallback = '-') {
  return map[String(id)]?.name || fallback;
}

function entityShort(map, id, fallback = '-') {
  return map[String(id)]?.shortName || map[String(id)]?.name || fallback;
}

function teacherFullName(teacher) {
  if (!teacher) return '';
  const composed = `${valueOf(teacher, ['ad', 'firstName', 'first_name'], '')} ${valueOf(teacher, ['soyad', 'lastName', 'last_name'], '')}`.trim();
  return valueOf(teacher, ['teacherName', 'teacher_name', 'name', 'adSoyad', 'ad_soyad'], composed);
}

function hasLessonTeacher(cell) {
  return Boolean(cell?.teacherId || asArray(cell?.teacherIds).filter(Boolean).length > 0);
}

function getLessonTeacherIds(cell) {
  const ids = asArray(cell?.teacherIds || cell?.teacher_ids).map(String).filter(Boolean);
  const teacherId = valueOf(cell, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '');
  if (teacherId) ids.push(String(teacherId));
  return [...new Set(ids)];
}

function getTeacherUnavailableSlotSet(teacher) {
  const unavailableSlots = asArray(valueOf(teacher, [
    'unavailableSlots',
    'unavailable_slots',
    'kapaliSaatler',
    'kapali_saatler',
  ], []));
  return new Set(unavailableSlots.map((item) => {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    const key = valueOf(item, ['slotKey', 'slot_key', 'key'], '');
    if (key) return String(key);
    const day = valueOf(item, ['dayIndex', 'day_index', 'day', 'gunIndex', 'gun_index'], '');
    const period = valueOf(item, ['period', 'periodIndex', 'period_index', 'slot', 'saat'], '');
    return day !== '' && period !== '' ? slotKey(day, period) : '';
  }).filter(Boolean));
}

function isTeacherUnavailable(teacherMap, teacherId, key) {
  if (!teacherId) return false;
  return getTeacherUnavailableSlotSet(teacherMap[String(teacherId)]).has(String(key));
}

function lessonTeacherLabel(cell, teacherMap) {
  if (!cell || cell.teacherless || !hasLessonTeacher(cell)) return 'Öğretmen atanmadı';
  const cellTeacherName = valueOf(cell, ['teacherName', 'teacher_name'], '');
  if (cellTeacherName && !/öğretmen atanmadı|ogretmen atanmadi/i.test(String(cellTeacherName))) return cellTeacherName;
  const teacherId = cell.teacherId || cell.teacherIds?.[0];
  return teacherFullName(teacherMap[String(teacherId)]) || 'Öğretmen atanmadı';
}

function payloadHasTimetable(payload) {
  return Boolean(payload?.timetableResult || payload?.timetable_result || payload?.program || payload?.assignments);
}

function mergeUpdatedProgramCells(timetableResult, updatedProgramCells) {
  const next = JSON.parse(JSON.stringify(timetableResult || { assignments: {} }));
  if (!next.assignments) next.assignments = {};
  asArray(updatedProgramCells).forEach((item) => {
    const classId = String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''));
    const key = String(valueOf(item, ['slotKey', 'slot_key', 'key'], '')) ||
      (item?.day !== undefined && item?.period !== undefined ? slotKey(item.day, item.period) : '');
    if (!classId || !key) return;
    if (!next.assignments[classId]) next.assignments[classId] = {};
    if (item.deleted || item.removed || item.clear) {
      delete next.assignments[classId][key];
      return;
    }
    const cell = item.cell || item.programCell || item.program_cell || item;
    next.assignments[classId][key] = {
      ...cell,
      teacherId: cell.teacherId ?? cell.teacher_id ?? item.teacherId ?? item.teacher_id ?? null,
      teacherIds: asArray(cell.teacherIds || cell.teacher_ids || item.teacherIds || item.teacher_ids).filter(Boolean),
      subjectId: String(valueOf(cell, ['subjectId', 'subject_id', 'dersId', 'ders_id'], valueOf(item, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''))),
      teacherless: boolValue(valueOf(cell, ['teacherless', 'teacher_less'], valueOf(item, ['teacherless', 'teacher_less'], false)), false),
    };
  });
  return next;
}

function sanitizeTimetableClosedSlots(timetableResult, closedSlotMap) {
  const next = JSON.parse(JSON.stringify(timetableResult || { assignments: {} }));
  if (!next.assignments) next.assignments = {};
  const removed = [];
  Object.entries(next.assignments).forEach(([classId, slots]) => {
    Object.keys(slots || {}).forEach((key) => {
      if (!isClosedSlotInMap(closedSlotMap, classId, key)) return;
      removed.push({ classId, key, cell: slots[key] });
      delete slots[key];
    });
  });
  return { timetableResult: next, removed };
}

function sanitizeTimetableClassDailyLimits(timetableResult, dailyLimits, settings) {
  const next = JSON.parse(JSON.stringify(timetableResult || { assignments: {} }));
  if (!next.assignments) next.assignments = {};
  const removed = [];
  Object.entries(next.assignments).forEach(([classId, slots]) => {
    Object.keys(slots || {}).forEach((key) => {
      const [dayIndex, period] = String(key).split('_').map(Number);
      if (isSlotWithinClassDailyLimit(dailyLimits, settings, classId, dayIndex, period)) return;
      removed.push({ classId, key, cell: slots[key] });
      delete slots[key];
    });
  });
  return { timetableResult: next, removed };
}

function WindowModal({ title, width = 980, children, onClose }) {
  return (
    <div className="hp-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="hp-window" style={{ maxWidth: width }}>
        <div className="hp-window-titlebar">
          <div>
            <strong>{title}</strong>
            <span>Haftalık program yönetimi</span>
          </div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="hp-window-body">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="hp-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function DenseTable({ headers, children, minWidth = 620 }) {
  return (
    <div className="hp-table-wrap">
      <table className="hp-table" style={{ minWidth }}>
        <thead>
          <tr>{headers.map((head) => <th key={head}>{head}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export default function DersProgramiPage() {
  const [loading, setLoading] = useState(true);
  const [activeWindow, setActiveWindow] = useState(null);
  const [viewMode, setViewMode] = useState('class');
  const [selectedId, setSelectedId] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [pendingLessons, setPendingLessons] = useState([]);
  const [selectedPendingLesson, setSelectedPendingLesson] = useState(null);
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [classClosedSlots, setClassClosedSlots] = useState({});
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [programStats, setProgramStats] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [emptyClassSubjects, setEmptyClassSubjects] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [settings, setSettings] = useState(buildDefaultSettings);
  const [dailyLimits, setDailyLimits] = useState([]);
  const [rules, setRules] = useState([]);
  const [timetableResult, setTimetableResult] = useState({ assignments: {} });
  const draftAutosaveReadyRef = useRef(false);
  const savedDraftSignatureRef = useRef('');
  const draftPayloadRef = useRef({ settings, dailyLimits, rules });
  const draftSaveTimerRef = useRef(null);
  const draftSavePromiseRef = useRef(null);
  const queuedDraftRef = useRef(null);
  const latestDraftSnapshotRef = useRef(null);

  const loadSetup = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/setup`);
      const payload = data?.data ?? data ?? {};
      setSubjects(normalizeSubjects(payload.subjects || payload.dersler));
      setClasses(normalizeClasses(payload.classes || payload.siniflar));
      setTeachers(normalizeTeachers(payload.teachers || payload.ogretmenler));
      setClassSubjects(normalizeClassSubjects(payload.classSubjects || payload.sinifDersleri || payload.sinif_dersleri));
      setEmptyClassSubjects(
        payload.emptyClassSubjects || payload.bosDersler
          ? normalizeClassSubjects(payload.emptyClassSubjects || payload.bosDersler)
          : null
      );
      setAssignments(normalizeAssignments(payload.assignments || payload.atamalar));
      setSettings(normalizeSettings(payload.settings || payload.ayarlar || payload.dailyConfigs || payload.daily_configs));
      setDailyLimits(asArray(payload.dailyLimits || payload.gunlukDersSayilari || payload.classDailyLimits));
      setRules(asArray(payload.rules || payload.kurallar));
      const nextTimetableResult = normalizeTimetable(payload.timetableResult || payload.program || payload.liste);
      savedDraftSignatureRef.current = JSON.stringify(nextTimetableResult);
      draftAutosaveReadyRef.current = true;
      setTimetableResult(nextTimetableResult);
      setProgramStats(null);
      setWarnings(asArray(payload.warnings || payload.uyarilar));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Haftalık program verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  useEffect(() => {
    const refreshProgram = () => loadSetup();
    window.addEventListener(WEEKLY_PROGRAM_REFRESH_EVENT, refreshProgram);
    return () => window.removeEventListener(WEEKLY_PROGRAM_REFRESH_EVENT, refreshProgram);
  }, [loadSetup]);

  useEffect(() => {
    setProgramStats(null);
  }, [assignments]);

  useEffect(() => {
    draftPayloadRef.current = { settings, dailyLimits, rules };
  }, [settings, dailyLimits, rules]);

  const flushDraftSaveQueue = useCallback(() => {
    if (draftSavePromiseRef.current) return draftSavePromiseRef.current;
    if (!queuedDraftRef.current) return Promise.resolve();

    const runningPromise = (async () => {
      while (queuedDraftRef.current) {
        const draft = queuedDraftRef.current;
        queuedDraftRef.current = null;
        try {
          const response = await axios.put(`${API}/taslak`, {
            timetableResult: draft.timetableResult,
            dailyConfigs: draft.settings,
            classDailyLimits: draft.dailyLimits,
            rules: draft.rules,
          });
          getPayload(response);
          savedDraftSignatureRef.current = draft.signature;
        } catch (error) {
          toast.error(scheduleErrorMessage(error, 'Program taslağı kaydedilemedi'));
        }
      }
    })();

    draftSavePromiseRef.current = runningPromise;
    runningPromise.finally(() => {
      if (draftSavePromiseRef.current === runningPromise) draftSavePromiseRef.current = null;
    });
    return runningPromise;
  }, []);

  const flushCurrentDraft = useCallback((force = false) => {
    if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = null;

    const signature = JSON.stringify(timetableResult);
    const snapshot = { timetableResult, signature, settings, dailyLimits, rules };
    latestDraftSnapshotRef.current = snapshot;
    if (
      force
      || signature !== savedDraftSignatureRef.current
      || draftSavePromiseRef.current
      || queuedDraftRef.current
    ) queuedDraftRef.current = snapshot;
    return flushDraftSaveQueue();
  }, [dailyLimits, flushDraftSaveQueue, rules, settings, timetableResult]);

  useEffect(() => () => {
    if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = null;
    const latestDraft = latestDraftSnapshotRef.current;
    if (
      latestDraft
      && (
        latestDraft.signature !== savedDraftSignatureRef.current
        || draftSavePromiseRef.current
        || queuedDraftRef.current
      )
    ) queuedDraftRef.current = latestDraft;
    flushDraftSaveQueue();
  }, [flushDraftSaveQueue]);

  useEffect(() => {
    if (!draftAutosaveReadyRef.current) return undefined;
    const signature = JSON.stringify(timetableResult);
    if (
      signature === savedDraftSignatureRef.current
      && !draftSavePromiseRef.current
      && !queuedDraftRef.current
    ) return undefined;

    const currentPayload = draftPayloadRef.current;
    const snapshot = {
      timetableResult,
      signature,
      settings: currentPayload.settings,
      dailyLimits: currentPayload.dailyLimits,
      rules: currentPayload.rules,
    };
    latestDraftSnapshotRef.current = snapshot;

    draftSaveTimerRef.current = window.setTimeout(() => {
      draftSaveTimerRef.current = null;
      queuedDraftRef.current = snapshot;
      flushDraftSaveQueue();
    }, 650);

    return () => {
      if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    };
  }, [flushDraftSaveQueue, timetableResult]);

  const subjectMap = useMemo(() => Object.fromEntries(subjects.map(item => [String(item.id), item])), [subjects]);
  const classMap = useMemo(() => Object.fromEntries(classes.map(item => [String(item.id), item])), [classes]);
  const teacherMap = useMemo(() => Object.fromEntries(teachers.map(item => [String(item.id), item])), [teachers]);
  const activeDays = useMemo(() => settings.filter(day => day.isActive), [settings]);
  const maxPeriod = useMemo(() => Math.max(1, ...activeDays.map(day => day.periodCount || 1)), [activeDays]);
  const periods = useMemo(() => Array.from({ length: maxPeriod }, (_, i) => i), [maxPeriod]);

  const visibleList = viewMode === 'teacher' ? teachers : viewMode === 'subject' ? subjects : classes;
  const resolvedSelectedId = selectedId || String(visibleList[0]?.id || '');

  useEffect(() => {
    setSelectedId('');
    setSelectedCell(null);
    setSelectedPendingLesson(null);
    setAvailabilityMap({});
  }, [viewMode]);

  const loadPendingLessons = useCallback(async (classId) => {
    if (!classId) {
      setPendingLessons([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/bekleyen-dersler`, { params: { classId } });
      const payload = getPayload(response);
      setPendingLessons(extractPendingLessons(payload));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bekleyen dersler alınamadı');
    }
  }, []);

  const getCellForSlot = useCallback((day, period) => {
    const key = slotKey(day, period);
    if (viewMode === 'class') {
      const cell = timetableResult.assignments?.[String(resolvedSelectedId)]?.[key];
      return cell ? { classId: String(resolvedSelectedId), key, cell } : null;
    }
    for (const [classId, slots] of Object.entries(timetableResult.assignments || {})) {
      const cell = slots?.[key];
      if (!cell) continue;
      const teacherIds = asArray(cell.teacherIds).map(String);
      if (cell.teacherId) teacherIds.push(String(cell.teacherId));
      if (viewMode === 'teacher' && teacherIds.includes(String(resolvedSelectedId))) return { classId, key, cell };
      if (viewMode === 'subject' && String(cell.subjectId) === String(resolvedSelectedId)) return { classId, key, cell };
    }
    return null;
  }, [resolvedSelectedId, timetableResult, viewMode]);

  const activeClassId = viewMode === 'class'
    ? resolvedSelectedId
    : String(selectedCell ? (getCellForSlot(selectedCell.day, selectedCell.period)?.classId || classes[0]?.id || '') : classes[0]?.id || '');

  const fetchClassClosedSlots = useCallback(async (classId) => {
    if (!classId) return new Set();
    const response = await axios.get(`${API}/sinif-kapali-slotlar`, { params: { classId } });
    return new Set(normalizeClosedSlots(response.data));
  }, []);

  const loadClassClosedSlots = useCallback(async (classId) => {
    if (!classId) return new Set();
    try {
      const nextSet = await fetchClassClosedSlots(classId);
      setClassClosedSlots(prev => ({
        ...prev,
        [String(classId)]: nextSet,
      }));
      return nextSet;
    } catch (error) {
      toast.error(scheduleErrorMessage(error, 'Sınıf kapalı saatleri alınamadı'));
      return new Set();
    }
  }, [fetchClassClosedSlots]);

  const loadClosedSlotsForClasses = useCallback(async (classIds) => {
    const ids = [...new Set(asArray(classIds).map(String).filter(Boolean))];
    if (ids.length === 0) return classClosedSlots;
    const nextMap = { ...classClosedSlots };
    await Promise.all(ids.map(async (classId) => {
      try {
        nextMap[classId] = await fetchClassClosedSlots(classId);
      } catch (error) {
        toast.error(scheduleErrorMessage(error, `${entityName(classMap, classId, 'Sınıf')} kapalı saatleri alınamadı`));
        nextMap[classId] = nextMap[classId] || new Set();
      }
    }));
    setClassClosedSlots(nextMap);
    return nextMap;
  }, [classClosedSlots, classMap, fetchClassClosedSlots]);

  useEffect(() => {
    if (activeWindow === 'programEkrani') loadPendingLessons(activeClassId);
  }, [activeClassId, activeWindow, loadPendingLessons]);

  useEffect(() => {
    if (activeWindow === 'programEkrani' && activeClassId) loadClassClosedSlots(activeClassId);
  }, [activeClassId, activeWindow, loadClassClosedSlots]);

  const isClassSlotClosed = useCallback((classId, key) => (
    isClosedSlotInMap(classClosedSlots, classId, key)
  ), [classClosedSlots]);

  const isClassSlotAllowed = useCallback((classId, dayIndex, period) => (
    isSlotWithinClassDailyLimit(dailyLimits, settings, classId, dayIndex, period)
  ), [dailyLimits, settings]);

  const refreshList = async (type) => {
    const endpoints = {
      subjects: ['dersler', setSubjects, normalizeSubjects],
      classes: ['siniflar', setClasses, normalizeClasses],
      teachers: ['ogretmenler', setTeachers, normalizeTeachers],
      assignments: ['atamalar', setAssignments, normalizeAssignments],
      classSubjects: ['sinif-dersleri', setClassSubjects, normalizeClassSubjects],
    };
    const config = endpoints[type];
    if (!config) return;
    const [endpoint, setter, normalizer] = config;
    const response = await axios.get(`${API}/${endpoint}`);
    setter(normalizer(getPayload(response)));
  };

  const updateTimetableCell = (classId, key, nextCell) => {
    setProgramStats(null);
    setTimetableResult((prev) => {
      const next = JSON.parse(JSON.stringify(prev || { assignments: {} }));
      if (!next.assignments) next.assignments = {};
      if (!next.assignments[String(classId)]) next.assignments[String(classId)] = {};
      if (nextCell) next.assignments[String(classId)][key] = nextCell;
      else delete next.assignments[String(classId)][key];
      return next;
    });
  };

  const handleDrop = async (event, day, period) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!dragSource) return;
    const targetKey = slotKey(day, period);
    if (String(dragSource.key) === targetKey) {
      setDragSource(null);
      return;
    }
    let closedSlotMap;
    try {
      const latestClosedSlots = await fetchClassClosedSlots(dragSource.classId);
      closedSlotMap = { ...classClosedSlots, [String(dragSource.classId)]: latestClosedSlots };
      setClassClosedSlots(closedSlotMap);
    } catch (error) {
      toast.error(scheduleErrorMessage(error, 'Sınıfın kapalı saatleri doğrulanamadığı için taşıma yapılmadı'));
      setDragSource(null);
      return;
    }
    if (isClosedSlotInMap(closedSlotMap, dragSource.classId, targetKey)) {
      toast.error('Bu sınıf için seçilen saat kapalı');
      setDragSource(null);
      return;
    }
    if (!isClassSlotAllowed(dragSource.classId, day, period)) {
      toast.error('Bu sınıf seçilen saatte ders yapamaz');
      setDragSource(null);
      return;
    }
    if (timetableResult.assignments?.[String(dragSource.classId)]?.[targetKey]) {
      toast.error('Sınıf çakışması: Hedef saatte bu sınıfın başka bir dersi var');
      setDragSource(null);
      return;
    }
    const teacherIds = getLessonTeacherIds(dragSource.cell);
    const unavailableTeacherId = teacherIds.find(teacherId => isTeacherUnavailable(teacherMap, teacherId, targetKey));
    if (unavailableTeacherId) {
      toast.error(`${entityName(teacherMap, unavailableTeacherId, 'Öğretmen')} bu saatte kapalı`);
      setDragSource(null);
      return;
    }
    const conflictingTeacherId = teacherIds.find(teacherId => (
      Object.entries(timetableResult.assignments || {}).some(([classId, slots]) => {
        if (String(classId) === String(dragSource.classId) && String(dragSource.key) === targetKey) return false;
        return getLessonTeacherIds(slots?.[targetKey]).includes(String(teacherId));
      })
    ));
    if (conflictingTeacherId) {
      toast.error(`${entityName(teacherMap, conflictingTeacherId, 'Öğretmen')} için öğretmen çakışması oluşuyor`);
      setDragSource(null);
      return;
    }
    updateTimetableCell(dragSource.classId, dragSource.key, null);
    updateTimetableCell(dragSource.classId, targetKey, dragSource.cell);
    setDragSource(null);
    setWarnings([{ message: 'Manuel taşıma yapıldı. Kaydet düğmesi timetableResult yapısını backend kaydına gönderir.' }]);
  };

  const handleCellDoubleClick = async (day, period) => {
    const key = slotKey(day, period);
    const cellData = getCellForSlot(day, period);
    const classId = cellData?.classId || (viewMode === 'class' ? resolvedSelectedId : activeClassId);
    if (!classId) return;
    if (!cellData && isClassSlotClosed(classId, key)) {
      toast.error('Kapalı saate ders atanamaz');
      return;
    }
    if (!cellData && !isClassSlotAllowed(classId, day, period)) {
      toast.error('Bu sınıf seçilen saatte ders yapamaz');
      return;
    }
    try {
      const response = await axios.post(`${API}/hucre-bosa-al`, {
        classId,
        slotKey: key,
        timetableResult,
      });
      const payload = getPayload(response);
      setTimetableResult(extractTimetable(payload));
      setPendingLessons(extractPendingLessons(payload));
      setSelectedPendingLesson(null);
      setAvailabilityMap({});
      toast.success('Hücre boşa alındı');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Hücre boşa alınamadı');
    }
  };

  const handlePendingLessonClick = async (lesson) => {
    setSelectedPendingLesson(lesson);
    try {
      const response = await axios.post(`${API}/hucre-uygunluk`, {
        lesson,
        timetableResult,
        dailyConfigs: settings,
        classDailyLimits: dailyLimits,
        rules,
      });
      const payload = getPayload(response);
      setAvailabilityMap(normalizeAvailability(payload.availability || payload.availabilityList || payload.uygunluk || payload));
    } catch (error) {
      setAvailabilityMap({});
      toast.error(error.response?.data?.message || 'Hücre uygunluğu alınamadı');
    }
  };

  const handlePlacePendingLesson = async (day, period) => {
    if (!selectedPendingLesson) return;
    const key = slotKey(day, period);
    const classId = String(valueOf(selectedPendingLesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], activeClassId));
    const closedSlotMap = await loadClosedSlotsForClasses([classId]);
    if (isClosedSlotInMap(closedSlotMap, classId, key)) {
      toast.error('Kapalı saate ders atanamaz');
      return;
    }
    if (!isClassSlotAllowed(classId, day, period)) {
      toast.error('Bu sınıf seçilen saatte ders yapamaz');
      return;
    }
    const availability = availabilityMap[key];
    if (availability && availability.status !== 'green' && !isSameSubjectDailyLimitMessage(availability.message)) {
      toast.error(availability.message || 'Bu hücre uygun değil');
      return;
    }
    try {
      const response = await axios.post(`${API}/bekleyen-ders-yerlestir`, {
        lesson: selectedPendingLesson,
        slotKey: key,
        timetableResult,
        isFixed: true,
      });
      const payload = getPayload(response);
      const closedSanitized = sanitizeTimetableClosedSlots(extractTimetable(payload), closedSlotMap);
      const limitSanitized = sanitizeTimetableClassDailyLimits(closedSanitized.timetableResult, dailyLimits, settings);
      setTimetableResult(limitSanitized.timetableResult);
      if (closedSanitized.removed.length > 0) toast.error(`${closedSanitized.removed.length} ders kapalı saatlerden çıkarıldı`);
      if (limitSanitized.removed.length > 0) toast.error(`${limitSanitized.removed.length} ders günlük sınır dışından çıkarıldı`);
      const nextPending = extractPendingLessons(payload);
      if (nextPending.length > 0 || payload.pendingLessons || payload.bekleyenDersler) {
        setPendingLessons(nextPending);
      } else {
        await loadPendingLessons(valueOf(selectedPendingLesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], activeClassId));
      }
      setSelectedPendingLesson(null);
      setAvailabilityMap({});
      toast.success('Bekleyen ders yerleştirildi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bekleyen ders yerleştirilemedi');
    }
  };

  const handleAutoGenerate = async (automaticSettings = {}, expectedTotalHours = 0) => {
    if (autoGenerating) return;
    setAutoGenerating(true);
    setProgramStats(null);
    try {
      const closedSlotMap = await loadClosedSlotsForClasses(classes.map(item => item.id));
      const closedSlotsByClass = Object.fromEntries(
        Object.keys(closedSlotMap).map(classId => [classId, Array.from(closedSlotSetFromMap(closedSlotMap, classId))])
      );
      const { data } = await axios.post(`${API}/otomatik`, {
        timetableResult,
        classDailyLimits: dailyLimits,
        dailyConfigs: settings,
        rules,
        classClosedSlots: closedSlotsByClass,
        closedSlotsByClass,
        autoSettings: automaticSettings,
        automaticSettings,
        otomatikAyarlar: automaticSettings,
      });
      const payload = data?.data ?? data ?? {};
      const backendTimetable = extractTimetable(payload);
      setTimetableResult(backendTimetable);
      if (payload.pendingLessons || payload.pending_lessons || payload.bekleyenDersler) {
        setPendingLessons(extractPendingLessons(payload));
      }
      const nextWarnings = [...asArray(payload.warnings || payload.uyarilar)];
      const stats = payload.stats || {};
      const summary = payload.summary || payload.ozet || {};
      const numberFrom = (keys, fallback) => {
        const raw = valueOf(stats, keys, valueOf(payload, keys, valueOf(summary, keys, fallback)));
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      const activeSlotKeys = new Set(settings.filter(day => day.isActive).flatMap(day => (
        Array.from({ length: Number(day.periodCount || 0) }, (_, period) => slotKey(day.dayIndex, period))
      )));
      const calculatedPlaced = Object.values(backendTimetable.assignments || {}).reduce((total, slots) => (
        total + Object.entries(slots || {}).filter(([key, cell]) => cell && activeSlotKeys.has(key)).length
      ), 0);
      const totalHours = numberFrom(['totalHours', 'total_hours', 'toplamSaat', 'toplam_saat'], Number(expectedTotalHours) || calculatedPlaced);
      const placedHours = numberFrom(['placedHours', 'placed_hours', 'yerlesenSaat', 'yerlesen_saat'], calculatedPlaced);
      const remainingHours = numberFrom(['remainingHours', 'remaining_hours', 'kalanSaat', 'kalan_saat'], Math.max(0, totalHours - placedHours));
      setProgramStats({ totalHours, placedHours, remainingHours });
      if (remainingHours > 0) {
        nextWarnings.push({ message: `Otomatik programda ${placedHours} saat yerleşti, ${remainingHours} saat kaldı.` });
      }
      setWarnings(nextWarnings);
      setSelectedPendingLesson(null);
      setAvailabilityMap({});
      if (remainingHours > 0) toast(`Otomatik program: ${placedHours} saat yerleşti, ${remainingHours} saat kaldı.`);
      else toast.success(`Otomatik program oluşturuldu: ${placedHours} saat yerleşti.`);
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Otomatik program oluşturulamadı';
      setWarnings([{ message }]);
      toast.error(message);
    } finally {
      setAutoGenerating(false);
    }
  };

  const handleSaveProgram = async () => {
    try {
      await flushCurrentDraft(true);
      const saveTimetableResult = buildSaveTimetableResult(timetableResult, { classMap, subjectMap, teacherMap });
      const response = await axios.post(`${API}/kaydet`, {
        timetableResult: saveTimetableResult,
        dailyConfigs: settings,
        classDailyLimits: dailyLimits,
        rules,
      });
      const payload = getPayload(response);
      setWarnings(asArray(payload.warnings || payload.uyarilar));
      toast.success('Program aktif edildi ve yedeklendi');
    } catch (error) {
      const message = fullBackendErrorMessage(error, 'Program kaydedilemedi');
      setWarnings([{ message }]);
      toast.error(message);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Tüm haftalık program temizlensin mi?')) return;
    try {
      await axios.delete(`${API}/temizle`);
      setTimetableResult({ assignments: {} });
      setSelectedCell(null);
      toast.success('Program temizlendi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Program temizlenemedi');
    }
  };

  const selectedCellData = selectedCell ? getCellForSlot(selectedCell.day, selectedCell.period) : null;
  const desktopModules = [
    { title: 'Dersler', icon: 'blocks', action: () => setActiveWindow('dersler') },
    { title: 'Sınıflar', icon: 'class', action: () => setActiveWindow('siniflar') },
    { title: 'Öğretmenler', icon: 'teachers', action: () => setActiveWindow('ogretmenler') },
    { title: 'Günlük\nDers Sayıları', icon: 'daily', action: () => setActiveWindow('gunlukDersSayilari') },
    { title: 'Sınıflarda\nOkutulan Dersler', icon: 'classSubjects', action: () => setActiveWindow('sinifDersleri') },
    { title: 'Öğretmenlere\nDers Atanması', icon: 'assignment', action: () => setActiveWindow('atamalar') },
    { title: 'Öğretmenlerin\nDers Programı', icon: 'teacherSchedule', action: () => { setViewMode('teacher'); setActiveWindow('programEkrani'); } },
    { title: 'Sınıfların\nDers Programı', icon: 'classSchedule', action: () => { setViewMode('class'); setActiveWindow('programEkrani'); } },
    { title: 'Öğretmenlerin\nNöbet Çizelgesi', icon: 'duty', action: () => toast('Nöbet çizelgesi için backend endpointi tanımlı değil') },
    { title: 'Yazılı\nAyarla', icon: 'examSetup', action: () => setActiveWindow('yaziliAyarla') },
    { title: 'Yedekleme', icon: 'backup', action: handleSaveProgram },
  ];

  return (
    <div className="hp-page pragmatic-page">
      <style>{styles}</style>
      <div className="prag-shell">
        <div className="prag-titlebar">
          <span className="prag-app-dot">A</span>
          <span>ASDER Sistem</span>
          <span className="prag-window-controls">- x</span>
        </div>
        <div className="prag-content">
          <aside className="prag-brand">
            <img className="prag-asder-logo" src={asderLogo} alt="ASDER Sistem" />
            <div className="prag-logo-text">ASDER Sistem</div>
            <div className="prag-dna">Akademik Yönetim Sistemi</div>
          </aside>

          <main className="prag-main">
            <div className="prag-top-row">
              <div className="prag-toolbar">
                <ToolbarTile icon={<FaUsers />} label="Programlar" onClick={() => setActiveWindow('programlar')} active />
                <ToolbarTile icon={<FaClone />} label="Program\nKopyala" onClick={() => toast('Program kopyalama için backend endpointi tanımlı değil')} />
                <ToolbarTile icon={<FaWrench />} label="Dosya\nBakımı" onClick={handleClear} />
                <ToolbarTile icon={<FaCog />} label="Ayarlar" onClick={() => setActiveWindow('ayarlar')} />
              </div>
              <button className="prag-contact" type="button" onClick={() => toast('İletişim bilgileri kurum ayarlarından yönetilir')}>💬<span>İletişim</span></button>
            </div>

            <h1 className="prag-heading">HAFTALIK DERS PROGRAMI <span>(ASDER SİSTEM)</span></h1>
            <div className="prag-school">ASDER SİSTEM&nbsp;&nbsp; AKADEMİK YÖNETİM</div>

            {warnings.length > 0 && (
              <div className="hp-warning-strip pragmatic-warning">
                {warnings.map((item, index) => (
                  <span key={index}>{item.message || item.text || String(item)}</span>
                ))}
              </div>
            )}

            <div className="prag-module-grid">
              {desktopModules.map((item) => (
                <DesktopModule key={item.title} item={item} />
              ))}
            </div>
          </main>
        </div>
        <div className="prag-bottom-actions">
          <button type="button" onClick={handleSaveProgram}>💾 Net Yedekleme</button>
          <button type="button" onClick={() => toast('Çıkış için sol menüden oturumu kapatabilirsiniz')}>🚪 Programdan Çıkış</button>
        </div>
      </div>

      {activeWindow === 'programEkrani' && (
        <ScheduleProgramWindow
          viewMode={viewMode}
          setViewMode={setViewMode}
          visibleList={visibleList}
          resolvedSelectedId={resolvedSelectedId}
          setSelectedId={setSelectedId}
          activeDays={activeDays}
          periods={periods}
          settings={settings}
          setSettings={setSettings}
          subjectMap={subjectMap}
          classMap={classMap}
          teacherMap={teacherMap}
          timetableResult={timetableResult}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          selectedCell={selectedCell}
          selectedCellData={selectedCellData}
          setSelectedCell={setSelectedCell}
          setDragSource={setDragSource}
          getCellForSlot={getCellForSlot}
          handleDrop={handleDrop}
          handleCellDoubleClick={handleCellDoubleClick}
          handlePendingLessonClick={handlePendingLessonClick}
          handlePlacePendingLesson={handlePlacePendingLesson}
          handleAutoGenerate={handleAutoGenerate}
          autoGenerating={autoGenerating}
          warnings={warnings}
          programStats={programStats}
          classClosedSlots={classClosedSlots}
          setClassClosedSlots={setClassClosedSlots}
          isClassSlotClosed={isClassSlotClosed}
          isClassSlotAllowed={isClassSlotAllowed}
          loadClassClosedSlots={loadClassClosedSlots}
          loadClosedSlotsForClasses={loadClosedSlotsForClasses}
          updateTimetableCell={updateTimetableCell}
          setTimetableResult={setTimetableResult}
          rules={rules}
          dailyLimits={dailyLimits}
          pendingLessons={pendingLessons}
          assignments={assignments}
          classSubjects={classSubjects}
          setClassSubjects={setClassSubjects}
          selectedPendingLesson={selectedPendingLesson}
          setSelectedPendingLesson={setSelectedPendingLesson}
          availabilityMap={availabilityMap}
          setAvailabilityMap={setAvailabilityMap}
          onClose={async () => {
            await flushCurrentDraft();
            setActiveWindow(null);
          }}
        />
      )}
      {activeWindow === 'dersler' && (
        <SubjectsWindow
          subjects={subjects}
          setSubjects={setSubjects}
          refresh={() => refreshList('subjects')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'siniflar' && (
        <ClassesWindow
          classes={classes}
          setClasses={setClasses}
          refresh={() => refreshList('classes')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'ayarlar' && (
        <SettingsWindow
          settings={settings}
          setSettings={setSettings}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'sinifDersleri' && (
        <ClassSubjectsWindow
          classes={classes}
          subjects={subjects}
          classSubjects={classSubjects}
          setClassSubjects={setClassSubjects}
          refresh={() => refreshList('classSubjects')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'gunlukDersSayilari' && (
        <DailyLimitsWindow
          classes={classes}
          settings={settings}
          dailyLimits={dailyLimits}
          setDailyLimits={setDailyLimits}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'ogretmenler' && (
        <TeachersWindow
          teachers={teachers}
          setTeachers={setTeachers}
          settings={settings}
          setTimetableResult={setTimetableResult}
          setWarnings={setWarnings}
          refresh={() => refreshList('teachers')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'atamalar' && (
        <AssignmentsWindow
          teachers={teachers}
          classes={classes}
          subjects={subjects}
          classSubjects={classSubjects}
          emptyClassSubjects={emptyClassSubjects}
          assignments={assignments}
          timetableResult={timetableResult}
          setAssignments={setAssignments}
          setEmptyClassSubjects={setEmptyClassSubjects}
          setTimetableResult={setTimetableResult}
          refresh={() => refreshList('assignments')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'programlar' && (
        <ProgramListWindow
          timetableResult={timetableResult}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'yaziliAyarla' && (
        <YaziliAyarlaWindow
          subjects={subjects}
          classes={classes}
          teachers={teachers}
          onClose={() => setActiveWindow(null)}
        />
      )}
    </div>
  );
}

function classGrade(classItem) {
  const text = String(classItem?.name || classItem?.ad || '');
  const match = text.match(/\d+/);
  return match ? match[0] : 'Diğer';
}

function classStudentCount(classItem) {
  return Number(valueOf(classItem, [
    'studentCount',
    'student_count',
    'ogrenciSayisi',
    'ogrenci_sayisi',
    'öğrenciSayısı',
    'mevcut',
  ], 0)) || 0;
}

function classRoomCapacity(classItem) {
  return Number(valueOf(classItem, [
    'capacity',
    'kapasite',
    'kontenjan',
    'roomCapacity',
    'room_capacity',
    'salonKapasitesi',
    'salon_kapasitesi',
    'maxOgrenci',
    'max_ogrenci',
  ], 30)) || 30;
}

function roomStudentCount(room) {
  return Number(valueOf(room, [
    'studentCount',
    'student_count',
    'ogrenciSayisi',
    'ogrenci_sayisi',
    'öğrenciSayısı',
    'mevcut',
    'count',
  ], 0)) || 0;
}

function compactDate(value) {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
}

function writtenExamErrorMessage(error, fallback) {
  const serverMessage = error.response?.data?.message || error.response?.data?.error;
  if (serverMessage) return serverMessage;
  if (error.response?.status === 409) return 'Kapasite veya yerleşim çakışması oluştu';
  if (error.response?.status === 400) return 'Geçersiz katılımcı veya koltuk bilgisi';
  if (error.response?.status === 404) return 'Sınav veya salon bulunamadı';
  return fallback;
}

function normalizeWrittenExam(raw) {
  const id = valueOf(raw, ['id', 'sinav_id', 'examId', 'exam_id'], '');
  return {
    ...raw,
    id: String(id || Date.now()),
    date: String(valueOf(raw, ['date', 'tarih'], '')).slice(0, 10),
    time: String(valueOf(raw, ['time', 'saat'], '')).slice(0, 5),
    description: valueOf(raw, ['description', 'aciklama', 'ad', 'name'], ''),
    participants: asArray(raw?.participants || raw?.katilimcilar),
    students: extractExamStudents(raw?.students || raw?.ogrenciler || []),
    rooms: asArray(raw?.rooms || raw?.salonlar),
  };
}

function extractWrittenExams(payload) {
  const root = payload?.data || payload;
  const source = root?.exams
    || root?.sinavlar
    || root?.yaziliSinavlar
    || root?.yazili_sinavlar
    || root;
  return asArray(source).map(normalizeWrittenExam);
}

function extractWrittenExamRecord(payload) {
  const source = payload?.data?.exam || payload?.exam || payload?.data || payload?.sinav || payload?.yaziliSinav || payload?.yazili_sinav || payload;
  return Array.isArray(source) ? normalizeWrittenExam(source[0] || {}) : normalizeWrittenExam(source);
}

function cleanExamText(value) {
  const text = String(value ?? '').trim();
  return text === '.' ? '' : text;
}

function normalizeExamStudent(raw, index = 0) {
  const id = valueOf(raw, ['id', 'studentId', 'student_id', 'ogrenciId', 'ogrenci_id'], '');
  const firstName = valueOf(raw, ['ad', 'firstName', 'first_name'], '');
  const lastName = valueOf(raw, ['soyad', 'lastName', 'last_name'], '');
  const name = cleanExamText(valueOf(raw, ['fullName', 'full_name', 'name', 'adSoyad', 'ad_soyad'], `${firstName} ${lastName}`.trim()));
  return {
    ...raw,
    id: String(id || `student-${index}`),
    no: Number(valueOf(raw, ['no', 'sira', 'order'], index + 1)) || index + 1,
    studentNo: cleanExamText(valueOf(raw, ['studentNo', 'student_no', 'ogrenciNo', 'ogrenci_no', 'numara'], '')),
    name,
    classId: String(valueOf(raw, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')),
    className: cleanExamText(valueOf(raw, ['className', 'class_name', 'sinifAdi', 'sinif_adi'], '')),
    subjectId: String(valueOf(raw, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '')),
    subjectName: valueOf(raw, ['subjectName', 'subject_name', 'dersAdi', 'ders_adi'], ''),
    level: String(valueOf(raw, ['level', 'duzey', 'sinifDuzeyi', 'sinif_duzeyi'], '')),
    gender: valueOf(raw, ['gender', 'cinsiyet'], ''),
  };
}

function extractExamStudents(payload) {
  const root = payload?.data || payload;
  const source = root?.students || root?.ogrenciler || root?.participants || root?.katilimcilar || root;
  return asArray(source).filter((item) => item && typeof item === 'object').map(normalizeExamStudent);
}

function extractExamRooms(payload) {
  const root = payload?.data || payload;
  const source = root?.rooms || root?.salonlar || root;
  return asArray(source).map((room, index) => {
    const roomId = String(valueOf(room, ['roomId', 'room_id', 'salonId', 'salon_id', 'id', 'classId', 'class_id'], index));
    return {
    ...room,
    id: roomId,
    roomId,
    classId: String(valueOf(room, ['classId', 'class_id'], roomId)),
    name: valueOf(room, ['name', 'salonAdi', 'salon_adi', 'className', 'class_name'], `Salon ${index + 1}`),
    capacity: classRoomCapacity(room),
    studentCount: roomStudentCount(room),
    rowCount: Number(valueOf(room, ['rowCount', 'row_count'], 0)),
    columnCount: Number(valueOf(room, ['columnCount', 'column_count'], 0)),
  };
  });
}

function extractSeatingState(payload, students = []) {
  const root = payload?.data || payload || {};
  const assignments = root?.seating || root?.yerlesim || [];
  const studentMap = Object.fromEntries(students.map((student) => [String(student.id), student]));
  const seating = {};
  if (Array.isArray(assignments)) {
    assignments.forEach((item, index) => {
      const roomId = String(valueOf(item, ['roomId', 'room_id', 'salonId', 'salon_id'], ''));
      const seatKey = String(valueOf(item, ['seatKey', 'seat_key', 'koltuk'], ''));
      const studentId = String(valueOf(item, ['studentId', 'student_id', 'ogrenciId', 'ogrenci_id'], ''));
      const matchedStudent = studentMap[studentId] || {};
      const nestedStudent = item.student || item.ogrenci || {};
      const fullName = cleanExamText(item.fullName) || cleanExamText(item.full_name) || cleanExamText(item.name)
        || cleanExamText(nestedStudent.fullName) || cleanExamText(nestedStudent.full_name) || cleanExamText(nestedStudent.name)
        || cleanExamText(matchedStudent.fullName) || cleanExamText(matchedStudent.full_name) || cleanExamText(matchedStudent.name);
      const studentNo = cleanExamText(item.studentNo) || cleanExamText(item.student_no)
        || cleanExamText(nestedStudent.studentNo) || cleanExamText(nestedStudent.student_no)
        || cleanExamText(matchedStudent.studentNo) || cleanExamText(matchedStudent.student_no);
      const className = cleanExamText(item.className) || cleanExamText(item.class_name)
        || cleanExamText(nestedStudent.className) || cleanExamText(nestedStudent.class_name)
        || cleanExamText(matchedStudent.className) || cleanExamText(matchedStudent.class_name);
      const studentSource = { ...matchedStudent, ...nestedStudent, ...item, id: studentId, fullName, studentNo, className };
      if (roomId && seatKey && studentId) {
        seating[`${roomId}:${seatKey}`] = {
          ...normalizeExamStudent(studentSource, index),
          roomId,
          seatKey,
          seatNo: Number(valueOf(item, ['seatNo', 'seat_no', 'sira'], index + 1)),
          fixed: Boolean(valueOf(item, ['fixed', 'sabit'], false)),
          constraintViolated: Boolean(valueOf(item, ['constraint_violated', 'constraintViolated'], false)),
        };
      }
    });
  }
  return {
    seating,
    fixedIds: new Set(Object.values(seating).filter((item) => item.fixed).map((item) => String(item.id))),
    blockedSeats: new Set(asArray(root?.settings?.blockedSeats || root?.settings?.blocked_seats).map(String)),
    commission: asArray(root?.commission),
    observers: asArray(root?.observers),
    settings: root?.settings || {},
  };
}

function ExamModalLayer({ children, nested = false, className = '' }) {
  return <div className={`${nested ? 'exam-nested-layer' : 'exam-manager-layer'} ${className}`.trim()}>{children}</div>;
}

function YaziliAyarlaWindow({ subjects, classes, teachers, onClose }) {
  const [showExamManager, setShowExamManager] = useState(false);
  const [showRoomManager, setShowRoomManager] = useState(false);
  const [showSinavmatik, setShowSinavmatik] = useState(false);
  const [writtenExams, setWrittenExams] = useState([]);
  const [writtenExamsLoading, setWrittenExamsLoading] = useState(false);
  const [examClasses, setExamClasses] = useState(classes);

  const loadWrittenExams = useCallback(async () => {
    setWrittenExamsLoading(true);
    try {
      const { data } = await axios.get(WRITTEN_EXAM_API);
      setWrittenExams(extractWrittenExams(data));
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Yazılı sınavlar yüklenemedi'));
    } finally {
      setWrittenExamsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWrittenExams();
  }, [loadWrittenExams]);

  useEffect(() => {
    setExamClasses(classes);
  }, [classes]);

  useEffect(() => {
    axios.get('/siniflar')
      .then(({ data }) => {
        const payload = data?.data ?? data ?? [];
        const freshClasses = normalizeClasses(payload.siniflar || payload.classes || payload);
        if (freshClasses.length > 0) setExamClasses(freshClasses);
      })
      .catch(() => {});
  }, []);

  const modules = [
    { title: 'Dersler', icon: <FaBookOpen />, className: 'dersler', action: () => toast('Dersler mevcut akademik tanımlardan alınır') },
    { title: 'Sınıflar', icon: <FaSchool />, className: 'siniflar', action: () => toast('Sınıflar mevcut sınıf tanımlarından alınır') },
    { title: 'Öğrenciler', icon: <FaUserGraduate />, className: 'ogrenciler', action: () => toast('Öğrenciler mevcut öğrenci listesinden alınır') },
    { title: 'Öğrencilere\nDers Atanması', icon: <FaTasks />, className: 'atama', action: () => toast('Öğrenci ders atama ekranı backend bağlantısıyla açılabilir') },
    { title: 'Öğretmenler', icon: <FaChalkboardTeacher />, className: 'ogretmenler', action: () => toast('Öğretmenler mevcut öğretmen listesinden alınır') },
    { title: 'Sınav Salonları', icon: <FaBuilding />, className: 'salonlar', action: () => writtenExams.length > 0 ? setShowRoomManager(true) : toast.error('Önce sınav tanımlayın') },
    { title: 'Sınav Tanımla', icon: <FaClipboardCheck />, className: 'sinav', action: () => setShowExamManager(true) },
    { title: 'Sınavmatik', icon: <FaRegCalendarAlt />, className: 'sinavmatik', action: () => setShowSinavmatik(true) },
    { title: 'Yedekleme', icon: <FaArchive />, className: 'yedek', action: () => toast('Yedekleme işlemi ders programı yedekleme modülünden yapılır') },
  ];

  return (
    <WindowModal title="Yazılı Ayarla" width={980} onClose={onClose}>
      <div className="exam-desktop">
        <div className="exam-toolbar">
          <div className="exam-brand">
            <img src={asderLogo} alt="ASDER Sistem" />
            <strong>ASDER</strong>
            <span>Yazılı Yönetimi</span>
          </div>
          <ToolbarTile icon={<FaUsers />} label="Programlar" onClick={() => toast('Yazılı programları bu ekrandan yönetilecek')} active />
          <ToolbarTile icon={<FaWrench />} label="Dosya\nBakımı" onClick={() => toast('Dosya bakımı için backend endpointi tanımlanabilir')} />
          <ToolbarTile icon={<FaCog />} label="Ayarlar" onClick={() => toast('Yazılı ayarları bu modüle bağlanabilir')} />
          <button className="exam-contact" type="button" onClick={() => toast('İletişim bilgileri kurum ayarlarından yönetilir')}>💬 İletişim</button>
        </div>

        <h2>ORTAK SINAV-1</h2>

        <div className="exam-module-grid">
          {modules.map((module) => (
            <button key={module.title} className="exam-module" type="button" onClick={module.action}>
              <span className={`exam-module-icon ${module.className}`}>{module.icon}</span>
              <b>{module.title.split('\n').map((part, index) => <React.Fragment key={part}>{index > 0 && <br />}{part}</React.Fragment>)}</b>
            </button>
          ))}
        </div>

        <div className="exam-bottom-actions">
          <button type="button" onClick={() => toast('Net yedekleme için backend bağlantısı eklenebilir')}>💾 Net Yedekleme</button>
          <button type="button" onClick={onClose}>🚪 Programdan Çıkış</button>
        </div>

        {showExamManager && (
          <ExamDefinitionWindow
            subjects={subjects}
            classes={examClasses}
            exams={writtenExams}
            setExams={setWrittenExams}
            loading={writtenExamsLoading}
            onReload={loadWrittenExams}
            onClose={() => setShowExamManager(false)}
          />
        )}

        {showRoomManager && (
          <ExamRoomsManagerWindow
            exams={writtenExams}
            setExams={setWrittenExams}
            classes={examClasses}
            onClose={() => setShowRoomManager(false)}
          />
        )}

        {showSinavmatik && (
          <SinavmatikWindow
            exams={writtenExams}
            classes={examClasses}
            teachers={teachers}
            onClose={() => setShowSinavmatik(false)}
          />
        )}
      </div>
    </WindowModal>
  );
}

function ExamDefinitionWindow({ subjects, classes, exams, setExams, loading, onReload, onClose }) {
  const [selectedExamId, setSelectedExamId] = useState('');
  const [examFormMode, setExamFormMode] = useState(null);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [savingExam, setSavingExam] = useState(false);

  const selectedExam = exams.find((exam) => String(exam.id) === String(selectedExamId)) || exams[0] || null;
  const selectedId = selectedExam?.id || '';

  useEffect(() => {
    if (!selectedExamId && exams[0]) setSelectedExamId(exams[0].id);
    if (selectedExamId && exams.length > 0 && !exams.some((exam) => String(exam.id) === String(selectedExamId))) {
      setSelectedExamId(exams[0].id);
    }
  }, [exams, selectedExamId]);

  const saveExam = async (form) => {
    const payload = {
      date: form.date,
      time: form.time,
      description: form.description.trim(),
    };
    setSavingExam(true);
    try {
      let savedExam = null;
      if (form.id) {
        toast.error('Mevcut backend sözleşmesinde sınav güncelleme endpointi bulunmuyor');
        return;
      } else {
        const { data } = await axios.post(WRITTEN_EXAM_API, payload);
        savedExam = extractWrittenExamRecord(data);
        toast.success('Sınav eklendi');
      }
      await onReload();
      if (savedExam?.id) setSelectedExamId(savedExam.id);
      setExamFormMode(null);
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Sınav kaydedilemedi'));
    } finally {
      setSavingExam(false);
    }
  };

  const updateSelectedExam = (updater) => {
    if (!selectedId) return;
    setExams((current) => current.map((exam) => String(exam.id) === String(selectedId) ? updater(exam) : exam));
  };

  const addParticipants = async (participants) => {
    if (!selectedExam) return;
    try {
      const { data } = await axios.post(`${WRITTEN_EXAM_API}/${selectedExam.id}/participants`, { participants });
      const responseParticipants = asArray(data?.participants || data?.katilimcilar);
      const students = extractExamStudents(data);
      updateSelectedExam((exam) => ({
        ...exam,
        participants: responseParticipants.length > 0 ? responseParticipants : participants,
        students: students.length > 0 ? students : exam.students,
      }));
      await onReload();
      setShowStudentForm(false);
      toast.success('Öğrenciler sınava eklendi');
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Katılımcılar kaydedilemedi'));
    }
  };

  const addRooms = async (rooms) => {
    if (!selectedExam) return;
    try {
      const roomPayload = rooms.map((room) => {
        const layout = buildRoomSeatLayout(room);
        return { ...room, rowCount: layout.rows, columnCount: layout.cols };
      });
      const { data } = await axios.put(`${WRITTEN_EXAM_API}/${selectedExam.id}/rooms`, { rooms: roomPayload });
      const savedRooms = extractExamRooms(data);
      updateSelectedExam((exam) => ({ ...exam, rooms: savedRooms }));
      setShowRoomForm(false);
      toast.success('Salonlar eklendi');
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Salonlar kaydedilemedi'));
    }
  };

  const removeExam = async () => {
    if (!selectedExam) return toast.error('Önce sınav seçin');
    toast.error('Mevcut backend sözleşmesinde sınav silme endpointi bulunmuyor');
  };

  const removeParticipants = async () => {
    if (!selectedExam) return toast.error('Önce sınav seçin');
    try {
      await axios.delete(`${WRITTEN_EXAM_API}/${selectedExam.id}/participants`);
      updateSelectedExam((exam) => ({ ...exam, participants: [], students: [] }));
      toast.success('Öğrenciler temizlendi');
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Öğrenciler temizlenemedi'));
    }
  };

  const removeRooms = async () => {
    if (!selectedExam) return toast.error('Önce sınav seçin');
    try {
      await axios.delete(`${WRITTEN_EXAM_API}/${selectedExam.id}/rooms`);
      updateSelectedExam((exam) => ({ ...exam, rooms: [] }));
      toast.success('Salonlar silindi');
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Salonlar silinemedi'));
    }
  };

  const openStudentForm = () => {
    if (!selectedExam) return toast.error('Önce sınav ekleyin');
    setShowStudentForm(true);
  };

  const openRoomForm = () => {
    if (!selectedExam) return toast.error('Önce sınav ekleyin');
    setShowRoomForm(true);
  };

  const participants = asArray(selectedExam?.participants);
  const examStudents = buildExamStudents(selectedExam);
  const rooms = asArray(selectedExam?.rooms);
  const levelRows = Object.values(participants.reduce((acc, item) => {
    const key = item.level;
    acc[key] = acc[key] || { level: key, count: 0 };
    acc[key].count += Number(item.count || 0);
    return acc;
  }, {}));

  return (
    <ExamModalLayer>
      <div className="exam-manager-window">
        <div className="hp-window-titlebar">
          <div><strong>Sınavlar</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="exam-manager-body">
          <div className="exam-columns">
            <ExamPanel title="SINAVLAR" className="exam-list-panel">
              <DenseTable headers={['No', 'Tarih', 'Saat', 'Açıklama', 'Ö.S.', 'Y.S.']} minWidth={470}>
                {loading ? (
                  <tr><td colSpan={6} className="hp-empty-cell">Sınavlar yükleniyor...</td></tr>
                ) : exams.length === 0 ? (
                  <tr><td colSpan={6} className="hp-empty-cell">Sınav eklenmedi</td></tr>
                ) : exams.map((exam, index) => {
                  const totalStudents = asArray(exam.participants).reduce((sum, item) => sum + Number(item.count || 0), 0);
                  const roomCapacity = asArray(exam.rooms).reduce((sum, item) => sum + Number(item.capacity || 0), 0);
                  return (
                    <tr key={exam.id} className={String(exam.id) === String(selectedId) ? 'selected' : ''} onClick={() => setSelectedExamId(exam.id)}>
                      <td>{index + 1}</td>
                      <td>{compactDate(exam.date)}</td>
                      <td>{exam.time}</td>
                      <td>{exam.description}</td>
                      <td>{totalStudents || ''}</td>
                      <td>{roomCapacity || ''}</td>
                    </tr>
                  );
                })}
              </DenseTable>
            </ExamPanel>

            <ExamPanel title="DÜZEYLER" className="exam-level-panel">
              <DenseTable headers={['Sınıf Düzeyi', 'Öğ.Sa']} minWidth={160}>
                {levelRows.length === 0 ? (
                  <tr><td colSpan={2} className="hp-empty-cell"></td></tr>
                ) : levelRows.map((row) => (
                  <tr key={row.level}><td>{row.level}</td><td>{row.count}</td></tr>
                ))}
              </DenseTable>
            </ExamPanel>

            <ExamPanel title="SINIFLAR" className="exam-class-panel">
              <DenseTable headers={['Sınıfın Adı', '']} minWidth={230}>
                {participants.length === 0 ? (
                  <tr><td colSpan={2} className="hp-empty-cell"></td></tr>
                ) : participants.map((item) => (
                  <tr key={`${item.classId}_${item.subjectId}`}><td>{item.className}</td><td>{item.shortSubject}</td></tr>
                ))}
              </DenseTable>
            </ExamPanel>

            <ExamPanel title="SINAV GİRECEK ÖĞRENCİLER" className="exam-student-panel">
              <DenseTable headers={['No', 'Öğ.No', 'Adı Soyadı']} minWidth={390}>
                {examStudents.length === 0 ? (
                  <tr><td colSpan={3} className="hp-empty-cell"></td></tr>
                ) : examStudents.map((student, index) => (
                  <tr key={student.id}>
                    <td>{index + 1}</td>
                    <td>{student.studentNo}</td>
                    <td>{student.name}</td>
                  </tr>
                ))}
              </DenseTable>
            </ExamPanel>

            <ExamPanel title="SALONLAR" className="exam-room-panel">
              <DenseTable headers={['Salon Adı', 'Kap.']} minWidth={170}>
                {rooms.length === 0 ? (
                  <tr><td colSpan={2} className="hp-empty-cell"></td></tr>
                ) : rooms.map((room) => (
                  <tr key={room.roomId}><td>{room.name}</td><td>{room.capacity}</td></tr>
                ))}
              </DenseTable>
            </ExamPanel>
          </div>

          <div className="exam-manager-actions">
            <button type="button" onClick={() => setExamFormMode('add')}>📋 Sınav Ekle</button>
            <button type="button" onClick={() => selectedExam ? setExamFormMode('edit') : toast.error('Önce sınav seçin')}>📝 Düzelt</button>
            <button type="button" onClick={() => toast('Sınav yukarı taşındı')}>⬇</button>
            <button type="button" onClick={() => toast('Sınav aşağı taşındı')}>⬆</button>
            <button type="button" onClick={() => window.print()}>🖨 Yazdır</button>
            <button type="button" onClick={removeExam}>📣 Sınav Sil</button>
            <button type="button" onClick={openStudentForm}>📋 Öğrenci Ekle</button>
            <button type="button" onClick={removeParticipants}>📣 Öğrencileri Temizle</button>
            <button type="button" onClick={openRoomForm}>📋 Salon Ekle</button>
            <button type="button" onClick={removeRooms}>📣 Salon Sil</button>
            <button type="button" onClick={onClose}>🚪 Kapat</button>
          </div>
        </div>

        {examFormMode && (
          <ExamAddDialog
            exam={examFormMode === 'edit' ? selectedExam : null}
            saving={savingExam}
            onSave={saveExam}
            onClose={() => setExamFormMode(null)}
          />
        )}

        {showStudentForm && (
          <ExamStudentDialog
            subjects={subjects}
            classes={classes}
            onSave={addParticipants}
            onClose={() => setShowStudentForm(false)}
          />
        )}

        {showRoomForm && (
          <ExamRoomDialog
            classes={classes}
            onSave={addRooms}
            onClose={() => setShowRoomForm(false)}
          />
        )}
      </div>
    </ExamModalLayer>
  );
}

function ExamPanel({ title, className = '', children }) {
  return (
    <section className={`exam-panel ${className}`}>
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function ExamAddDialog({ exam, saving, onSave, onClose }) {
  const [form, setForm] = useState({
    id: exam?.id || '',
    date: exam?.date || '',
    time: exam?.time || '',
    description: exam?.description || '',
  });

  const submit = () => {
    if (!form.date || !form.time || !form.description.trim()) {
      return toast.error('Sınav tarihi, saati ve açıklama zorunlu');
    }
    onSave(form);
  };

  return (
    <ExamModalLayer nested>
      <div className="exam-small-dialog exam-add-dialog">
        <div className="hp-window-titlebar">
          <div><strong>{exam ? 'Sınav Düzelt' : 'Sınav Ekle'}</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="exam-form-body">
          <Field label="Sınav Tarihi">
            <input type="date" value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} />
          </Field>
          <Field label="Sınav Saati">
            <input type="time" value={form.time} onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))} />
          </Field>
          <Field label="Açıklama">
            <input value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="MATEMATİK" />
          </Field>
          <div className="exam-dialog-actions">
            <button type="button" onClick={submit} disabled={saving}>{saving ? 'Kaydediliyor...' : '✅ Tamam'}</button>
            <button type="button" onClick={onClose} disabled={saving}>❌ İptal</button>
          </div>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function ExamStudentDialog({ subjects, classes, onSave, onClose }) {
  const levels = useMemo(() => {
    const grouped = {};
    classes.forEach((classItem) => {
      const level = classGrade(classItem);
      grouped[level] = grouped[level] || { level, count: 0 };
      grouped[level].count += classStudentCount(classItem);
    });
    return Object.values(grouped).sort((a, b) => Number(a.level) - Number(b.level));
  }, [classes]);
  const [selectedLevel, setSelectedLevel] = useState(levels[0]?.level || '');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState(subjects[0]?.id || '');
  const [sameLevel, setSameLevel] = useState(true);
  const [hideConflicts, setHideConflicts] = useState(false);
  const [participants, setParticipants] = useState([]);

  const levelClasses = classes.filter((classItem) => classGrade(classItem) === selectedLevel);
  const selectedSubject = subjects.find((subject) => String(subject.id) === String(selectedSubjectId)) || subjects[0];

  useEffect(() => {
    if (!selectedClassId && levelClasses[0]) setSelectedClassId(levelClasses[0].id);
  }, [levelClasses, selectedClassId]);

  const addSelection = (classItem, subjectOverride = selectedSubject) => {
    if (!subjectOverride || !classItem) return;
    const next = {
      classId: String(classItem.id),
      className: classItem.name,
      level: classGrade(classItem),
      subjectId: String(subjectOverride.id),
      subjectName: subjectOverride.name,
      shortSubject: subjectOverride.shortName || subjectOverride.name.slice(0, 3).toUpperCase(),
      count: classStudentCount(classItem),
    };
    setParticipants((current) => {
      const exists = current.some((item) => item.classId === next.classId && item.subjectId === next.subjectId);
      return exists ? current : [...current, next];
    });
  };

  const addCurrent = (subjectOverride = selectedSubject) => {
    if (sameLevel) {
      levelClasses.forEach((classItem) => addSelection(classItem, subjectOverride));
      return;
    }
    addSelection(levelClasses.find((item) => String(item.id) === String(selectedClassId)), subjectOverride);
  };

  const removeSelected = () => setParticipants((current) => current.slice(0, -1));
  const total = participants.reduce((sum, item) => sum + item.count, 0);

  return (
    <ExamModalLayer nested>
      <div className="exam-student-dialog">
        <div className="hp-window-titlebar">
          <div><strong>AddStudentForm</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="exam-student-body">
          <div className="exam-student-left">
            <ExamPanel title="DÜZEYLER">
              <DenseTable headers={['Sınıf Düzeyi', '']} minWidth={160}>
                {levels.map((row) => (
                  <tr key={row.level} className={row.level === selectedLevel ? 'selected' : ''} onClick={() => { setSelectedLevel(row.level); setSelectedClassId(''); }}>
                    <td>{row.level}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </DenseTable>
            </ExamPanel>
            <ExamPanel title="SINIFLAR">
              <DenseTable headers={['Sınıfın Adı', '']} minWidth={170}>
                {levelClasses.map((classItem) => (
                  <tr key={classItem.id} className={String(classItem.id) === String(selectedClassId) ? 'selected' : ''} onClick={() => setSelectedClassId(classItem.id)}>
                    <td>{classItem.name}</td>
                    <td>{classStudentCount(classItem)}</td>
                  </tr>
                ))}
              </DenseTable>
            </ExamPanel>
          </div>

          <ExamPanel title="DERSLER" className="exam-student-subjects">
            <DenseTable headers={['No', 'Dersin Adı']} minWidth={430}>
              {subjects.map((subject, index) => (
                <tr
                  key={subject.id}
                  className={String(subject.id) === String(selectedSubjectId) ? 'selected' : ''}
                  onClick={() => {
                    setSelectedSubjectId(subject.id);
                    addCurrent(subject);
                  }}
                  onDoubleClick={() => addCurrent(subject)}
                >
                  <td>{index + 1}</td>
                  <td>{subject.name}</td>
                </tr>
              ))}
            </DenseTable>
          </ExamPanel>

          <ExamPanel title="SINAVA KATILACAKLAR" className="exam-student-participants">
            <DenseTable headers={['Sınıfın Adı', 'Dersin Adı', 'Öğ.Sa.']} minWidth={520}>
              {participants.length === 0 ? (
                <tr><td colSpan={3} className="hp-empty-cell"></td></tr>
              ) : participants.map((item) => (
                <tr key={`${item.classId}_${item.subjectId}`}>
                  <td>{item.className}</td>
                  <td>{item.subjectName}</td>
                  <td>{item.count}</td>
                </tr>
              ))}
            </DenseTable>
          </ExamPanel>

          <label className="hp-check exam-student-check">
            <input type="checkbox" checked={sameLevel} onChange={(e) => setSameLevel(e.target.checked)} />
            Aynı düzeydeki tüm sınıfları ekle
          </label>
          <label className="hp-check exam-student-check">
            <input type="checkbox" checked={hideConflicts} onChange={(e) => setHideConflicts(e.target.checked)} />
            Çakışanları Gizle
          </label>
          <div className="exam-student-total">
            <span>Toplam</span>
            <strong>{total}</strong>
          </div>
          <div className="exam-student-actions">
            <button type="button" onClick={removeSelected}>📣 Sil</button>
            <button type="button" onClick={() => onSave(participants)}>🚪 Kapat</button>
          </div>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function ExamRoomDialog({ classes, onSave, onClose }) {
  const [selectedIds, setSelectedIds] = useState(new Set(classes.map((item) => String(item.id))));
  const rows = classes.map((classItem, index) => ({
    id: String(classItem.id),
    no: index + 1,
    name: classItem.name,
    code: classItem.name,
    capacity: classRoomCapacity(classItem),
    studentCount: classStudentCount(classItem),
  }));

  const toggle = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = () => {
    const rooms = rows
      .filter((row) => selectedIds.has(row.id))
      .map((row) => ({
        classId: row.id,
        name: row.name,
        capacity: row.capacity,
        rowCount: buildRoomSeatLayout(row).rows,
        columnCount: buildRoomSeatLayout(row).cols,
      }));
    onSave(rooms);
  };

  return (
    <ExamModalLayer nested>
      <div className="exam-room-dialog">
        <div className="hp-window-titlebar">
          <div><strong>Salon Ekle</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="exam-room-body">
          <DenseTable headers={['No', 'Salon Adı', 'K.Adı', 'Kap.', '']} minWidth={560}>
            {rows.map((row) => (
              <tr key={row.id} className={selectedIds.has(row.id) ? 'selected' : ''} onClick={() => toggle(row.id)}>
                <td>{row.no}</td>
                <td>{row.name}</td>
                <td>{row.code}</td>
                <td>{row.capacity}</td>
                <td>{selectedIds.has(row.id) ? 'X' : ''}</td>
              </tr>
            ))}
          </DenseTable>
          <div className="exam-room-actions">
            <button type="button" onClick={() => setSelectedIds(new Set(rows.map((row) => row.id)))}>✅ Tümü</button>
            <button type="button" onClick={() => setSelectedIds(new Set())}>❌ Hiçbiri</button>
            <button type="button" onClick={submit}>🖨 Sınıflar</button>
            <button type="button" onClick={onClose}>🚪 Kapat</button>
          </div>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function ExamRoomsManagerWindow({ exams, setExams, classes, onClose }) {
  const [saving, setSaving] = useState(false);
  const [selectedExamId, setSelectedExamId] = useState(exams[0]?.id || '');
  const selectedExam = exams.find((exam) => String(exam.id) === String(selectedExamId)) || exams[0] || null;
  const rows = useMemo(() => classes.map((classItem, index) => ({
    id: String(classItem.id),
    no: index + 1,
    name: classItem.name,
    code: classItem.name,
    capacity: classRoomCapacity(classItem),
    studentCount: classStudentCount(classItem),
  })), [classes]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewRoomId, setPreviewRoomId] = useState('');
  const previewRoom = rows.find((row) => String(row.id) === String(previewRoomId)) || rows[0] || null;
  const previewLayout = useMemo(() => buildRoomSeatLayout(previewRoom || 30), [previewRoom]);

  useEffect(() => {
    if (!selectedExam?.id) return;
    axios.get(`${WRITTEN_EXAM_API}/${selectedExam.id}/rooms`)
      .then(({ data }) => {
        const savedRooms = extractExamRooms(data);
        setExams((current) => current.map((exam) => (
          String(exam.id) === String(selectedExam.id) ? { ...exam, rooms: savedRooms } : exam
        )));
      })
      .catch((error) => toast.error(writtenExamErrorMessage(error, 'Sınav salonları yüklenemedi')));
  }, [selectedExam?.id, setExams]);

  useEffect(() => {
    const examRooms = asArray(selectedExam?.rooms);
    const ids = examRooms.length > 0
      ? examRooms.map((room) => String(room.classId || room.class_id || room.roomId || room.id))
      : rows.map((row) => row.id);
    setSelectedIds(new Set(ids));
    if ((!previewRoomId || !rows.some((row) => String(row.id) === String(previewRoomId))) && rows[0]) setPreviewRoomId(rows[0].id);
  }, [selectedExamId, selectedExam, rows, previewRoomId]);

  const toggle = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveRooms = async () => {
    if (!selectedExam) return toast.error('Önce sınav seçin');
    const rooms = rows
      .filter((row) => selectedIds.has(row.id))
      .map((row) => ({
        classId: row.id,
        name: row.name,
        capacity: row.capacity,
        rowCount: buildRoomSeatLayout(row).rows,
        columnCount: buildRoomSeatLayout(row).cols,
      }));
    if (rooms.length === 0) return toast.error('En az bir salon seçin');
    setSaving(true);
    try {
      const { data } = await axios.put(`${WRITTEN_EXAM_API}/${selectedExam.id}/rooms`, { rooms });
      const savedRooms = extractExamRooms(data);
      setExams((current) => current.map((exam) => (
        String(exam.id) === String(selectedExam.id) ? { ...exam, rooms: savedRooms } : exam
      )));
      toast.success('Sınav salonları seçildi');
      onClose();
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Sınav salonları kaydedilemedi'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ExamModalLayer>
      <div className="exam-room-dialog">
        <div className="hp-window-titlebar">
          <div><strong>Sınav Salonları</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="exam-room-body">
          <div className="exam-room-layout">
            <div className="exam-room-list-side">
              <Field label="Sınav">
                <select value={selectedExam?.id || ''} onChange={(event) => setSelectedExamId(event.target.value)}>
                  {exams.map((exam) => (
                    <option key={exam.id} value={exam.id}>
                      {compactDate(exam.date)} {exam.time} - {exam.description}
                    </option>
                  ))}
                </select>
              </Field>
              <DenseTable headers={['No', 'Salon Adı', 'K.Adı', 'Kap.', '']} minWidth={560}>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={String(row.id) === String(previewRoom?.id) ? 'selected' : ''}
                    onClick={() => setPreviewRoomId(row.id)}
                    onDoubleClick={() => toggle(row.id)}
                  >
                    <td>{row.no}</td>
                    <td>{row.name}</td>
                    <td>{row.code}</td>
                    <td>{row.capacity}</td>
                    <td>
                      <button className="room-check" type="button" onClick={(event) => { event.stopPropagation(); toggle(row.id); }}>
                        {selectedIds.has(row.id) ? 'X' : ''}
                      </button>
                    </td>
                  </tr>
                ))}
              </DenseTable>
              <div className="exam-room-actions">
                <button type="button" onClick={() => setSelectedIds(new Set(rows.map((row) => row.id)))}>✅ Tümü</button>
                <button type="button" onClick={() => setSelectedIds(new Set())}>❌ Hiçbiri</button>
                <button type="button" onClick={saveRooms} disabled={saving}>{saving ? 'Kaydediliyor...' : '✅ Kaydet'}</button>
                <button type="button" onClick={onClose}>🚪 Kapat</button>
              </div>
            </div>
            <div className="exam-room-plan-side">
              <div className="exam-room-plan-head">
                <strong>{previewRoom?.name || 'Salon'}</strong>
                <span>Kapasite: {previewRoom?.capacity || 0}</span>
              </div>
              <div className="room-plan-cols">
                <span></span>
                {Array.from({ length: previewLayout.cols }, (_, col) => <b key={col}>{col + 1}</b>)}
              </div>
              <div className="room-plan-grid" style={{ gridTemplateColumns: `34px repeat(${previewLayout.cols}, 1fr)` }}>
                {Array.from({ length: previewLayout.rows }, (_, row) => (
                  <React.Fragment key={row}>
                    <b className="room-row-label">{row + 1}</b>
                    {Array.from({ length: previewLayout.cols }, (_, col) => {
                      const seatKey = `${row}_${col}`;
                      const active = previewLayout.seats.includes(seatKey);
                      return <button key={seatKey} type="button" className={`room-plan-cell${active ? '' : ' blocked'}`} />;
                    })}
                  </React.Fragment>
                ))}
              </div>
              <div className="room-plan-tools">
                <label><input type="radio" checked readOnly /> İkili Oturma Düzeni</label>
                <span>2'li sıra blokları, kapasite dışı alanlar kırmızı</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ExamModalLayer>
  );
}

const SAMPLE_TEACHERS = [
  { id: 'sample-teacher-1', name: 'RUMEYSA', branch: 'MATEMATİK' },
  { id: 'sample-teacher-2', name: 'EREN', branch: 'MATEMATİK' },
  { id: 'sample-teacher-3', name: 'SÜHEDA', branch: 'MATEMATİK' },
  { id: 'sample-teacher-4', name: 'NURCAN', branch: 'FEN BİLİMLERİ' },
  { id: 'sample-teacher-5', name: 'RUKİYE', branch: 'FEN BİLİMLERİ' },
  { id: 'sample-teacher-6', name: 'ŞÜKRAN', branch: 'FEN BİLİMLERİ' },
  { id: 'sample-teacher-7', name: 'KADER', branch: 'SOSYAL BİLGİLER' },
  { id: 'sample-teacher-8', name: 'MUSTAFA', branch: 'BEDEN EĞİTİMİ' },
  { id: 'sample-teacher-9', name: 'NEDİM', branch: 'BEDEN EĞİTİMİ' },
  { id: 'sample-teacher-10', name: 'AYŞE', branch: 'DİN KÜLTÜRÜ' },
];

function buildExamStudents(exam) {
  return asArray(exam?.students).map(normalizeExamStudent);
}

function buildExamRooms(exam, classes) {
  const rooms = asArray(exam?.rooms);
  const classMap = Object.fromEntries(asArray(classes).map((classItem) => [String(classItem.id), classItem]));
  if (rooms.length > 0) {
    return rooms.map((room, index) => {
      const id = String(room.roomId || room.id || index);
      const classItem = classMap[id] || room;
      return {
        id,
        name: room.name || room.className || classItem.name || `Salon ${index + 1}`,
        capacity: classRoomCapacity(classItem),
        studentCount: classStudentCount(classItem) || roomStudentCount(room),
        rowCount: Number(room.rowCount || room.row_count || 0),
        columnCount: Number(room.columnCount || room.column_count || 0),
      };
    });
  }
  return classes.slice(0, 4).map((classItem) => ({
    id: String(classItem.id),
    name: classItem.name,
    capacity: classRoomCapacity(classItem),
    studentCount: classStudentCount(classItem),
  }));
}

function buildRoomSeatLayout(roomOrCapacity) {
  const room = roomOrCapacity && typeof roomOrCapacity === 'object' ? roomOrCapacity : {};
  const total = Math.max(1, Number(room.capacity || roomOrCapacity || 30));
  const cols = Math.max(1, Number(room.columnCount || room.column_count || 6));
  const deskCols = Array.from({ length: cols }, (_, index) => index);
  const rows = Math.max(1, Number(room.rowCount || room.row_count || Math.ceil(total / cols)));
  const roomId = String(room.id || room.roomId || '');
  const seats = [];
  for (let row = 0; row < rows; row += 1) {
    deskCols.forEach((col) => {
      if (seats.length < total) seats.push(roomId ? `${roomId}_${seats.length + 1}` : `${row}_${col}`);
    });
  }
  return { rows, cols, deskCols, seats };
}

function normalizeExamTeachers(teachers) {
  const source = asArray(teachers).length > 0 ? teachers : SAMPLE_TEACHERS;
  return source.map((teacher, index) => ({
    id: String(teacher.id || teacher.teacherId || index + 1),
    name: teacher.name || teacher.fullName || teacher.adSoyad || `Öğretmen ${index + 1}`,
    branch: teacher.branch || teacher.brans || teacher.subject || '',
  }));
}

function SinavmatikWindow({ exams, classes, teachers, onClose }) {
  const [selectedExamId, setSelectedExamId] = useState(exams[0]?.id || '');
  const selectedExam = exams.find((exam) => String(exam.id) === String(selectedExamId)) || exams[0] || null;
  const [remoteStudents, setRemoteStudents] = useState([]);
  const [remoteRooms, setRemoteRooms] = useState([]);
  const rooms = remoteRooms;
  const students = remoteStudents;
  const teacherRows = useMemo(() => normalizeExamTeachers(teachers), [teachers]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedSeatKey, setSelectedSeatKey] = useState('');
  const [seating, setSeating] = useState({});
  const [fixedIds, setFixedIds] = useState(new Set());
  const [blockedSeats, setBlockedSeats] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [showCommission, setShowCommission] = useState(false);
  const [showObserver, setShowObserver] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showExamPaperPrint, setShowExamPaperPrint] = useState(false);
  const [commission, setCommission] = useState([]);
  const [observers, setObservers] = useState({});
  const [observerPeriod, setObserverPeriod] = useState('1');
  const [unplacedStudents, setUnplacedStudents] = useState([]);
  const loadingExamState = useRef(false);
  const [settings, setSettings] = useState({
    automatic: true,
    avoidSameClassAround: true,
    sameGenderTogether: false,
    doubleSeatMode: false,
  });

  const applySinavmatikPayload = useCallback((payload) => {
    const data = payload?.data || payload || {};
    const nextStudents = extractExamStudents(data.students || []);
    const nextRooms = extractExamRooms(data.rooms || []);
    const saved = extractSeatingState({ data }, nextStudents);
    const teacherMap = Object.fromEntries(teacherRows.map((teacher) => [String(teacher.id), teacher]));
    const nextCommission = asArray(data.commission).map((item) => {
      const teacherId = String(valueOf(item, ['teacherId', 'teacher_id', 'id'], item));
      return item?.teacher || teacherMap[teacherId] || { id: teacherId, name: valueOf(item, ['teacherName', 'teacher_name', 'name'], teacherId) };
    });
    const nextObservers = {};
    asArray(data.observers).forEach((item) => {
      const roomId = String(valueOf(item, ['roomId', 'room_id'], ''));
      const teacherId = String(valueOf(item, ['teacherId', 'teacher_id'], ''));
      if (!roomId || !teacherId) return;
      nextObservers[roomId] = nextObservers[roomId] || [];
      nextObservers[roomId].push(item.teacher || teacherMap[teacherId] || { id: teacherId, name: valueOf(item, ['teacherName', 'teacher_name'], teacherId) });
    });
    setObserverPeriod(String(valueOf(asArray(data.observers)[0], ['examPeriod', 'exam_period'], '1')));
    setRemoteStudents(nextStudents);
    setRemoteRooms(nextRooms);
    setSeating(saved.seating);
    setFixedIds(saved.fixedIds);
    setBlockedSeats(saved.blockedSeats);
    setSettings((current) => ({ ...current, ...saved.settings }));
    setCommission(nextCommission);
    setObservers(nextObservers);
    return { data, students: nextStudents, rooms: nextRooms, saved };
  }, [teacherRows]);

  useEffect(() => {
    if (!selectedExamId && exams[0]) setSelectedExamId(exams[0].id);
  }, [exams, selectedExamId]);

  useEffect(() => {
    if (!selectedRoomId && rooms[0]) setSelectedRoomId(rooms[0].id);
  }, [rooms, selectedRoomId]);

  useEffect(() => {
    setSeating({});
    setFixedIds(new Set());
    setSelectedStudentId('');
    setSelectedSeatKey('');
    setRemoteStudents([]);
    setRemoteRooms([]);
    setUnplacedStudents([]);
  }, [selectedExamId]);

  useEffect(() => {
    if (!selectedExam?.id) return undefined;
    let active = true;
    loadingExamState.current = true;
    Promise.all([
      axios.get(`${WRITTEN_EXAM_API}/${selectedExam.id}/sinavmatik`),
      axios.get(`${WRITTEN_EXAM_API}/${selectedExam.id}/rooms`),
    ])
      .then(([sinavmatikResponse, roomsResponse]) => {
        if (!active) return;
        applySinavmatikPayload(sinavmatikResponse.data);
        setRemoteRooms(extractExamRooms(roomsResponse.data));
      })
      .catch((error) => { if (active) toast.error(writtenExamErrorMessage(error, 'Sınavmatik verileri yüklenemedi')); })
      .finally(() => { loadingExamState.current = false; });
    return () => { active = false; };
  }, [selectedExam?.id, applySinavmatikPayload]);

  useEffect(() => {
    const handler = (event) => {
      if (event.key !== 'F8') return;
      event.preventDefault();
      const selectedStudent = students.find((student) => student.id === selectedStudentId);
      if (!selectedStudent) return;
      const isPlaced = Object.values(seating).some((student) => student.id === selectedStudent.id);
      if (!isPlaced) return toast.error('Sabitlemek için önce öğrenciyi yerleştirin');
      setFixedIds((current) => {
        const next = new Set([...current, selectedStudent.id]);
        persistSeating(seating, next);
        return next;
      });
      toast.success('Öğrenci sabitlendi');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedStudentId, seating, students]);

  const selectedRoom = rooms.find((room) => room.id === selectedRoomId) || rooms[0] || null;
  const placedCount = Object.keys(seating).length;
  const totalStudents = students.length;
  const remainingCount = Math.max(totalStudents - placedCount, 0);
  const selectedLayout = useMemo(() => buildRoomSeatLayout(selectedRoom || 30), [selectedRoom]);
  const seatRows = selectedLayout.rows;
  const seatCols = selectedLayout.cols;
  const seatKeys = selectedLayout.seats;
  const roomSeatKey = (roomId, seatKey) => `${roomId}:${seatKey}`;
  const seatCoords = (seatKey) => {
    const [row, col] = String(seatKey).split('_').map(Number);
    return { row, col };
  };
  const sameClassKey = (student) => String(student?.classId || student?.className || '');
  const sameLevelKey = (student) => String(student?.level || classGrade({ name: student?.className }) || '');
  const hasSameClassNeighbor = (next, roomId, seatKey, student) => {
    const room = rooms.find((item) => String(item.id) === String(roomId)) || selectedRoom;
    const layout = buildRoomSeatLayout(room?.capacity || 30);
    const { row, col } = seatCoords(seatKey);
    const studentClass = sameClassKey(student);
    const studentLevel = sameLevelKey(student);
    if (!studentClass && !studentLevel) return false;
    for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
      for (let colOffset = -1; colOffset <= 1; colOffset += 1) {
        if (rowOffset === 0 && colOffset === 0) continue;
        const neighborRow = row + rowOffset;
        const neighborCol = col + colOffset;
        if (neighborRow < 0 || neighborRow >= layout.rows || neighborCol < 0 || neighborCol >= layout.cols) continue;
        if (!layout.deskCols.includes(neighborCol)) continue;
        const neighbor = next[roomSeatKey(roomId, `${neighborRow}_${neighborCol}`)];
        if (!neighbor) continue;
        if (studentClass && sameClassKey(neighbor) === studentClass) return true;
        if (studentLevel && sameLevelKey(neighbor) === studentLevel) return true;
      }
    }
    return false;
  };
  const seatFitsRules = (next, roomId, seatKey, student, strict = true) => {
    if (!strict) return true;
    if (settings.avoidSameClassAround && hasSameClassNeighbor(next, roomId, seatKey, student)) return false;
    return true;
  };
  const interleaveByClass = (items) => {
    const groups = {};
    items.forEach((student) => {
      const key = `${sameLevelKey(student)}_${sameClassKey(student)}`;
      groups[key] = groups[key] || [];
      groups[key].push(student);
    });
    const buckets = Object.values(groups).sort((a, b) => b.length - a.length);
    const result = [];
    let added = true;
    while (added) {
      added = false;
      buckets.forEach((bucket) => {
        const nextStudent = bucket.shift();
        if (nextStudent) {
          result.push(nextStudent);
          added = true;
        }
      });
    }
    return result;
  };

  const persistSeating = async (nextSeating, nextFixedIds = fixedIds) => {
    if (!selectedExam?.id || loadingExamState.current) return false;
    const seatingPayload = Object.entries(nextSeating).map(([fullKey, student], index) => {
      const separator = fullKey.indexOf(':');
      return {
        roomId: separator >= 0 ? fullKey.slice(0, separator) : student.roomId,
        seatKey: separator >= 0 ? fullKey.slice(separator + 1) : fullKey,
        seatNo: Number(student.seatNo || index + 1),
        studentId: student.id,
        fixed: nextFixedIds.has(String(student.id)),
      };
    });
    try {
      await axios.put(`${WRITTEN_EXAM_API}/${selectedExam.id}/seating`, { seating: seatingPayload });
      return true;
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Oturma planı kaydedilemedi'));
      return false;
    }
  };

  const persistSettings = async (nextSettings = settings, nextBlockedSeats = blockedSeats) => {
    if (!selectedExam?.id) return;
    try {
      await axios.put(`${WRITTEN_EXAM_API}/${selectedExam.id}/settings`, {
        automatic: Boolean(nextSettings.automatic),
        avoidSameClassAround: Boolean(nextSettings.avoidSameClassAround),
        sameGenderTogether: Boolean(nextSettings.sameGenderTogether),
        blockedSeats: [...nextBlockedSeats],
      });
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Sınavmatik ayarları kaydedilemedi'));
    }
  };

  const persistCommission = async (nextCommission = commission) => {
    if (!selectedExam?.id) return;
    try {
      await axios.put(`${WRITTEN_EXAM_API}/${selectedExam.id}/commission`, {
        teacherIds: nextCommission.map((teacher) => String(teacher.id)),
      });
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Komisyon kaydedilemedi'));
    }
  };

  const persistObservers = async (nextObservers = observers, examPeriod = '1') => {
    if (!selectedExam?.id) return;
    const observerPayload = Object.entries(nextObservers).flatMap(([roomId, items]) => asArray(items).map((teacher, slotIndex) => ({
      roomId: String(roomId),
      teacherId: String(teacher.id),
      slotIndex,
      examPeriod: String(examPeriod || '1'),
    })));
    try {
      await axios.put(`${WRITTEN_EXAM_API}/${selectedExam.id}/observers`, { observers: observerPayload });
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Gözetmenler kaydedilemedi'));
    }
  };

  const ensureStudents = () => {
    if (students.length > 0) return students;
    toast.error('Gerçek öğrenci listesi bulunamadı; katılımcı endpointini kontrol edin');
    return [];
  };

  const placeStudent = async (student, preferredKey = '') => {
    if (!student || !selectedRoom) return;
    const targetSeatKey = preferredKey || selectedSeatKey || seatKeys.find((key) => !seating[roomSeatKey(selectedRoom.id, key)] && !blockedSeats.has(roomSeatKey(selectedRoom.id, key)));
    const targetKey = targetSeatKey ? roomSeatKey(selectedRoom.id, targetSeatKey) : '';
    if (!targetKey) return toast.error('Boş sıra bulunamadı');
    if (blockedSeats.has(targetKey)) return toast.error('Bu sıra kullanıma kapalı');
    const next = Object.fromEntries(Object.entries(seating).filter(([, item]) => item.id !== student.id));
    next[targetKey] = { ...student, roomId: selectedRoom.id, roomName: selectedRoom.name, seatKey: targetSeatKey, seatNo: seatKeys.indexOf(targetSeatKey) + 1, fixed: fixedIds.has(String(student.id)) };
    if (!await persistSeating(next)) return;
    setSeating(next);
    setSelectedStudentId(student.id);
    setSelectedSeatKey(targetSeatKey);
  };

  const autoPlace = async () => {
    if (!selectedExam) return toast.error('Önce sınav tanımlayın');
    if (rooms.length === 0) return toast.error('Önce en az bir salon seçip kaydedin.');
    try {
      const { data: response } = await axios.post(`${WRITTEN_EXAM_API}/${selectedExam.id}/auto-seat`);
      const { data: refreshedResponse } = await axios.get(`${WRITTEN_EXAM_API}/${selectedExam.id}/sinavmatik`);
      const applied = applySinavmatikPayload(refreshedResponse);
      const studentMap = Object.fromEntries(applied.students.map((student) => [String(student.id), student]));
      const nextUnplacedStudents = asArray(response?.unplaced).map((item) => ({
        ...(studentMap[String(item.studentId)] || { id: String(item.studentId), name: '' }),
        reason: item.reason,
      }));
      setUnplacedStudents(nextUnplacedStudents);
      const unplacedCount = Number(response?.allocation?.unplacedStudents ?? nextUnplacedStudents.length);
      const placedCountAfterAllocation = Number(response?.allocation?.placedStudents || 0);
      if (placedCountAfterAllocation > 0) toast.success('Otomatik yerleştirme tamamlandı');
      if (unplacedCount > 0) toast.error(`${unplacedCount} öğrenci yerleştirilemedi`);
      if (placedCountAfterAllocation === 0 && unplacedCount === 0) toast.error('Yerleştirilecek öğrenci bulunamadı');
      asArray(response?.warnings).forEach((warning) => toast(warning.message || String(warning)));
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Otomatik yerleştirme tamamlanamadı'));
    }
  };

  const printSeatingPlan = async () => {
    if (!selectedExam?.id) return toast.error('Önce sınav seçin');
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
      toast.error('Yazdırma penceresine izin verin.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head><title>Oturma Planı</title></head>
        <body>Yazdırma verileri hazırlanıyor...</body>
      </html>
    `);
    printWindow.document.close();
    let printData = null;
    try {
      const { data: response } = await axios.get(`${WRITTEN_EXAM_API}/${selectedExam.id}/print-data`);
      printData = response?.data;
    } catch (error) {
      printWindow.close();
      toast.error(writtenExamErrorMessage(error, 'Yazdırma verileri yüklenemedi'));
      return;
    }
    const roomPlans = asArray(printData.roomPlans || printData.room_plans);
    const classAnnouncements = asArray(printData.classAnnouncements || printData.class_announcements);
    if (roomPlans.length === 0) {
      printWindow.close();
      toast.error('Yazdırılacak oturma planı bulunamadı.');
      return;
    }
    const printExam = normalizeWrittenExam(printData.exam || selectedExam);
    const safeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const roomPages = roomPlans.map((roomPlan) => {
      const rows = asArray(roomPlan.grid);
      const columnCount = Math.max(1, Number(roomPlan.columnCount || roomPlan.column_count || Math.max(0, ...rows.map((row) => asArray(row).length)) || 1));
      const gridCells = rows.flatMap((row, rowIndex) => {
        const cells = asArray(row);
        return Array.from({ length: columnCount }, (_, colIndex) => {
          const seat = cells[colIndex] || {};
          const seatNo = Number(seat.seatNo || seat.seat_no || (rowIndex * columnCount) + colIndex + 1);
          if (seat.blocked) return `<div class="seat blocked"><span>Sıra ${seatNo}</span><strong>KAPALI</strong></div>`;
          const student = seat.student || null;
          if (!student) return `<div class="seat empty"><span>Sıra ${seatNo}</span><strong>BOŞ</strong></div>`;
          const fullName = cleanExamText(student.fullName || student.full_name || student.name);
          const studentNo = cleanExamText(student.studentNo || student.student_no);
          const className = cleanExamText(student.className || student.class_name);
          return `<div class="seat filled">
            <span>Sıra ${seatNo}</span>
            ${fullName ? `<strong>${safeHtml(fullName)}</strong>` : ''}
            ${studentNo ? `<em>No: ${safeHtml(studentNo)}</em>` : ''}
            ${className ? `<em>${safeHtml(className)}</em>` : ''}
          </div>`;
        });
      }).join('');
      const roomName = roomPlan.roomName || roomPlan.room_name || '';
      return `<section class="room-plan">
        <header><div><h1>SALON OTURMA PLANI</h1><h2>${safeHtml(printExam.description || 'Sınav')}</h2></div>
          <div class="exam-meta"><strong>${safeHtml(roomName)}</strong><span>${safeHtml(compactDate(printExam.date))} ${safeHtml(printExam.time)}</span></div></header>
        <div class="seat-grid" style="grid-template-columns:repeat(${columnCount},minmax(0,1fr));">${gridCells}</div>
      </section>`;
    }).join('');
    const announcementPages = classAnnouncements.map((announcement) => {
      const rows = asArray(announcement.students).map((student) => `<tr>
        <td>${safeHtml(cleanExamText(student.studentNo || student.student_no))}</td>
        <td>${safeHtml(cleanExamText(student.fullName || student.full_name || student.name))}</td>
        <td>${safeHtml(cleanExamText(student.roomName || student.room_name))}</td>
        <td>${safeHtml(student.seatNo || student.seat_no || '')}</td>
      </tr>`).join('');
      return `<section class="announcement-page">
        <header><div><h1>SINIF DUYURU LİSTELERİ</h1><h2>${safeHtml(announcement.className || announcement.class_name || '')}</h2></div>
          <div class="exam-meta"><span>${safeHtml(compactDate(printExam.date))} ${safeHtml(printExam.time)}</span><strong>${safeHtml(printExam.description)}</strong></div></header>
        <table><thead><tr><th>Öğrenci No</th><th>Ad Soyad</th><th>Sınav Salonu</th><th>Sıra No</th></tr></thead><tbody>${rows}</tbody></table>
      </section>`;
    }).join('');
    const fullPrintHtml = `
      <!doctype html>
      <html lang="tr">
        <head>
          <meta charset="utf-8" />
          <title>${safeHtml(printExam.description || 'Yazılı Sınav')} - Oturma Planı</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            @page roomPlan { size: A4 landscape; margin: 10mm; }
            @page classList { size: A4 portrait; margin: 12mm; }
            * { box-sizing: border-box; }
            body { margin:0; background:#e5e7eb; font-family:Arial,sans-serif; color:#111827; }
            .room-plan { page:roomPlan; width:297mm; min-height:210mm; margin:12px auto; padding:10mm; background:#fff; break-after:page; page-break-after:always; }
            .room-plan:last-child { break-after:auto; page-break-after:auto; }
            .announcement-page { page:classList; width:210mm; min-height:297mm; margin:12px auto; padding:14mm; background:#fff; page-break-before:always; page-break-after:always; }
            header { display:flex; justify-content:space-between; align-items:flex-end; gap:20px; margin-bottom:8mm; border-bottom:2px solid #111827; padding-bottom:5mm; }
            h1 { margin:0; font-size:22px; } h2 { margin:5px 0 0; font-size:17px; }
            .exam-meta { display:grid; gap:5px; text-align:right; font-size:14px; }
            .seat-grid { display: grid; gap: 6px; }
            .seat { min-height:24mm; border:1.5px solid #94a3b8; padding:5px; background:#f8fafc; display:flex; flex-direction:column; gap:3px; }
            .seat.filled { background: #dcfce7; border-color: #16a34a; }
            .seat.blocked { background:#fee2e2; border-color:#dc2626; color:#991b1b; align-items:center; justify-content:center; }
            .seat.empty { color:#64748b; align-items:center; justify-content:center; }
            .seat span { font-size:9px; color:inherit; font-weight:800; } .seat strong { font-size:11px; line-height:1.15; }
            .seat em { font-style:normal; font-size:9px; color:inherit; font-weight:700; }
            table { width:100%; border-collapse:collapse; font-size:12px; } th,td { border:1px solid #64748b; padding:7px; text-align:left; }
            th { background:#e2e8f0; } th:first-child,td:first-child,th:last-child,td:last-child { width:22mm; text-align:center; }
            @media print {
              html, body { display:block !important; visibility:visible !important; background:white; }
              .room-plan { margin:0; break-after:page; page-break-after:always; }
              .room-plan:last-child { break-after:auto; page-break-after:auto; }
              .announcement-page { margin:0; }
            }
          </style>
        </head>
        <body>${roomPages}${announcementPages}</body>
      </html>
    `;
    if (process.env.NODE_ENV === 'development') {
      console.log(printData);
      console.log(fullPrintHtml.length);
    }
    if (!fullPrintHtml.length) {
      printWindow.close();
      toast.error('Yazdırma içeriği oluşturulamadı');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(fullPrintHtml);
    printWindow.document.close();
    if (printWindow.document.fonts?.ready) await printWindow.document.fonts.ready;
    await new Promise((resolve) => {
      printWindow.requestAnimationFrame(() => {
        printWindow.requestAnimationFrame(resolve);
      });
    });
    setTimeout(() => {
      if (printWindow.closed) return;
      printWindow.focus();
      printWindow.print();
    }, 300);
    return null;
  };

  const clearSeating = async () => {
    if (!selectedExam?.id) return;
    try {
      await axios.delete(`${WRITTEN_EXAM_API}/${selectedExam.id}/seating`);
      setSeating({});
      setFixedIds(new Set());
      setUnplacedStudents([]);
      toast.success('Oturma planı temizlendi');
    } catch (error) {
      toast.error(writtenExamErrorMessage(error, 'Oturma planı temizlenemedi'));
    }
  };

  const toggleBlockedSeat = (key) => {
    if (!selectedRoom || seating[roomSeatKey(selectedRoom.id, key)]) return;
    const fullKey = roomSeatKey(selectedRoom.id, key);
    setBlockedSeats((current) => {
      const next = new Set(current);
      if (next.has(fullKey)) next.delete(fullKey);
      else next.add(fullKey);
      persistSettings(settings, next);
      return next;
    });
  };

  return (
    <ExamModalLayer className="sinavmatik-layer">
      <div className="sinavmatik-window">
        <div className="hp-window-titlebar">
          <div><strong>Sınavmatik</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="sinavmatik-body">
          <div className="sinavmatik-left">
            <div className="sinavmatik-top-lists">
              <ExamPanel title="SINAVLAR">
                <DenseTable headers={['No', 'Tarih', 'Saat', 'Açıklama', 'Ö.S.']} minWidth={430}>
                  {exams.length === 0 ? (
                    <tr><td colSpan={5} className="hp-empty-cell">Sınav tanımla ekranından sınav ekleyin</td></tr>
                  ) : exams.map((exam, index) => (
                    <tr key={exam.id} className={exam.id === selectedExam?.id ? 'selected' : ''} onClick={() => setSelectedExamId(exam.id)}>
                      <td>{index + 1}</td>
                      <td>{compactDate(exam.date)}</td>
                      <td>{exam.time}</td>
                      <td>{exam.description}</td>
                      <td>{exam.participants.reduce((sum, item) => sum + item.count, 0)}</td>
                    </tr>
                  ))}
                </DenseTable>
              </ExamPanel>
              <ExamPanel title="SALONLAR">
                <DenseTable headers={['Salon Adı', 'Kap.']} minWidth={160}>
                  {rooms.map((room) => (
                    <tr key={room.id} className={room.id === selectedRoom?.id ? 'selected' : ''} onClick={() => setSelectedRoomId(room.id)}>
                      <td>{room.name}</td>
                      <td>{room.capacity}</td>
                    </tr>
                  ))}
                </DenseTable>
              </ExamPanel>
            </div>

            <ExamPanel title="">
              <DenseTable headers={['No', 'Öğ.No', 'Adı Soyadı', 'Sınıfı', 'Dersin Adı', '']} minWidth={610}>
                {students.length === 0 ? (
                  <tr><td colSpan={6} className="hp-empty-cell">Öğrenci ekleyin veya Eksik Öğrencileri Ekle butonuna basın</td></tr>
                ) : students.map((student) => {
                  const isFixed = fixedIds.has(student.id);
                  return (
                    <tr
                      key={student.id}
                      className={student.id === selectedStudentId ? 'selected' : ''}
                      onClick={() => setSelectedStudentId(student.id)}
                      onDoubleClick={() => placeStudent(student)}
                    >
                      <td>{student.no}</td>
                      <td>{student.studentNo}</td>
                      <td>{student.name}</td>
                      <td>{student.className}</td>
                      <td>{student.subjectName}</td>
                      <td>{isFixed ? 'X' : ''}</td>
                    </tr>
                  );
                })}
              </DenseTable>
            </ExamPanel>
            <div className="sinavmatik-extra-actions">
              <button type="button" onClick={ensureStudents}>↔ Eksik Öğrencileri Ekle</button>
              <button type="button" onClick={autoPlace}>▣ Kalanları Programı Bozmadan Yerleştir</button>
            </div>
          </div>

          <div className="sinavmatik-right">
            {unplacedStudents.length > 0 && (
              <div className="sinavmatik-unplaced-alert" role="alert">
                <strong>{unplacedStudents.length} öğrenci yerleştirilemedi</strong>
                <span>{unplacedStudents.map((student) => `${student.studentNo ? `${student.studentNo} - ` : ''}${student.name}`).join(', ')}</span>
              </div>
            )}
            <div className="sinavmatik-counters">
              <span>Toplam <strong>{totalStudents}</strong></span>
              <span>Yerleşen <strong>{placedCount}</strong></span>
              <span>Kalan <strong>{remainingCount}</strong></span>
              <input value={selectedExam?.time || ''} readOnly />
            </div>
            <div className="sinavmatik-seat-grid" style={{ gridTemplateColumns: `repeat(${seatCols}, minmax(60px, 1fr))` }}>
              {Array.from({ length: seatRows * seatCols }, (_, index) => {
                const row = Math.floor(index / seatCols);
                const col = index % seatCols;
                const key = selectedLayout.seats[index] || `${row}_${col}`;
                if (!selectedLayout.deskCols.includes(col)) {
                  return <div key={key} className="sinav-aisle" />;
                }
                const fullKey = selectedRoom ? roomSeatKey(selectedRoom.id, key) : key;
                const student = seating[fullKey];
                const fixed = student && fixedIds.has(student.id);
                const blocked = blockedSeats.has(fullKey);
                const constraintViolated = Boolean(student?.constraintViolated);
                return (
                  <button
                    key={key}
                    type="button"
                    title={constraintViolated ? 'Komşuluk kuralı zorunlu olarak ihlal edildi' : undefined}
                    className={`sinav-seat${student ? ' filled' : ''}${fixed ? ' fixed' : ''}${constraintViolated ? ' constraint-violated' : ''}${blocked ? ' blocked' : ''}${key === selectedSeatKey ? ' active' : ''}`}
                    onClick={() => setSelectedSeatKey(key)}
                    onDoubleClick={() => {
                      if (student) {
                        setSelectedStudentId(student.id);
                        setFixedIds((current) => {
                          const next = new Set([...current, String(student.id)]);
                          persistSeating(seating, next);
                          return next;
                        });
                        return;
                      }
                      const selectedStudent = students.find((item) => item.id === selectedStudentId);
                      if (selectedStudent) placeStudent(selectedStudent, key);
                      else toggleBlockedSeat(key);
                    }}
                  >
                    {student && (
                      <>
                        {student.name && <b title={student.name}>{student.name}</b>}
                        {student.studentNo && <span title={student.studentNo}>No: {student.studentNo}</span>}
                        {student.className && <span title={student.className}>{student.className}</span>}
                        {student.seatNo > 0 && <em>Sıra: {student.seatNo}</em>}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="sinavmatik-actions">
          <button type="button" onClick={() => setShowPrintMenu(true)}>🖨 Yazdır</button>
          <button type="button" onClick={() => setShowCommission(true)}>🚶 Komisyon</button>
          <button type="button" onClick={() => setShowObserver(true)}>🚶 Gözcü Ata</button>
          <button type="button" onClick={() => setShowSettings(true)}>🛠 Ayarlar</button>
          <button type="button" onClick={autoPlace}>🖨 Otomat</button>
          <button type="button" onClick={() => toast('Yerleşmeyen öğrenciler listesi güncel ekranda kalan sayısıyla takip ediliyor')}>📄 Yerleşmeyen Öğrenciler</button>
          <button type="button" disabled>🚫 Durdur</button>
          <button type="button" onClick={clearSeating}>📣 Temizle</button>
          <button type="button" onClick={onClose}>🚪 Kapat</button>
        </div>

        {showSettings && (
          <SinavmatikSettingsDialog
            settings={settings}
            setSettings={setSettings}
            blockedSeats={blockedSeats}
            setBlockedSeats={setBlockedSeats}
            onPersist={(nextSettings, nextBlockedSeats) => persistSettings(nextSettings, nextBlockedSeats)}
            onClose={() => setShowSettings(false)}
          />
        )}
        {showCommission && (
          <SinavCommissionDialog
            teachers={teacherRows}
            commission={commission}
            setCommission={setCommission}
            subjectName={selectedExam?.description || ''}
            onClose={() => { persistCommission(); setShowCommission(false); }}
          />
        )}
        {showObserver && (
          <SinavObserverDialog
            rooms={rooms}
            teachers={teacherRows}
            observers={observers}
            setObservers={setObservers}
            examDate={selectedExam?.date}
            initialPeriod={observerPeriod}
            onPersist={(nextObservers, examPeriod) => { setObserverPeriod(examPeriod); persistObservers(nextObservers, examPeriod); }}
            onClose={() => setShowObserver(false)}
          />
        )}
        {showPrintMenu && (
          <SinavmatikPrintMenu
            onSeatingPlan={printSeatingPlan}
            onExamPapers={() => {
              setShowPrintMenu(false);
              setShowExamPaperPrint(true);
            }}
            onClose={() => setShowPrintMenu(false)}
          />
        )}
        {showExamPaperPrint && (
          <SinavPaperPrintDialog
            selectedExam={selectedExam}
            students={students}
            rooms={rooms}
            seating={seating}
            onClose={() => setShowExamPaperPrint(false)}
          />
        )}
      </div>
    </ExamModalLayer>
  );
}

function SinavmatikSettingsDialog({ settings, setSettings, blockedSeats, setBlockedSeats, onPersist, onClose }) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [singleBlocks, setSingleBlocks] = useState(new Set(['0_1', '1_0', '1_2', '2_1']));
  const [doubleBlocks, setDoubleBlocks] = useState(new Set());

  const toggleGrid = (setter) => (key) => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = () => {
    setSettings(localSettings);
    const nextBlockedSeats = new Set([...blockedSeats]);
    setBlockedSeats(nextBlockedSeats);
    onPersist(localSettings, nextBlockedSeats);
    toast.success('Sınavmatik ayarları kaydedildi');
    onClose();
  };

  return (
    <ExamModalLayer nested>
      <div className="sinavmatik-dialog sinavmatik-settings">
        <div className="hp-window-titlebar">
          <div><strong>Ayarlar</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="sinavmatik-settings-body">
          <section>
            <h3>TEKLİ OTURMA DÜZENİ</h3>
            <SeatRuleGrid rows={3} cols={3} blocked={singleBlocks} onToggle={toggleGrid(setSingleBlocks)} center />
          </section>
          <section>
            <h3>İKİLİ OTURMA DÜZENİ</h3>
            <div className="double-seat-rules">
              <SeatRuleGrid rows={3} cols={2} blocked={doubleBlocks} onToggle={toggleGrid(setDoubleBlocks)} center />
              <SeatRuleGrid rows={3} cols={2} blocked={doubleBlocks} onToggle={toggleGrid(setDoubleBlocks)} />
            </div>
          </section>
          <label><input type="checkbox" checked={localSettings.avoidSameClassAround} onChange={(e) => setLocalSettings((prev) => ({ ...prev, avoidSameClassAround: e.target.checked }))} /> Aynı sınıf/düzey olanları yan, arka ve çapraz getirme</label>
          <label><input type="checkbox" checked={localSettings.sameGenderTogether} onChange={(e) => setLocalSettings((prev) => ({ ...prev, sameGenderTogether: e.target.checked }))} /> E-E, K-K</label>
          <label><input type="checkbox" checked={localSettings.automatic} onChange={(e) => setLocalSettings((prev) => ({ ...prev, automatic: e.target.checked }))} /> Otomatik Ayarlar</label>
          <div className="sinavmatik-dialog-actions">
            <button type="button" onClick={save}>✅ Tamam</button>
            <button type="button" onClick={onClose}>❌ İptal</button>
          </div>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function SeatRuleGrid({ rows, cols, blocked, onToggle, center = false }) {
  return (
    <div className="seat-rule-grid" style={{ gridTemplateColumns: `repeat(${cols}, 58px)` }}>
      {Array.from({ length: rows * cols }, (_, index) => {
        const row = Math.floor(index / cols);
        const col = index % cols;
        const key = `${row}_${col}`;
        const isCenter = center && row === 1 && col === 1;
        return (
          <button
            key={key}
            type="button"
            className={`${blocked.has(key) ? 'blocked' : ''}${isCenter ? ' student' : ''}`}
            onDoubleClick={() => !isCenter && onToggle(key)}
          >
            {isCenter ? '👤' : ''}
          </button>
        );
      })}
    </div>
  );
}

function SinavCommissionDialog({ teachers, commission, setCommission, subjectName, onClose }) {
  const addTeacher = (teacher) => {
    setCommission((current) => current.some((item) => item.id === teacher.id) ? current : [...current, teacher]);
  };
  const removeLast = () => setCommission((current) => current.slice(0, -1));

  return (
    <ExamModalLayer nested>
      <div className="sinavmatik-dialog sinav-commission">
        <div className="hp-window-titlebar">
          <div><strong>Sınav Komisyonu</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="sinav-commission-body">
          <ExamPanel title="ÖĞRETMENLER">
            <DenseTable headers={['No', 'Branşı', 'Adı Soyadı']} minWidth={420}>
              {teachers.map((teacher, index) => (
                <tr key={teacher.id} onDoubleClick={() => addTeacher(teacher)}>
                  <td>{index + 1}</td>
                  <td>{teacher.branch}</td>
                  <td>{teacher.name}</td>
                </tr>
              ))}
            </DenseTable>
          </ExamPanel>
          <ExamPanel title="SINAV KOMİSYONU">
            <DenseTable headers={['No', 'Adı Soyadı']} minWidth={350}>
              {commission.length === 0 ? (
                <tr><td colSpan={2} className="hp-empty-cell"></td></tr>
              ) : commission.map((teacher, index) => (
                <tr key={teacher.id}><td>{index + 1}</td><td>{teacher.name}</td></tr>
              ))}
            </DenseTable>
          </ExamPanel>
          <ExamPanel title="">
            <DenseTable headers={['Dersin Adı']} minWidth={230}>
              <tr><td>{subjectName}</td></tr>
            </DenseTable>
          </ExamPanel>
        </div>
        <div className="sinavmatik-dialog-actions">
          <button type="button" onClick={removeLast}>📣 Sil</button>
          <button type="button" onClick={onClose}>🚪 Kapat</button>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function SinavObserverDialog({ rooms, teachers, observers, setObservers, examDate, initialPeriod, onPersist, onClose }) {
  const [selectedRoomId, setSelectedRoomId] = useState(rooms[0]?.id || '');
  const [period, setPeriod] = useState(initialPeriod || '1');
  const selectedRoom = rooms.find((room) => room.id === selectedRoomId) || rooms[0];

  const assignTeacher = (teacher) => {
    if (!selectedRoom) return;
    setObservers((current) => {
      const currentRoom = current[selectedRoom.id] || [];
      if (currentRoom.some((item) => item.id === teacher.id)) return current;
      return {
        ...current,
        [selectedRoom.id]: [...currentRoom.slice(0, 1), teacher].slice(0, 2),
      };
    });
  };

  const clearSelected = () => {
    if (!selectedRoom) return;
    setObservers((current) => ({ ...current, [selectedRoom.id]: [] }));
  };
  const closeAndPersist = () => {
    onPersist(observers, period);
    onClose();
  };

  return (
    <ExamModalLayer nested>
      <div className="sinavmatik-dialog sinav-observer">
        <div className="hp-window-titlebar">
          <div><strong>Gözcü Ata</strong></div>
          <button className="hp-icon-btn" type="button" onClick={closeAndPersist}>x</button>
        </div>
        <div className="sinav-observer-head">
          <input value={`${compactDate(examDate)}  Pazartesi`} readOnly />
          <label>Sınav Saati (Kaçıncı Ders) <input value={period} onChange={(e) => setPeriod(e.target.value)} /></label>
        </div>
        <div className="sinav-observer-body">
          <ExamPanel title="">
            <DenseTable headers={['No', 'Salon Adı', 'Gözcü Öğretmen', 'Gözcü Öğretmen']} minWidth={560}>
              {rooms.map((room, index) => {
                const assigned = observers[room.id] || [];
                return (
                  <tr key={room.id} className={room.id === selectedRoom?.id ? 'selected' : ''} onClick={() => setSelectedRoomId(room.id)}>
                    <td>{index + 1}</td>
                    <td>{room.name}</td>
                    <td>{assigned[0]?.name || ''}</td>
                    <td>{assigned[1]?.name || ''}</td>
                  </tr>
                );
              })}
            </DenseTable>
          </ExamPanel>
          <ExamPanel title="">
            <DenseTable headers={['No', 'Adı Soyadı']} minWidth={280}>
              {teachers.map((teacher, index) => (
                <tr key={teacher.id} onDoubleClick={() => assignTeacher(teacher)}>
                  <td>{index + 1}</td>
                  <td>{teacher.name}</td>
                </tr>
              ))}
            </DenseTable>
          </ExamPanel>
        </div>
        <div className="sinavmatik-dialog-actions">
          <button type="button" onClick={() => window.print()}>🖨 Yazdır</button>
          <button type="button" onClick={clearSelected}>📣 Sil</button>
          <button type="button" onClick={closeAndPersist}>🚪 Kapat</button>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function SinavmatikPrintMenu({ onSeatingPlan, onExamPapers, onClose }) {
  const printItems = [
    { label: 'Öğrenci Sınava Giriş Kartı', action: () => toast('Giriş kartı yazdırma backend raporuna bağlanabilir') },
    { label: 'Öğrenci Sınav Programı', action: () => toast('Öğrenci sınav programı raporu hazırlanabilir') },
    { label: 'Sınıf Sınav Programı', action: () => toast('Sınıf sınav programı raporu hazırlanabilir') },
    { label: 'Öğretmen Görev Çizelgesi', action: () => toast('Görev çizelgesi raporu hazırlanabilir') },
    { label: 'Oturma Planı ve Sınıf Duyuruları', action: onSeatingPlan },
    { label: 'Sınav Kağıtları', action: onExamPapers },
  ];

  return (
    <ExamModalLayer nested>
      <div className="sinavmatik-dialog sinav-print-menu">
        <div className="hp-window-titlebar">
          <div><strong>Yazdır</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="sinav-print-menu-body">
          {printItems.map((item) => (
            <button key={item.label} type="button" onClick={item.action}>🖨 {item.label}</button>
          ))}
          <button className="close" type="button" onClick={onClose}>🚪 Kapat</button>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function SinavPaperPrintDialog({ selectedExam, students, rooms, seating, onClose }) {
  const [loadedFile, setLoadedFile] = useState('');
  const [selectedRooms, setSelectedRooms] = useState(new Set(rooms.map((room) => room.id)));
  const placedStudents = useMemo(() => Object.entries(seating).map(([seatKey, student], index) => ({
    ...student,
    seatKey,
    seatNo: index + 1,
    roomName: student.roomName || rooms[0]?.name || '',
  })), [seating, rooms]);
  const printableStudents = placedStudents.length > 0 ? placedStudents : students.map((student, index) => ({
    ...student,
    seatNo: index + 1,
    roomName: rooms[index % Math.max(rooms.length, 1)]?.name || '',
  }));
  const levelRows = useMemo(() => {
    const grouped = {};
    printableStudents.forEach((student) => {
      const level = classGrade({ name: student.className });
      const key = `${level}_${student.subjectName}`;
      grouped[key] = grouped[key] || { level, subjectName: student.subjectName, fileName: loadedFile };
    });
    return Object.values(grouped);
  }, [printableStudents, loadedFile]);

  const toggleRoom = (roomId) => {
    setSelectedRooms((current) => {
      const next = new Set(current);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  };

  const safeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const selectedPrintableStudents = () => printableStudents.filter((student) => (
    selectedRooms.has(student.roomId || rooms[0]?.id)
  ));

  const buildPaperHtml = (items) => {
    const examTitle = safeHtml(selectedExam?.description || items[0]?.subjectName || 'SINAV');
    const pages = items.map((student) => `
      <section class="paper-page">
        <div class="paper-head">
          <h2>${examTitle}</h2>
          <h3>1. DÖNEM</h3>
          <h3>1. YAZILI SINAVI</h3>
        </div>
        <div class="paper-fields">
          <div><strong>Adı Soyadı:</strong> ${safeHtml(student.name)}</div>
          <div><strong>Sınıfı:</strong> ${safeHtml(student.className)}</div>
          <div><strong>Salonu:</strong> ${safeHtml(student.roomName || '')}</div>
          <div><strong>Sırası:</strong> ${safeHtml(student.seatNo)}</div>
          <div><strong>Öğrenci No:</strong> ${safeHtml(student.studentNo)}</div>
        </div>
      </section>
    `).join('');

    const html = `
      <html>
        <head>
          <title>Sınav Kağıtları</title>
          <style>
            @page { size: A4; margin: 18mm; }
            * { box-sizing: border-box; }
            body { margin: 0; background: #e5e7eb; font-family: Arial, sans-serif; color: #111; }
            .toolbar { position: sticky; top: 0; z-index: 2; display: flex; justify-content: center; gap: 10px; padding: 12px; background: #f8fafc; border-bottom: 1px solid #cbd5e1; }
            .toolbar button { min-width: 150px; height: 38px; border: 1px solid #b8bdc1; background: linear-gradient(#fff,#d8dde1); font-weight: 800; cursor: pointer; border-radius: 4px; }
            .paper-page { width: 210mm; min-height: 297mm; margin: 16px auto; padding: 28mm 24mm; background: #fff; box-shadow: 0 6px 18px rgba(15, 23, 42, .18); page-break-after: always; }
            .paper-head { text-align: center; margin-top: 22mm; }
            .paper-head h2, .paper-head h3 { margin: 10px 0; font-weight: 500; }
            .paper-fields { margin-top: 42mm; display: grid; gap: 18px; font-size: 16px; }
            .paper-fields strong { display: inline-block; min-width: 120px; }
            @media print {
              body { background: #fff; }
              .toolbar { display: none; }
              .paper-page { margin: 0; box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <button onclick="window.print()">Yazdır / PDF Kaydet</button>
            <button onclick="window.close()">Kapat</button>
          </div>
          ${pages}
        </body>
      </html>
    `;
    return html;
  };

  const openPaperWindow = ({ autoPrint = false, sampleOnly = false } = {}) => {
    const items = selectedPrintableStudents();
    const finalItems = sampleOnly ? items.slice(0, 1) : items;
    if (finalItems.length === 0) return toast.error('Yazdırılacak öğrenci yok');
    const popup = window.open('', '_blank');
    if (!popup) return toast.error('Yazdırma penceresi engellendi');
    popup.document.write(buildPaperHtml(finalItems));
    popup.document.close();
    if (autoPrint) {
      popup.focus();
      setTimeout(() => popup.print(), 350);
    }
    return null;
  };

  const openPreview = () => openPaperWindow({ sampleOnly: true });
  const printPapers = () => openPaperWindow({ autoPrint: true });
  const printSample = () => openPaperWindow({ autoPrint: true, sampleOnly: true });

  return (
    <ExamModalLayer nested>
      <div className="sinavmatik-dialog sinav-paper-print">
        <div className="hp-window-titlebar">
          <div><strong>Sınav Kağıtlarını Yazdır</strong></div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="sinav-paper-body">
          <div className="sinav-paper-main-table">
            <DenseTable headers={['No', 'Düzey', 'Dersin Adı', 'Dosya Adı']} minWidth={720}>
              {levelRows.length === 0 ? (
                <tr><td colSpan={4} className="hp-empty-cell">Sınav kağıdı için öğrenci bulunamadı</td></tr>
              ) : levelRows.map((row, index) => (
                <tr key={`${row.level}_${row.subjectName}`} className={index === 0 ? 'selected' : ''}>
                  <td>{index + 1}</td>
                  <td>{row.level}</td>
                  <td>{row.subjectName}</td>
                  <td>{row.fileName}</td>
                </tr>
              ))}
            </DenseTable>
          </div>
          <div className="sinav-paper-room-table">
            <DenseTable headers={['Salon Adı', '']} minWidth={170}>
              {rooms.map((room) => (
                <tr key={room.id} className={selectedRooms.has(room.id) ? '' : 'muted'} onClick={() => toggleRoom(room.id)}>
                  <td>{room.name}</td>
                  <td>{selectedRooms.has(room.id) ? 'X' : ''}</td>
                </tr>
              ))}
            </DenseTable>
            <div className="sinav-paper-room-actions">
              <button type="button" onClick={() => setSelectedRooms(new Set(rooms.map((room) => room.id)))}>✅ Tümü</button>
              <button type="button" onClick={() => setSelectedRooms(new Set())}>❌ Hiçbiri</button>
            </div>
          </div>
        </div>
        <div className="sinav-paper-actions">
          <label className="file-button">
            📋 Yükle
            <input
              type="file"
              accept=".doc,.docx,.rtf,.txt"
              onChange={(event) => setLoadedFile(event.target.files?.[0]?.name || '')}
            />
          </label>
          <button type="button" onClick={printPapers}>🖨 Yazdır / PDF</button>
          <button type="button" onClick={printSample}>🖨 Deneme Yazdır</button>
          <button type="button" onClick={openPreview}>📄 Aç</button>
          <button type="button" onClick={() => setLoadedFile('')}>📣 Sil</button>
          <button type="button" onClick={onClose}>🚪 Kapat</button>
        </div>
        <div className="sinav-paper-hint">
          X_ADI&nbsp;&nbsp; X_NO&nbsp;&nbsp; X_DERS&nbsp;&nbsp; X_SINIF&nbsp;&nbsp; X_SALON&nbsp;&nbsp; X_SIRA
          <span>(Hepsinin kullanılması mecburi değildir. Word Programı açıksa kapatın.)</span>
        </div>
      </div>
    </ExamModalLayer>
  );
}

function ToolbarTile({ icon, label, onClick, active = false, muted = false }) {
  return (
    <button className={`prag-toolbar-tile${active ? ' active' : ''}${muted ? ' muted' : ''}`} type="button" onClick={onClick}>
      <span>{icon}</span>
      <b>{label.split('\n').map((part, index) => <React.Fragment key={part}>{index > 0 && <br />}{part}</React.Fragment>)}</b>
    </button>
  );
}

function DesktopModule({ item }) {
  return (
    <button className="prag-module" type="button" onClick={item.action}>
      <span className={`prag-module-icon ${item.icon}`}>
        <DesktopIcon type={item.icon} />
      </span>
      <b>{item.title.split('\n').map((part, index) => <React.Fragment key={part}>{index > 0 && <br />}{part}</React.Fragment>)}</b>
    </button>
  );
}

function DesktopIcon({ type }) {
  const iconMap = {
    blocks: <FaBookOpen />,
    class: <FaSchool />,
    room: <FaBuilding />,
    teachers: <FaUserGraduate />,
    daily: <FaCalendarAlt />,
    classSubjects: <FaBook />,
    assignment: <FaTasks />,
    teacherSchedule: <FaChalkboardTeacher />,
    classSchedule: <FaTable />,
    roomSchedule: <FaUniversity />,
    newTable: <FaPlus />,
    duty: <FaBell />,
    extra: <FaMoneyBillWave />,
    board: <FaMapMarkedAlt />,
    examSetup: <FaClipboardCheck />,
    backup: <FaArchive />,
  };
  return iconMap[type] || <FaTable />;
}

function ScheduleProgramWindow({
  viewMode,
  setViewMode,
  visibleList,
  resolvedSelectedId,
  setSelectedId,
  activeDays,
  periods,
  settings,
  setSettings,
  subjectMap,
  classMap,
  teacherMap,
  timetableResult,
  classes,
  subjects,
  teachers,
  selectedCell,
  selectedCellData,
  setSelectedCell,
  setDragSource,
  getCellForSlot,
  handleDrop,
  handleCellDoubleClick,
  handlePendingLessonClick,
  handlePlacePendingLesson,
  handleAutoGenerate,
  autoGenerating,
  warnings,
  programStats,
  classClosedSlots,
  setClassClosedSlots,
  isClassSlotClosed,
  isClassSlotAllowed,
  loadClassClosedSlots,
  loadClosedSlotsForClasses,
  updateTimetableCell,
  setTimetableResult,
  rules,
  dailyLimits,
  pendingLessons,
  assignments = [],
  classSubjects = [],
  setClassSubjects,
  selectedPendingLesson,
  setSelectedPendingLesson,
  availabilityMap,
  setAvailabilityMap,
  onClose,
	}) {
	  const [lessonMenu, setLessonMenu] = useState(null);
	  const [placementEditor, setPlacementEditor] = useState(null);
	  const [autoSettingsOpen, setAutoSettingsOpen] = useState(false);
	  const [printOpen, setPrintOpen] = useState(false);
	  const [manualModalOpen, setManualModalOpen] = useState(false);
	  const [savingClosedSlots, setSavingClosedSlots] = useState(false);
	  const [periodTimeEditor, setPeriodTimeEditor] = useState(null);
	  const [savingPeriodTime, setSavingPeriodTime] = useState(false);
	  const [manualForm, setManualForm] = useState({
	    classId: '',
	    subjectId: '',
	    teacherId: '',
	    day: 0,
	    period: 0,
	    blockLength: 1,
	  });
	  const [printOptions, setPrintOptions] = useState({ type: 'el', orientation: 'horizontal' });
	  const [autoSettings, setAutoSettings] = useState({
	    allowTeacherGaps: 'E',
	    maxWeeklyGaps: 2,
	    maxBetweenLessonGaps: 2,
	    sameClassLessonsDifferentDays: 'E',
	    splitTwoHourAsOnePlusOne: 'H',
	    maxDailyTeacherHours: 7,
	  });
	  const [localDays, setLocalDays] = useState(settings);
	  const gridDays = settings.length > 0 ? settings : buildDefaultSettings();
	  const nameHeader = viewMode === 'teacher' ? 'Adı Soyadı' : viewMode === 'subject' ? 'Dersin Adı' : 'Sınıfın Adı';
	  const detailHeader = viewMode === 'teacher' ? 'Branşı' : viewMode === 'subject' ? 'Kısa Adı' : 'Alan';
	  const selectedClassId = viewMode === 'class' ? String(resolvedSelectedId || '') : String(classes[0]?.id || '');
	  const selectedClassClosedSet = classClosedSlots[String(selectedClassId)] || new Set();
	  useEffect(() => {
	    setLocalDays(settings);
	  }, [settings]);
	  const getPeriodTime = (period) => {
	    const slot = gridDays.find(day => day.timeSlots?.[period])?.timeSlots?.[period] || {};
	    return {
	      start: String(slot.start || ''),
	      end: String(slot.end || ''),
	    };
	  };
	  const openPeriodTimeEditor = (period) => {
	    const slot = getPeriodTime(period);
	    const isEmpty = (!slot.start && !slot.end) || (slot.start === '00:00' && slot.end === '00:00');
	    setPeriodTimeEditor({
	      period,
	      start: isEmpty ? '' : slot.start,
	      end: isEmpty ? '' : slot.end,
	    });
	  };
	  const savePeriodTime = async () => {
	    if (!periodTimeEditor) return;
	    const { period, start, end } = periodTimeEditor;
	    if (!start || !end) {
	      toast.error('Başlangıç ve bitiş saatini girin');
	      return;
	    }
	    if (end <= start) {
	      toast.error('Bitiş saati başlangıç saatinden büyük olmalı');
	      return;
	    }
	    const nextSettings = settings.map((day) => {
	      const currentSlots = asArray(day.timeSlots);
	      const slotCount = Math.max(18, currentSlots.length, period + 1);
	      const timeSlots = Array.from({ length: slotCount }, (_, index) => ({
	        ...(currentSlots[index] || { start: '00:00', end: '00:00' }),
	      }));
	      timeSlots[period] = { ...timeSlots[period], start, end };
	      return { ...day, timeSlots };
	    });

	    setSavingPeriodTime(true);
	    try {
	      const response = await axios.put(`${API}/ayarlar`, {
	        dailyConfigs: nextSettings,
	        daily_configs: nextSettings,
	      });
	      getPayload(response);
	      setSettings(nextSettings);
	      setPeriodTimeEditor(null);
	      toast.success(`${period + 1}. ders saati kaydedildi`);
	    } catch (error) {
	      toast.error(scheduleErrorMessage(error, 'Ders saati kaydedilemedi'));
	    } finally {
	      setSavingPeriodTime(false);
	    }
	  };
	  useEffect(() => {
	    setManualForm(prev => ({
	      ...prev,
	      classId: prev.classId || String(selectedClassId || classes[0]?.id || ''),
	      subjectId: prev.subjectId || String(subjects[0]?.id || ''),
	      teacherId: prev.teacherId || '',
	    }));
	  }, [classes, selectedClassId, subjects]);
	  const classSubjectMap = useMemo(() => {
	    const map = {};
	    classSubjects.forEach((item) => {
	      map[`${String(item.classId)}_${String(item.subjectId)}`] = item;
	    });
	    return map;
	  }, [classSubjects]);
	  const classSubjectHours = useMemo(() => Object.fromEntries(
	    Object.entries(classSubjectMap).map(([key, item]) => [key, Number(item.weeklyHours || 0)])
	  ), [classSubjectMap]);
	  const getLessonHours = useCallback((lesson) => {
	    const subjectId = valueOf(lesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '');
	    const classId = valueOf(lesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], '');
	    return Number(valueOf(lesson, ['weeklyHours', 'weekly_hours', 'haftalikSaat', 'haftalik_saat', 'hds', 'hours'], 0)) ||
	      Number(classSubjectHours[`${String(classId)}_${String(subjectId)}`] || 0) ||
	      Number(subjectMap[String(subjectId)]?.weeklyHours || 0);
	  }, [classSubjectHours, subjectMap]);
	  const teacherTotals = useMemo(() => assignments.reduce((totals, row) => {
	    const teacherId = String(valueOf(row, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	    if (!teacherId) return totals;
	    totals[teacherId] = (totals[teacherId] || 0) + getLessonHours(row);
	    return totals;
	  }, {}), [assignments, getLessonHours]);
	  const schedulableLessons = assignments;
	  const getSchedulableClassIds = useCallback((rows = schedulableLessons) => (
	    [...new Set(asArray(rows).map(row => String(valueOf(row, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''))).filter(Boolean))]
	  ), [schedulableLessons]);
	  const getSubjectOptionsForClass = useCallback((classId) => {
	    const subjectIds = classSubjects
	      .filter(item => String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')) === String(classId))
	      .map(item => String(valueOf(item, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '')))
	      .filter(Boolean);
	    const uniqueIds = [...new Set(subjectIds)];
	    const mapped = uniqueIds.map(id => subjectMap[id]).filter(Boolean);
	    return mapped.length > 0 ? mapped : subjects;
	  }, [classSubjects, subjectMap, subjects]);
	  const getTeacherOptionsForLesson = useCallback((classId, subjectId) => {
	    const teacherIds = assignments
	      .filter(row => (
	        String(valueOf(row, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')) === String(classId) &&
	        String(valueOf(row, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '')) === String(subjectId)
	      ))
	      .map(row => String(valueOf(row, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '')))
	      .filter(Boolean);
	    const uniqueIds = [...new Set(teacherIds)];
	    const mapped = uniqueIds.map(id => teacherMap[id]).filter(Boolean);
	    return mapped.length > 0 ? mapped : teachers;
	  }, [assignments, teacherMap, teachers]);
	  const manualSubjectOptions = useMemo(
	    () => getSubjectOptionsForClass(manualForm.classId),
	    [getSubjectOptionsForClass, manualForm.classId]
	  );
	  const manualTeacherOptions = useMemo(
	    () => getTeacherOptionsForLesson(manualForm.classId, manualForm.subjectId),
	    [getTeacherOptionsForLesson, manualForm.classId, manualForm.subjectId]
	  );
	  useEffect(() => {
	    const subjectIdValid = manualSubjectOptions.some(item => String(item.id) === String(manualForm.subjectId));
	    const nextSubjectId = subjectIdValid ? String(manualForm.subjectId || '') : String(manualSubjectOptions[0]?.id || '');
	    const teacherOptions = getTeacherOptionsForLesson(manualForm.classId, nextSubjectId);
	    const teacherIdValid = !manualForm.teacherId || teacherOptions.some(item => String(item.id) === String(manualForm.teacherId));
	    const nextTeacherId = teacherIdValid ? String(manualForm.teacherId || '') : '';
	    if (nextSubjectId !== String(manualForm.subjectId || '') || nextTeacherId !== String(manualForm.teacherId || '')) {
	      setManualForm(prev => ({ ...prev, subjectId: nextSubjectId, teacherId: nextTeacherId }));
	    }
	  }, [getTeacherOptionsForLesson, manualForm.classId, manualForm.subjectId, manualForm.teacherId, manualSubjectOptions]);
	  const visibleLessonRows = viewMode === 'teacher'
	    ? assignments.filter(row => String(valueOf(row, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '')) === String(resolvedSelectedId))
	    : pendingLessons;
	  const selectedEntityTotal = viewMode === 'teacher'
	    ? Number(teacherTotals[String(resolvedSelectedId)] || 0)
	    : pendingLessons.reduce((total, lesson) => total + getLessonHours(lesson), 0);
	  const placedVisible = activeDays.reduce((total, day) => (
	    total + periods.filter(period => period < day.periodCount && getCellForSlot(day.dayIndex, period)).length
	  ), 0);
	  const totalProgramHours = useMemo(() => (
	    schedulableLessons.reduce((total, lesson) => total + Number(getLessonHours(lesson) || 0), 0)
	  ), [getLessonHours, schedulableLessons]);
	  const placedProgramHours = useMemo(() => {
	    const activeSlotKeys = new Set(
	      activeDays.flatMap(day => (
	        periods
	          .filter(period => period < day.periodCount)
	          .map(period => slotKey(day.dayIndex, period))
	      ))
	    );
	    return Object.values(timetableResult?.assignments || {}).reduce((total, slots) => (
	      total + Object.entries(slots || {}).filter(([key, cell]) => cell && activeSlotKeys.has(key)).length
	    ), 0);
	  }, [activeDays, periods, timetableResult]);
	  const remainingProgramHours = Math.max(0, totalProgramHours - placedProgramHours);
	  const displayedTotalProgramHours = Number(programStats?.totalHours ?? totalProgramHours);
	  const displayedPlacedProgramHours = Number(programStats?.placedHours ?? placedProgramHours);
	  const displayedRemainingProgramHours = Number(programStats?.remainingHours ?? remainingProgramHours);
	  const openPlacementEditor = (lesson) => {
	    const subjectId = String(valueOf(lesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	    const classId = String(valueOf(lesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], resolvedSelectedId));
	    if (!classId || !subjectId) return toast.error('Ders ve sınıf bilgisi bulunamadı');
	    const classSubject = classSubjectMap[`${classId}_${subjectId}`] || {};
	    const subject = subjectMap[subjectId] || {};
	    const currentFormat = classSubject.distributionFormat || subject.distributionFormat || String(getLessonHours(lesson) || 1);
	    setPlacementEditor({ classId, subjectId, distributionFormat: currentFormat });
	    setLessonMenu(null);
	  };
	  const countPlacedForAssignment = (assignment) => {
	    const targetTeacherId = String(valueOf(assignment, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	    const targetClassId = String(valueOf(assignment, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''));
	    const targetSubjectId = String(valueOf(assignment, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	    return Object.entries(timetableResult?.assignments || {}).reduce((total, [placedClassId, slots]) => {
	      if (String(placedClassId) !== targetClassId) return total;
	      return total + Object.values(slots || {}).filter((cell) => {
	        if (!targetTeacherId) return String(cell.subjectId) === targetSubjectId;
	        const teacherIds = asArray(cell.teacherIds).map(String);
	        if (cell.teacherId) teacherIds.push(String(cell.teacherId));
	        return String(cell.subjectId) === targetSubjectId && teacherIds.includes(targetTeacherId);
	      }).length;
	    }, 0);
	  };
	  const getDistributionParts = (assignment, remainingHours) => {
	    const subjectId = String(valueOf(assignment, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	    const classId = String(valueOf(assignment, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''));
	    const classSubject = classSubjectMap[`${classId}_${subjectId}`] || {};
	    const subject = subjectMap[subjectId] || {};
	    const rawFormat = classSubject.distributionFormat || subject.distributionFormat || String(remainingHours || 1);
	    const cleanFormat = String(rawFormat || '').replace(/\s+/g, '');
	    const parsed = /^\d+(\+\d+)*$/.test(cleanFormat) ? cleanFormat.split('+').map(Number).filter(Boolean) : [];
	    const sourceParts = parsed.length > 0 ? parsed : Array.from({ length: remainingHours }, () => 1);
	    const parts = [];
	    let left = remainingHours;
	    sourceParts.forEach((part) => {
	      if (left <= 0) return;
	      const next = Math.min(part, left);
	      if (next > 0) parts.push(next);
	      left -= next;
	    });
	    while (left > 0) {
	      parts.push(1);
	      left -= 1;
	    }
	    return parts;
	  };
	  const canPlaceBlock = (classId, teacherId, dayIndex, startPeriod, blockLength, closedSlotMap = classClosedSlots) => {
	    const day = activeDays.find(item => item.dayIndex === dayIndex);
	    if (!day || startPeriod + blockLength > day.periodCount) return false;
	    for (let offset = 0; offset < blockLength; offset += 1) {
	      const key = slotKey(dayIndex, startPeriod + offset);
	      if (isClosedSlotInMap(closedSlotMap, classId, key)) return false;
	      if (!isClassSlotAllowed(classId, dayIndex, startPeriod + offset)) return false;
	      if (timetableResult?.assignments?.[String(classId)]?.[key]) return false;
	      if (!teacherId) continue;
	      if (isTeacherUnavailable(teacherMap, teacherId, key)) return false;
	      const teacherBusy = Object.entries(timetableResult?.assignments || {}).some(([, slots]) => {
	        const cell = slots?.[key];
	        if (!cell) return false;
	        const teacherIds = asArray(cell.teacherIds).map(String);
	        if (cell.teacherId) teacherIds.push(String(cell.teacherId));
	        return teacherIds.includes(String(teacherId));
	      });
	      if (teacherBusy) return false;
	    }
	    return true;
	  };
	  const createAutomaticPlacements = (targetTeacherIds = null, closedSlotMap = classClosedSlots) => {
	    const targetSet = targetTeacherIds ? new Set(targetTeacherIds.map(String)) : null;
	    const blocked = new Set();
	    const teacherBusy = {};
	    const teacherDayHours = {};
	    Object.entries(timetableResult?.assignments || {}).forEach(([classId, slots]) => {
	      Object.entries(slots || {}).forEach(([key, cell]) => {
	        blocked.add(`${classId}_${key}`);
	        const dayIndex = Number(String(key).split('_')[0]);
	        const teacherIds = asArray(cell.teacherIds).map(String);
	        if (cell.teacherId) teacherIds.push(String(cell.teacherId));
	        teacherIds.forEach((teacherId) => {
	          if (!teacherBusy[teacherId]) teacherBusy[teacherId] = new Set();
	          if (!teacherDayHours[teacherId]) teacherDayHours[teacherId] = {};
	          teacherBusy[teacherId].add(key);
	          teacherDayHours[teacherId][dayIndex] = (teacherDayHours[teacherId][dayIndex] || 0) + 1;
	        });
	      });
	    });

	    const placements = [];
	    const orderedAssignments = [...schedulableLessons].sort((a, b) => {
	      const aTeacher = String(valueOf(a, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	      const bTeacher = String(valueOf(b, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	      if (aTeacher !== bTeacher) return aTeacher.localeCompare(bTeacher, 'tr');
	      return getLessonHours(b) - getLessonHours(a);
	    });

	    orderedAssignments.forEach((assignment) => {
	      const teacherId = String(valueOf(assignment, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	      if (targetSet && !targetSet.has(teacherId)) return;
	      const classId = String(valueOf(assignment, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''));
	      const subjectId = String(valueOf(assignment, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	      const totalHours = getLessonHours(assignment);
	      const remainingHours = Math.max(0, totalHours - countPlacedForAssignment(assignment));
	      if (!classId || !subjectId || remainingHours <= 0) return;
	      const parts = getDistributionParts(assignment, remainingHours);
	      if (teacherId && !teacherBusy[teacherId]) teacherBusy[teacherId] = new Set();
	      if (teacherId && !teacherDayHours[teacherId]) teacherDayHours[teacherId] = {};

	      parts.forEach((blockLength) => {
	        let placed = false;
	        for (const day of activeDays) {
	          if (placed) break;
	          if (teacherId && (teacherDayHours[teacherId][day.dayIndex] || 0) + blockLength > Number(autoSettings.maxDailyTeacherHours || 18)) continue;
	          for (const period of periods) {
	            if (period + blockLength > day.periodCount) continue;
	            let available = canPlaceBlock(classId, teacherId, day.dayIndex, period, blockLength, closedSlotMap);
	            for (let offset = 0; offset < blockLength; offset += 1) {
	              const key = slotKey(day.dayIndex, period + offset);
	              if (
	                isClosedSlotInMap(closedSlotMap, classId, key) ||
	                !isClassSlotAllowed(classId, day.dayIndex, period + offset) ||
	                blocked.has(`${classId}_${key}`) ||
	                (teacherId && isTeacherUnavailable(teacherMap, teacherId, key)) ||
	                (teacherId && teacherBusy[teacherId].has(key))
	              ) available = false;
	            }
	            if (!available) continue;
	            for (let offset = 0; offset < blockLength; offset += 1) {
	              const key = slotKey(day.dayIndex, period + offset);
	              blocked.add(`${classId}_${key}`);
	              if (teacherId) {
	                teacherBusy[teacherId].add(key);
	                teacherDayHours[teacherId][day.dayIndex] = (teacherDayHours[teacherId][day.dayIndex] || 0) + 1;
	              }
	              placements.push({ teacherId, classId, key, subjectId });
	            }
	            placed = true;
	            break;
	          }
	        }
	      });
	    });
	    return placements;
	  };
	  const applyPlacements = (placements, successMessage) => {
	    if (placements.length === 0) return toast.error('Yerleştirilecek uygun boş saat bulunamadı');
	    placements.forEach(({ teacherId, classId, key, subjectId }) => {
	      updateTimetableCell(classId, key, {
	        teacherId: teacherId || null,
	        teacherIds: teacherId ? [teacherId] : [],
	        subjectId,
	        teacherless: !teacherId,
	        isFixed: false,
	      });
	    });
	    setSelectedPendingLesson(null);
	    setAvailabilityMap({});
	    toast.success(successMessage || `${placements.length} saat ders yerleştirildi`);
	  };
	  const persistClosedSlots = async (classId, nextSet) => {
	    if (!classId) return;
	    setSavingClosedSlots(true);
	    try {
	      const slots = Array.from(nextSet);
	      await axios.put(`${API}/sinif-kapali-slotlar`, { classId: String(classId), slots });
	      setClassClosedSlots(prev => ({ ...prev, [String(classId)]: new Set(slots) }));
	      toast.success('Kapalı saatler kaydedildi');
	    } catch (error) {
	      toast.error(scheduleErrorMessage(error, 'Kapalı saatler kaydedilemedi'));
	    } finally {
	      setSavingClosedSlots(false);
	    }
	  };
	  const toggleClosedSlot = (dayIndex, period) => {
	    if (!selectedClassId) return toast.error('Sınıf seçin');
	    const key = slotKey(dayIndex, period);
	    const next = new Set(selectedClassClosedSet);
	    if (next.has(key)) next.delete(key);
	    else {
	      if (timetableResult?.assignments?.[String(selectedClassId)]?.[key]) return toast.error('Dolu hücre kapatılamaz');
	      next.add(key);
	    }
	    persistClosedSlots(selectedClassId, next);
	  };
	  const toggleClosedDay = (dayIndex) => {
	    if (!selectedClassId) return toast.error('Sınıf seçin');
	    const day = activeDays.find(item => item.dayIndex === dayIndex);
	    if (!day) return;
	    const dayKeys = periods.filter(period => period < day.periodCount).map(period => slotKey(dayIndex, period));
	    const allClosed = dayKeys.every(key => selectedClassClosedSet.has(key));
	    const next = new Set(selectedClassClosedSet);
	    if (allClosed) {
	      dayKeys.forEach(key => next.delete(key));
	    } else {
	      const hasFilled = dayKeys.some(key => timetableResult?.assignments?.[String(selectedClassId)]?.[key]);
	      if (hasFilled) return toast.error('Ders bulunan gün tamamen kapatılamaz');
	      dayKeys.forEach(key => next.add(key));
	    }
	    persistClosedSlots(selectedClassId, next);
	  };
	  const openManualModal = () => {
	    const classId = String(selectedClassId || classes[0]?.id || '');
	    const subjectOptions = getSubjectOptionsForClass(classId);
	    const subjectId = String(subjectOptions[0]?.id || '');
	    setManualForm({
	      classId,
	      subjectId,
	      teacherId: '',
	      day: activeDays[0]?.dayIndex || 0,
	      period: 0,
	      blockLength: 1,
	    });
	    setManualModalOpen(true);
	  };
	  const openManualModalForCell = (day, period) => {
	    const classId = String(viewMode === 'class' ? resolvedSelectedId : selectedClassId);
	    const key = slotKey(day, period);
	    if (!classId) return toast.error('Sınıf seçin');
	    if (isClassSlotClosed(classId, key)) return toast.error('Kapalı saate ders atanamaz');
	    if (!isClassSlotAllowed(classId, day, period)) return toast.error('Bu sınıf seçilen saatte ders yapamaz');
	    const subjectOptions = getSubjectOptionsForClass(classId);
	    const subjectId = String(subjectOptions[0]?.id || '');
	    setSelectedCell({ day, period });
	    setManualForm({
	      classId,
	      subjectId,
	      teacherId: '',
	      day,
	      period,
	      blockLength: 1,
	    });
	    setManualModalOpen(true);
	  };
	  const openManualModalForTeacherlessLesson = (day, period, lesson) => {
	    const classId = String(lesson?.classId || (viewMode === 'class' ? resolvedSelectedId : selectedClassId));
	    const subjectId = String(lesson?.cell?.subjectId || '');
	    const teacherOptions = getTeacherOptionsForLesson(classId, subjectId);
	    setSelectedCell({ day, period });
	    setManualForm({
	      classId,
	      subjectId,
	      teacherId: String(teacherOptions[0]?.id || ''),
	      day,
	      period,
	      blockLength: 1,
	    });
	    setManualModalOpen(true);
	  };
	  const saveManualLesson = async () => {
	    const classId = String(manualForm.classId || '');
	    const subjectId = String(manualForm.subjectId || '');
	    const teacherId = String(manualForm.teacherId || '');
	    const day = Number(manualForm.day);
	    const period = Number(manualForm.period);
	    const blockLength = Math.max(1, Number(manualForm.blockLength || 1));
	    const firstKey = slotKey(day, period);
	    if (!classId || !subjectId) return toast.error('Sınıf ve ders seçin');
	    for (let offset = 0; offset < blockLength; offset += 1) {
	      const key = slotKey(day, period + offset);
	      if (isClassSlotClosed(classId, key)) return toast.error('Kapalı saate ders atanamaz');
	      if (!isClassSlotAllowed(classId, day, period + offset)) return toast.error('Bu sınıf seçilen saatte ders yapamaz');
	      if (teacherId && isTeacherUnavailable(teacherMap, teacherId, key)) return toast.error('Öğretmen bu saatte kapalı');
	    }
	    try {
	      const closedSlotMap = {
	        ...classClosedSlots,
	        [classId]: await loadClassClosedSlots(classId),
	      };
	      const response = await axios.post(`${API}/manuel-ders-ata`, {
	        classId,
	        subjectId,
	        teacherId: teacherId || null,
	        teacherIds: teacherId ? [teacherId] : [],
	        teacherless: !teacherId,
	        slotKey: firstKey,
	        blockLength,
	        isFixed: true,
	        timetableResult,
	        dailyConfigs: settings,
	        scheduleRules: rules,
	        classDailyLimits: dailyLimits,
	      });
	      const payload = getPayload(response);
	      const closedSanitized = sanitizeTimetableClosedSlots(extractTimetable(payload), closedSlotMap);
	      const limitSanitized = sanitizeTimetableClassDailyLimits(closedSanitized.timetableResult, dailyLimits, settings);
	      setTimetableResult(limitSanitized.timetableResult);
	      if (closedSanitized.removed.length > 0) toast.error(`${closedSanitized.removed.length} ders kapalı saatlerden çıkarıldı`);
	      if (limitSanitized.removed.length > 0) toast.error(`${limitSanitized.removed.length} ders günlük sınır dışından çıkarıldı`);
	      setManualModalOpen(false);
	      setSelectedPendingLesson(null);
	      setAvailabilityMap({});
	      toast.success('Manuel ders atandı');
	    } catch (error) {
	      toast.error(scheduleErrorMessage(error, error.response?.status === 409 ? 'Ders çakışması oluştu' : 'Manuel ders atanamadı'));
	    }
	  };
	  const distributeSelectedTeacher = async () => {
	    if (viewMode !== 'teacher') {
	      setViewMode('teacher');
	      return toast('Önce öğretmen seçin');
	    }
	    const teacherId = String(resolvedSelectedId || '');
	    if (!teacherId) return toast.error('Öğretmen seçin');
	    const teacherAssignments = assignments.filter(row => String(valueOf(row, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '')) === teacherId);
	    if (teacherAssignments.length === 0) return toast.error('Seçili öğretmene atanmış ders yok');

	    const closedSlotMap = await loadClosedSlotsForClasses(getSchedulableClassIds(teacherAssignments));
	    const placements = createAutomaticPlacements([teacherId], closedSlotMap);
	    applyPlacements(placements, `${placements.length} saat ders yerleştirildi`);
	  };
	  const generateAutomaticProgram = () => {
	    if (schedulableLessons.length === 0) return toast.error('Önce sınıflara okutulacak dersleri girin');
	    return handleAutoGenerate(autoSettings, totalProgramHours);
	  };
	  const openAutoSettings = () => {
	    setLocalDays(settings);
	    setAutoSettingsOpen(true);
	  };
	  const updateLocalDay = (dayIndex, patch) => {
	    const updater = prev => prev.map(day => (
	      Number(day.dayIndex) === Number(dayIndex)
	        ? { ...day, ...patch }
	        : day
	    ));
	    setLocalDays(updater);
	    setSettings(updater);
	  };
	  const changeDayPeriodCount = (dayIndex, delta) => {
	    const updater = prev => prev.map(day => {
	      if (Number(day.dayIndex) !== Number(dayIndex)) return day;
	      const periodCount = Math.max(1, Math.min(18, Number(day.periodCount || 1) + delta));
	      return { ...day, periodCount };
	    });
	    setLocalDays(updater);
	    setSettings(updater);
	  };
	  const saveDayPeriodCounts = async () => {
	    const nextDays = localDays.map(day => ({
	      ...day,
	      periodCount: Math.max(1, Math.min(18, Number(day.periodCount || 1))),
	      isActive: Boolean(day.isActive),
	    }));
	    try {
	      await axios.put(`${API}/ayarlar`, {
	        dailyConfigs: nextDays,
	        daily_configs: nextDays,
	        autoSettings,
	        otomatikAyarlar: autoSettings,
	      });
	      setSettings(nextDays);
	      toast.success('Ders saatleri kaydedildi');
	    } catch (error) {
	      toast.error(error.response?.data?.message || 'Ders saatleri kaydedilemedi');
	    }
	  };
	  const saveAutoSettings = async () => {
	    const nextDays = localDays.map(day => ({
	      ...day,
	      periodCount: Math.max(1, Math.min(18, Number(day.periodCount || 1))),
	      isActive: Boolean(day.isActive),
	    }));
	    try {
	      await axios.put(`${API}/ayarlar`, {
	        dailyConfigs: nextDays,
	        daily_configs: nextDays,
	        autoSettings,
	        otomatikAyarlar: autoSettings,
	      });
	      setSettings(nextDays);
	      setAutoSettingsOpen(false);
	      toast.success('Ayarlar kaydedildi');
	    } catch (error) {
	      toast.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
	    }
	  };
	  const getPrintEntityName = (mode, id) => {
	    if (mode === 'teacher') return entityName(teacherMap, id);
	    if (mode === 'class') return entityName(classMap, id);
	    return entityName(subjectMap, id);
	  };
	  const getPrintTitle = (mode = viewMode, id = resolvedSelectedId, scope = 'selected') => {
	    if (mode === 'teacher') return scope === 'all' ? 'Tüm Öğretmenlerin Ders Programı' : `${getPrintEntityName(mode, id)} Öğretmen Ders Programı`;
	    if (mode === 'class') return scope === 'all' ? 'Tüm Sınıfların Ders Programı' : `${getPrintEntityName(mode, id)} Sınıf Ders Programı`;
	    return `${getPrintEntityName(mode, id)} Ders Programı`;
	  };
	  const getCellForPrintSlot = (mode, entityId, day, period) => {
	    const key = slotKey(day, period);
	    if (mode === 'class') {
	      const cell = timetableResult.assignments?.[String(entityId)]?.[key];
	      return cell ? { classId: String(entityId), key, cell } : null;
	    }
	    for (const [classId, slots] of Object.entries(timetableResult.assignments || {})) {
	      const cell = slots?.[key];
	      if (!cell) continue;
	      const teacherIds = asArray(cell.teacherIds).map(String);
	      if (cell.teacherId) teacherIds.push(String(cell.teacherId));
	      if (mode === 'teacher' && teacherIds.includes(String(entityId))) return { classId, key, cell };
	      if (mode === 'subject' && String(cell.subjectId) === String(entityId)) return { classId, key, cell };
	    }
	    return null;
	  };
	  const getPrintEntities = (mode, scope) => {
	    if (scope === 'selected') return [{ id: resolvedSelectedId, name: getPrintEntityName(mode, resolvedSelectedId) }].filter(item => item.id);
	    const source = mode === 'teacher' ? teachers : mode === 'class' ? classes : subjects;
	    return source.map(item => ({ id: item.id, name: item.name }));
	  };
	  const getPrintableRows = (mode = viewMode, entityId = resolvedSelectedId) => activeDays.map(day => ({
	    day,
	    cells: periods.map((period) => {
	      if (period >= day.periodCount) return null;
	      const lesson = getCellForPrintSlot(mode, entityId, day.dayIndex, period);
	      if (!lesson) return null;
	      const subjectId = lesson.cell.subjectId;
	      return {
	        className: entityName(classMap, lesson.classId, ''),
	        subjectName: entityName(subjectMap, subjectId, ''),
	        subjectShort: entityShort(subjectMap, subjectId, ''),
	        teacherName: lessonTeacherLabel(lesson.cell, teacherMap),
	      };
	    }),
	  }));
	  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
	    '&': '&amp;',
	    '<': '&lt;',
	    '>': '&gt;',
	    '"': '&quot;',
	    "'": '&#039;',
	  }[char]));
	  const buildPrintTable = (mode, entity) => {
	    const rows = getPrintableRows(mode, entity.id);
	    const title = getPrintTitle(mode, entity.id, 'selected');
	    const periodHeaders = periods.map((period) => {
	      const slot = getPeriodTime(period);
	      const hasTime = Boolean(slot.start && slot.end && !(slot.start === '00:00' && slot.end === '00:00'));
	      return `<th><strong class="period-number">${escapeHtml(period + 1)}</strong>${hasTime ? `<span class="period-time">${escapeHtml(`${slot.start} - ${slot.end}`)}</span>` : ''}</th>`;
	    }).join('');
	    const tableRows = rows.map(({ day, cells }) => `
	      <tr>
	        <th>${escapeHtml(day.name)}</th>
	        ${cells.map((cell) => `
	          <td>
	            ${cell ? `
	              <b>${escapeHtml(cell.subjectName)}</b>
	              <span>${escapeHtml(mode === 'teacher' ? cell.className : mode === 'class' ? cell.teacherName : cell.className)}</span>
	            ` : ''}
	          </td>
	        `).join('')}
	      </tr>
	    `).join('');
	    return `
	      <section class="print-page">
	        <img class="print-watermark" src="${kareKursBulvarLogo}" alt="" />
	        <header class="print-header">
	          <img class="print-logo" src="${kareKursBulvarLogo}" alt="Kare Kurs" />
	          <div>
	            <h1>${escapeHtml(title)}</h1>
	            <p>ASDER SİSTEM AKADEMİK YÖNETİM</p>
	          </div>
	        </header>
	        <table>
	          <thead>
	            <tr><th>Gün / Saat</th>${periodHeaders}</tr>
	          </thead>
	          <tbody>${tableRows}</tbody>
	        </table>
	      </section>
	    `;
	  };
	  const buildPrintHtml = ({ mode = viewMode, scope = 'selected' } = {}) => {
	    if (!['teacher', 'class', 'subject'].includes(mode)) mode = 'class';
	    const entities = getPrintEntities(mode, scope);
	    if (entities.length === 0) return '';
	    const isVertical = printOptions.orientation === 'vertical';
	    const title = getPrintTitle(mode, resolvedSelectedId, scope);
	    return `
	      <!doctype html>
	      <html>
	        <head>
	          <meta charset="utf-8" />
	          <title>${escapeHtml(title)}</title>
	          <style>
	            @page { size: ${isVertical ? 'A4 portrait' : 'A4 landscape'}; margin: 10mm; }
	            * { box-sizing:border-box; }
	            body { margin:0; font-family: Tahoma, Arial, sans-serif; color:#111; background:#fff; }
	            .print-page { position:relative; min-height:${isVertical ? '277mm' : '190mm'}; page-break-after:always; padding:0; overflow:hidden; }
	            .print-page:last-child { page-break-after:auto; }
	            .print-watermark { position:absolute; inset:0; margin:auto; width:58%; max-width:520px; opacity:.075; z-index:0; pointer-events:none; }
	            .print-header { position:relative; z-index:1; display:flex; align-items:center; justify-content:center; gap:14px; margin:0 0 10px; min-height:72px; text-align:left; }
	            .print-logo { width:64px; height:64px; object-fit:contain; }
	            h1 { font-size:18px; text-align:left; margin:0 0 5px; }
	            p { margin:0; font-size:11px; font-weight:800; letter-spacing:.08em; color:#475569; }
	            table { position:relative; z-index:1; width:100%; border-collapse:collapse; table-layout:fixed; background:rgba(255,255,255,.88); }
	            th, td { border:1px solid #333; padding:6px; height:${isVertical ? '58px' : '46px'}; vertical-align:top; font-size:12px; }
	            thead th { background:#b7d7f0; }
	            .period-number, .period-time { display:block; text-align:center; }
	            .period-time { margin-top:3px; font-size:9px; font-weight:600; white-space:nowrap; }
	            tbody th { background:#b7d7f0; width:95px; text-align:left; }
	            td b, td span { display:block; line-height:1.2; }
	            td b { font-weight:800; }
	            @media print {
	              body { print-color-adjust:exact; -webkit-print-color-adjust:exact; }
	            }
	          </style>
	        </head>
	        <body>
	          ${entities.map(entity => buildPrintTable(mode, entity)).join('')}
	        </body>
	      </html>
	    `;
	  };
	  const openPrintPreview = (mode = viewMode, scope = 'selected') => {
	    if (mode === 'teacher' && (scope === 'all' ? teachers.length === 0 : !resolvedSelectedId)) return toast.error('Yazdırılacak öğretmen bulunamadı');
	    if (mode === 'class' && (scope === 'all' ? classes.length === 0 : !resolvedSelectedId)) return toast.error('Yazdırılacak sınıf bulunamadı');
	    const printWindow = window.open('', '_blank', 'width=1100,height=760');
	    if (!printWindow) return toast.error('Baskı önizleme penceresi açılamadı');
	    printWindow.document.write(buildPrintHtml({ mode, scope }));
	    printWindow.document.close();
	    printWindow.focus();
	    setTimeout(() => printWindow.print(), 250);
	  };
	  const exportPrintCsv = () => {
	    const rows = getPrintableRows();
	    const lines = [
	      ['Gün / Saat', ...periods.map((period) => {
	        const slot = getPeriodTime(period);
	        const hasTime = Boolean(slot.start && slot.end && !(slot.start === '00:00' && slot.end === '00:00'));
	        return hasTime ? `${period + 1} (${slot.start} - ${slot.end})` : String(period + 1);
	      })],
	      ...rows.map(({ day, cells }) => [
	        day.name,
	        ...cells.map(cell => cell ? `${cell.className} ${cell.subjectShort} ${cell.teacherName}`.trim() : ''),
	      ]),
	    ];
	    const csv = lines.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(';')).join('\n');
	    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
	    const url = URL.createObjectURL(blob);
	    const link = document.createElement('a');
	    link.href = url;
	    link.download = `${getPrintTitle().replace(/[^\wğüşöçıİĞÜŞÖÇ-]+/gi, '_')}.csv`;
	    document.body.appendChild(link);
	    link.click();
	    document.body.removeChild(link);
	    URL.revokeObjectURL(url);
	  };
	  const savePlacementFormat = async () => {
	    if (!placementEditor) return;
	    const distributionFormat = String(placementEditor.distributionFormat || '').replace(/\s+/g, '');
	    const total = sumDistributionFormat(distributionFormat);
	    if (!total) return toast.error('Yerleşme biçimi 2+1 gibi olmalı');
	    const payload = {
	      classId: placementEditor.classId,
	      subjectId: placementEditor.subjectId,
	      sinifId: placementEditor.classId,
	      dersId: placementEditor.subjectId,
	      weeklyHours: total,
	      haftalikSaat: total,
	      distributionFormat,
	      distribution_format: distributionFormat,
	      placementFormat: distributionFormat,
	      placement_format: distributionFormat,
	      yerlesimBicimi: distributionFormat,
	      yerlesim_bicimi: distributionFormat,
	      yerlesmeBicimi: distributionFormat,
	      yerlesme_bicimi: distributionFormat,
	      yerBicimi: distributionFormat,
	      yer_bicimi: distributionFormat,
	    };
	    try {
	      await axios.put(`${API}/siniflar/${placementEditor.classId}/dersler/${placementEditor.subjectId}`, payload);
	      setClassSubjects(prev => prev.map(item => (
	        String(item.classId) === String(placementEditor.classId) && String(item.subjectId) === String(placementEditor.subjectId)
	          ? { ...item, weeklyHours: total, distributionFormat }
	          : item
	      )));
	      setPlacementEditor(null);
	      toast.success('Yerleşme biçimi güncellendi');
	    } catch (error) {
	      toast.error(error.response?.data?.message || 'Yerleşme biçimi kaydedilemedi');
	    }
	  };
	  return (
	    <WindowModal title="Ders Programları" width={1220} onClose={onClose}>
	      <div className="prag-program-tabs">
	        <button className={viewMode === 'teacher' ? 'active' : ''} onClick={() => setViewMode('teacher')}>Öğretmenlerin Ders Programı</button>
	        <button className={viewMode === 'class' ? 'active' : ''} onClick={() => setViewMode('class')}>Sınıfların Ders Programı</button>
	        <button className={viewMode === 'subject' ? 'active' : ''} onClick={() => setViewMode('subject')}>Derslerin Ders Programı</button>
	      </div>
	      {warnings.length > 0 && (
	        <div className="hp-warning-strip hp-program-warnings">
	          {warnings.map((item, index) => (
	            <span key={index}>{item.message || item.text || String(item)}</span>
	          ))}
	        </div>
	      )}
	      <div className="prag-program-main full">
	        <div className="hp-grid-scroll">
	          <table className="hp-schedule-grid pragmatic-schedule-grid">
	            <thead>
	              <tr>
	                <th className="sticky-day">Gün / Saat</th>
	                {periods.map((period) => {
	                  const slot = getPeriodTime(period);
	                  const hasTime = Boolean(slot.start && slot.end && !(slot.start === '00:00' && slot.end === '00:00'));
	                  return (
	                    <th key={period}>
	                      <button type="button" className="hp-period-time-button" onClick={() => openPeriodTimeEditor(period)}>
	                        <strong>{period + 1}</strong>
	                        <span>{hasTime ? `${slot.start} - ${slot.end}` : 'Saat gir'}</span>
	                      </button>
	                    </th>
	                  );
	                })}
	              </tr>
	            </thead>
	            <tbody>
	              {gridDays.map((day) => (
	                <tr key={day.dayIndex}>
	                  <th className="sticky-day">{day.name}</th>
	                  {periods.map((period) => {
	                    const inactive = !day.isActive || period >= day.periodCount;
	                    const lesson = inactive ? null : getCellForSlot(day.dayIndex, period);
	                    const subject = lesson ? subjectMap[String(lesson.cell.subjectId)] : null;
		                    const teacherLabel = lessonTeacherLabel(lesson?.cell, teacherMap);
	                    const teacherless = Boolean(lesson?.cell?.teacherless || (lesson && !hasLessonTeacher(lesson.cell)));
	                    const key = slotKey(day.dayIndex, period);
	                    const availability = availabilityMap[key];
	                    const closedClassId = lesson?.classId || (viewMode === 'class' ? resolvedSelectedId : (viewMode === 'teacher' ? '' : selectedClassId));
	                    const classClosed = !inactive && closedClassId && isClassSlotClosed(closedClassId, key);
	                    const teacherClosed = !inactive && viewMode === 'teacher' && isTeacherUnavailable(teacherMap, resolvedSelectedId, key);
	                    const isDark = !inactive && Boolean(lesson) && (period + day.dayIndex) % 3 !== 0;
	                    const toneClass = inactive ? '' : isDark ? 'dark' : 'light';
	                    return (
	                      <td
	                        key={period}
	                        className={`${inactive ? 'inactive' : ''} ${lesson ? 'filled' : ''} ${toneClass} ${classClosed ? 'class-closed' : ''} ${teacherClosed ? 'teacher-closed' : ''} ${availability ? `availability-${availability.status}` : ''}`}
	                        title={teacherClosed ? 'Öğretmen bu saatte kapalı' : (classClosed ? 'Bu sınıf için kapalı saat' : (availability?.message || ''))}
	                        onClick={() => {
	                          if (inactive) return;
	                          if (teacherClosed) {
	                            toast.error('Öğretmen bu saatte kapalı');
	                            return;
	                          }
	                          if (!lesson && !classClosed) {
	                            openManualModalForCell(day.dayIndex, period);
	                            return;
	                          }
	                          if (lesson && teacherless) {
	                            openManualModalForTeacherlessLesson(day.dayIndex, period, lesson);
	                            return;
	                          }
	                          setSelectedCell({ day: day.dayIndex, period });
	                        }}
	                        onDoubleClick={() => {
	                          if (inactive || classClosed || teacherClosed) {
	                            if (teacherClosed) toast.error('Öğretmen bu saatte kapalı');
	                            if (classClosed) toast.error('Kapalı saate ders atanamaz');
	                            return;
	                          }
	                          if (selectedPendingLesson) handlePlacePendingLesson(day.dayIndex, period);
	                          else handleCellDoubleClick(day.dayIndex, period);
	                        }}
                        onDragEnter={(e) => {
                          if (inactive) return;
                          e.preventDefault();
                        }}
                        onDragOver={(e) => {
                          if (inactive) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = teacherClosed ? 'none' : 'move';
                        }}
                        onDrop={(e) => {
                          if (inactive) return;
                          if (teacherClosed) {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragSource(null);
                            toast.error('Öğretmen bu saatte kapalı');
                            return;
                          }
                          handleDrop(e, day.dayIndex, period);
                        }}
	                      >
	                        {!inactive && teacherClosed && <span className="hp-closed-label">KAPALI</span>}
	                        {!inactive && !teacherClosed && classClosed && !lesson && <span className="hp-closed-label">Kapalı</span>}
	                        {!inactive && !teacherClosed && lesson && (
	                          <div
	                            className={`hp-lesson pragmatic-lesson${teacherless ? ' teacherless' : ''}`}
	                            draggable={!lesson.cell.isFixed && !teacherClosed}
	                            onDragStart={(e) => {
	                              e.dataTransfer.effectAllowed = 'move';
	                              e.dataTransfer.setData('text/plain', lesson.key);
	                              setDragSource(lesson);
	                            }}
	                            onDragEnd={() => setDragSource(null)}
	                          >
	                            <b>{subject?.shortName || lesson.cell.subjectName || lesson.cell.subjectId}</b>
                            <span className="teacher-full-name">{teacherless ? 'Öğretmen atanmadı' : teacherLabel}</span>
	                            {(!teacherless || viewMode !== 'class') && <small>{entityName(classMap, lesson.classId)}</small>}
	                            {lesson.cell.isFixed && <FaLock className="lesson-lock" />}
	                          </div>
	                        )}
	                      </td>
	                    );
	                  })}
	                </tr>
	              ))}
	            </tbody>
	          </table>
	        </div>
	        <div className="prag-program-bottom wide">
	          <div className="prag-program-list">
	            <DenseTable headers={['No', nameHeader, detailHeader, 'T', 'K']} minWidth={420}>
	              {visibleList.map((item, index) => {
	                const isCurrent = String(item.id) === String(resolvedSelectedId);
	                const itemTotal = viewMode === 'teacher'
	                  ? Number(teacherTotals[String(item.id)] || 0)
	                  : isCurrent ? selectedEntityTotal : Number(item.weeklyHours || 0);
	                const itemPlaced = isCurrent ? placedVisible : 0;
	                return (
	                  <tr
	                    key={item.id}
	                    className={isCurrent ? 'selected' : ''}
	                    onClick={() => setSelectedId(String(item.id))}
	                  >
	                    <td>{index + 1}</td>
	                    <td>{item.name}</td>
	                    <td>{viewMode === 'teacher' ? item.branch : item.shortName || valueOf(item, ['area', 'alan'], '') || '-'}</td>
	                    <td>{itemTotal}</td>
	                    <td>{Math.max(0, itemTotal - itemPlaced)}</td>
	                  </tr>
	                );
	              })}
	            </DenseTable>
	          </div>
	          <DenseTable headers={['Sınıf', 'Dersin Adı', 'Kısa Adı', 'H', 'Derslik', 'K']} minWidth={620}>
	            {visibleLessonRows.map((lesson, index) => {
	              const subjectId = valueOf(lesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '');
	              const teacherId = valueOf(lesson, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '');
	              const classId = valueOf(lesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], resolvedSelectedId);
	              const lessonKey = valueOf(lesson, ['id', '_id'], `${classId}_${subjectId}_${teacherId}_${index}`);
	              const hours = getLessonHours(lesson);
	              const isSelected = selectedPendingLesson && String(valueOf(selectedPendingLesson, ['id', '_id'], '')) === String(valueOf(lesson, ['id', '_id'], '')) && String(subjectId) === String(valueOf(selectedPendingLesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	              return (
	                <tr
	                  key={lessonKey}
	                  className={isSelected ? 'selected' : ''}
	                  onClick={() => handlePendingLessonClick(lesson)}
	                  onContextMenu={(event) => {
	                    event.preventDefault();
	                    setLessonMenu({ x: event.clientX, y: event.clientY, lesson });
	                  }}
	                >
	                  <td>{entityName(classMap, classId)}</td>
	                  <td>{entityName(subjectMap, subjectId, valueOf(lesson, ['subjectName', 'dersAdi', 'name'], '-'))}</td>
	                  <td>{entityShort(subjectMap, subjectId, '-')}</td>
	                  <td>{hours || ''}</td>
	                  <td />
	                  <td>{hours || ''}</td>
	                </tr>
	              );
	            })}
	            {visibleLessonRows.length === 0 && (
	              <tr><td colSpan={6}>{viewMode === 'teacher' ? 'Seçili öğretmene atanmış ders yok.' : 'Bekleyen ders yok. Hücreyi boşa almak için program hücresine çift tıklayın.'}</td></tr>
	            )}
	          </DenseTable>
	          <div className="prag-program-side">
	            <div className="prag-period-panel">
	              <strong>Ders Saatleri</strong>
	              <div className="prag-period-list">
	                {localDays.map(day => (
	                  <div key={day.dayIndex} className={`prag-period-row${day.isActive ? '' : ' muted'}`}>
	                    <label>
	                      <input
	                        type="checkbox"
	                        checked={Boolean(day.isActive)}
	                        onChange={event => updateLocalDay(day.dayIndex, { isActive: event.target.checked })}
	                      />
	                      <span>{day.shortName || day.name}</span>
	                    </label>
	                    <button type="button" onClick={() => changeDayPeriodCount(day.dayIndex, -1)} disabled={!day.isActive}>-</button>
	                    <b>{day.periodCount}</b>
	                    <button type="button" onClick={() => changeDayPeriodCount(day.dayIndex, 1)} disabled={!day.isActive}>+</button>
	                  </div>
	                ))}
	              </div>
	              <button className="prag-period-save" type="button" onClick={saveDayPeriodCounts}>Saatleri Kaydet</button>
	            </div>
	            <div className="prag-closed-panel">
	              <strong>Kapalı Gün/Saatler</strong>
	              <select
	                value={selectedClassId}
	                onChange={event => {
	                  setViewMode('class');
	                  setSelectedId(event.target.value);
	                }}
	              >
	                {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
	              </select>
	              <div className="prag-closed-days">
	                {gridDays.map(day => {
	                  const dayKeys = periods.filter(period => period < day.periodCount).map(period => slotKey(day.dayIndex, period));
	                  const allClosed = dayKeys.length > 0 && dayKeys.every(key => selectedClassClosedSet.has(key));
	                  return (
	                    <div key={day.dayIndex} className="prag-closed-day">
	                      <button type="button" onClick={() => toggleClosedDay(day.dayIndex)} disabled={savingClosedSlots}>
	                        {allClosed ? 'Aç' : 'Kapat'} {day.shortName || day.name}
	                      </button>
	                      <div>
	                        {periods.filter(period => period < day.periodCount).map(period => {
	                          const key = slotKey(day.dayIndex, period);
	                          return (
	                            <label key={key} className={selectedClassClosedSet.has(key) ? 'closed' : ''}>
	                              <input
	                                type="checkbox"
	                                checked={selectedClassClosedSet.has(key)}
	                                disabled={savingClosedSlots}
	                                onChange={() => toggleClosedSlot(day.dayIndex, period)}
	                              />
	                              {period + 1}
	                            </label>
	                          );
	                        })}
	                      </div>
	                    </div>
	                  );
	                })}
	              </div>
	            </div>
	            <div className="prag-color-legend">
	              <div><b style={{ background:'#111827', color:'#fff' }}>1</b><span>Dr.Sa</span></div>
	              <div><b style={{ background:'#5ff06d' }}>2</b><span>Rh.Öğ</span></div>
	              <div><b style={{ background:'#ff57df' }}>3</b><span>Yön.</span></div>
	              <div><b style={{ background:'#b8ff6a' }}>4</b><span>Eg.</span></div>
	            </div>
	            <div className="prag-side-actions">
	              <button disabled>Geç</button>
	              <button disabled>Durdur</button>
	            </div>
	            <div className="prag-time-row"><span>00:00:00</span><span>0</span></div>
	            <div className="prag-counts">
	              <label>Toplam <strong>{displayedTotalProgramHours}</strong></label>
	              <label>Yerleşen <strong>{displayedPlacedProgramHours}</strong></label>
	              <label>Kalan <strong>{displayedRemainingProgramHours}</strong></label>
	            </div>
	          </div>
	        </div>
	        <div className="prag-selected-row">
	          <DenseTable headers={['Sınıf', 'Dersin Adı', 'H', 'Öğretmen', 'Derslik', 'K']} minWidth={660}>
	            <tr>
	              <td>{selectedCellData ? entityName(classMap, selectedCellData.classId) : ''}</td>
	              <td>{selectedCellData ? entityName(subjectMap, selectedCellData.cell.subjectId) : ''}</td>
	              <td>{selectedCellData ? subjectMap[String(selectedCellData.cell.subjectId)]?.weeklyHours || '' : ''}</td>
	              <td>{selectedCellData ? lessonTeacherLabel(selectedCellData.cell, teacherMap) : ''}</td>
	              <td />
	              <td>{selectedCellData ? subjectMap[String(selectedCellData.cell.subjectId)]?.weeklyHours || '' : ''}</td>
	            </tr>
	          </DenseTable>
	          <strong>SEÇİLİ HÜCRE</strong>
	        </div>
	      </div>
	      <div className="prag-program-actions">
	        <button type="button" onClick={() => setPrintOpen(true)}>▦ Yazdır</button>
	        <button type="button" onClick={openManualModal}>＋ Manuel Ata</button>
	        <button type="button" onClick={distributeSelectedTeacher}>🚶 Öğretmen</button>
	        <button type="button" onClick={generateAutomaticProgram} disabled={autoGenerating}>
	          {autoGenerating ? 'Oluşturuluyor…' : '▣ Otomat'}
	        </button>
	        <button>📋 Rapor</button>
	        <button>▣ Özel Ayarlar</button>
	        <button type="button" onClick={openAutoSettings}>🔧 Ayarlar</button>
	        <button>⚑ Sil</button>
	        <button disabled>◉ Düzenle</button>
	        <button onClick={onClose}>▥ Kapat</button>
	      </div>
	      <div className="prag-program-shortcuts">
	        <span>F3=Kapalılık Haritası</span>
	        <span>F8=Kapat/Aç</span>
	        <span>F9=Ders Dışı Ver/Sil</span>
	      </div>
	      {periodTimeEditor && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-period-time-window">
	            <div className="hp-window-titlebar">
	              <div><strong>{periodTimeEditor.period + 1}. Ders Saati</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setPeriodTimeEditor(null)}>x</button>
	            </div>
	            <div className="hp-form-card">
	              <Field label="Başlangıç">
	                <input
	                  type="time"
	                  value={periodTimeEditor.start}
	                  onChange={event => setPeriodTimeEditor(current => ({ ...current, start: event.target.value }))}
	                />
	              </Field>
	              <Field label="Bitiş">
	                <input
	                  type="time"
	                  value={periodTimeEditor.end}
	                  onChange={event => setPeriodTimeEditor(current => ({ ...current, end: event.target.value }))}
	                />
	              </Field>
	              <p className="hp-muted">Bu saat aralığı haftanın bütün günlerinde aynı ders sırasına uygulanır.</p>
	              <div className="hp-row-actions">
	                <button type="button" onClick={savePeriodTime} disabled={savingPeriodTime}>
	                  {savingPeriodTime ? 'Kaydediliyor…' : 'Kaydet'}
	                </button>
	                <button type="button" onClick={() => setPeriodTimeEditor(null)} disabled={savingPeriodTime}>İptal</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	      {lessonMenu && (
	        <div
	          className="prag-lesson-menu"
	          style={{ left: lessonMenu.x, top: lessonMenu.y }}
	          onMouseLeave={() => setLessonMenu(null)}
	        >
	          <button type="button" onClick={() => toast('Sınıfa bağlan özelliği için backend alanı bekleniyor')}>Sınıfa Bağlan</button>
	          <button type="button" onClick={() => toast('Dersliğe bağlan özelliği için backend alanı bekleniyor')}>Dersliğe Bağlan</button>
	          <button type="button" className="active" onClick={() => openPlacementEditor(lessonMenu.lesson)}>Yerleşme Biçimi</button>
	          <button type="button" onClick={() => toast('Özellikler penceresi ayrıca bağlanabilir')}>Özellikler</button>
	          <button type="button" onClick={() => toast('Derslik silme için önce program hücresinden derslik seçimi gerekir')}>Dersliği Sil</button>
	          <button type="button" onClick={() => handlePendingLessonClick(lessonMenu.lesson)}>Uygunları Göster</button>
	        </div>
	      )}
	      {manualModalOpen && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-manual-window">
	            <div className="hp-window-titlebar">
	              <div><strong>Manuel Ders Ata</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setManualModalOpen(false)}>x</button>
	            </div>
	            <div className="hp-form-card">
	              <Field label="Sınıf">
	                <select
	                  value={manualForm.classId}
	                  onChange={event => {
	                    const classId = event.target.value;
	                    const subjectOptions = getSubjectOptionsForClass(classId);
	                    const subjectId = String(subjectOptions[0]?.id || '');
	                    setManualForm({
	                      ...manualForm,
	                      classId,
	                      subjectId,
	                      teacherId: '',
	                    });
	                  }}
	                >
	                  {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
	                </select>
	              </Field>
	              <Field label="Ders">
	                <select
	                  value={manualForm.subjectId}
	                  onChange={event => {
	                    const subjectId = event.target.value;
	                    const teacherOptions = getTeacherOptionsForLesson(manualForm.classId, subjectId);
	                    setManualForm({
	                      ...manualForm,
	                      subjectId,
	                      teacherId: teacherOptions.some(item => String(item.id) === String(manualForm.teacherId)) ? manualForm.teacherId : '',
	                    });
	                  }}
	                >
	                  {manualSubjectOptions.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
	                </select>
	              </Field>
	              <Field label="Öğretmen">
	                <select value={manualForm.teacherId} onChange={event => setManualForm({ ...manualForm, teacherId: event.target.value })}>
	                  <option value="">Öğretmen atanmadı</option>
	                  {manualTeacherOptions.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
	                </select>
	              </Field>
	              <Field label="Gün">
	                <select
	                  value={manualForm.day}
	                  onChange={event => setManualForm({ ...manualForm, day: Number(event.target.value), period: 0 })}
	                >
	                  {activeDays.map(day => <option key={day.dayIndex} value={day.dayIndex}>{day.name}</option>)}
	                </select>
	              </Field>
	              <Field label="Saat">
	                <select value={manualForm.period} onChange={event => setManualForm({ ...manualForm, period: Number(event.target.value) })}>
	                  {periods
		                    .filter(period => period < (activeDays.find(day => Number(day.dayIndex) === Number(manualForm.day))?.periodCount || 0))
		                    .map(period => {
		                      const key = slotKey(manualForm.day, period);
		                      const closed = isClassSlotClosed(manualForm.classId, key);
		                      const disallowed = !isClassSlotAllowed(manualForm.classId, manualForm.day, period);
		                      return <option key={period} value={period} disabled={closed || disallowed}>{period + 1}{closed ? ' - Kapalı' : disallowed ? ' - Ders yapamaz' : ''}</option>;
		                    })}
	                </select>
	              </Field>
	              <Field label="Blok Uzunluğu">
	                <input
	                  type="number"
	                  min="1"
	                  max="6"
	                  value={manualForm.blockLength}
	                  onChange={event => setManualForm({ ...manualForm, blockLength: Number(event.target.value) })}
	                />
	              </Field>
	              <p className="hp-muted">Manuel atanan ders backend'e sabit ders olarak gönderilir.</p>
	              <div className="hp-row-actions">
	                <button type="button" onClick={saveManualLesson}>Ata</button>
	                <button type="button" onClick={() => setManualModalOpen(false)}>İptal</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	      {printOpen && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-print-window">
	            <div className="hp-window-titlebar">
	              <div><strong>Yazdır</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setPrintOpen(false)}>x</button>
	            </div>
	            <div className="hp-print-body">
	              <div className="hp-print-options">
	                <div className="hp-print-group">
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.type === 'el'}
	                      onChange={() => setPrintOptions({ ...printOptions, type: 'el' })}
	                    />
	                    <span>El Programı</span>
	                  </label>
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.type === 'carsaf'}
	                      onChange={() => setPrintOptions({ ...printOptions, type: 'carsaf' })}
	                    />
	                    <span>Çarşaf Liste</span>
	                  </label>
	                </div>
	                <div className="hp-print-group">
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.orientation === 'vertical'}
	                      onChange={() => setPrintOptions({ ...printOptions, orientation: 'vertical' })}
	                    />
	                    <span>Günler Dikey</span>
	                  </label>
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.orientation === 'horizontal'}
	                      onChange={() => setPrintOptions({ ...printOptions, orientation: 'horizontal' })}
	                    />
	                    <span>Günler Yatay</span>
	                  </label>
	                </div>
	              </div>
	              <div className="hp-print-actions">
	                {viewMode === 'teacher' && (
	                  <>
	                    <button type="button" onClick={() => openPrintPreview('teacher', 'all')}>▦ Tüm Öğretmenlerin Programını Yazdır</button>
	                    <button type="button" onClick={() => openPrintPreview('teacher', 'selected')}>▦ Seçili Öğretmenin Programını Yazdır</button>
	                  </>
	                )}
	                {viewMode === 'class' && (
	                  <>
	                    <button type="button" onClick={() => openPrintPreview('class', 'all')}>▦ Tüm Sınıfların Programını Yazdır</button>
	                    <button type="button" onClick={() => openPrintPreview('class', 'selected')}>▦ Seçili Sınıfın Programını Yazdır</button>
	                  </>
	                )}
	                {viewMode === 'subject' && (
	                  <button type="button" onClick={() => openPrintPreview('subject', 'selected')}>▦ Seçili Dersin Programını Yazdır</button>
	                )}
	                <button type="button" onClick={exportPrintCsv}>▣ Excel'e Aktar</button>
	                <button type="button" onClick={() => setPrintOpen(false)}>▥ Kapat</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	      {placementEditor && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-placement-window">
	            <div className="hp-window-titlebar">
	              <div><strong>Yerleşme Biçimi</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setPlacementEditor(null)}>x</button>
	            </div>
	            <div className="hp-form-card">
	              <Field label="Sınıf"><input value={entityName(classMap, placementEditor.classId)} readOnly /></Field>
	              <Field label="Ders"><input value={entityName(subjectMap, placementEditor.subjectId)} readOnly /></Field>
	              <Field label="Yerleşme biçimi">
	                <input
	                  placeholder="2+1"
	                  value={placementEditor.distributionFormat}
	                  onChange={event => setPlacementEditor({ ...placementEditor, distributionFormat: event.target.value })}
	                />
	              </Field>
	              <p className="hp-muted">Örnek: 3 saatlik ders için 2+1, 5 saatlik ders için 2+2+1.</p>
	              <div className="hp-row-actions">
	                <button type="button" onClick={savePlacementFormat}>Kaydet</button>
	                <button type="button" onClick={() => setPlacementEditor(null)}>İptal</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	      {autoSettingsOpen && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-auto-settings-window">
	            <div className="hp-window-titlebar">
	              <div><strong>Otomat Ayarları</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setAutoSettingsOpen(false)}>x</button>
	            </div>
	            <div className="hp-auto-settings-body">
	              <div className="hp-auto-settings-form">
	                <label>
	                  <span>1 - Öğretmen programlarında dersler arasında boşluk verilsin mi?</span>
	                  <select value={autoSettings.allowTeacherGaps} onChange={event => setAutoSettings({ ...autoSettings, allowTeacherGaps: event.target.value })}>
	                    <option value="E">E</option>
	                    <option value="H">H</option>
	                  </select>
	                </label>
	                <label>
	                  <span>2 - Bir haftada boşluk sayısı en fazla kaç olsun?</span>
	                  <input type="number" min="0" max="20" value={autoSettings.maxWeeklyGaps} onChange={event => setAutoSettings({ ...autoSettings, maxWeeklyGaps: Number(event.target.value) })} />
	                </label>
	                <label>
	                  <span>3 - İki ders arasında en fazla kaç boşluk olsun?</span>
	                  <input type="number" min="0" max="20" value={autoSettings.maxBetweenLessonGaps} onChange={event => setAutoSettings({ ...autoSettings, maxBetweenLessonGaps: Number(event.target.value) })} />
	                </label>
	                <label>
	                  <span>4 - Öğretmen aynı sınıfın birden fazla dersine giriyorsa farklı günlere gelsin mi?</span>
	                  <select value={autoSettings.sameClassLessonsDifferentDays} onChange={event => setAutoSettings({ ...autoSettings, sameClassLessonsDifferentDays: event.target.value })}>
	                    <option value="E">E</option>
	                    <option value="H">H</option>
	                  </select>
	                </label>
	                <label>
	                  <span>5 - Bir ders 2 biçiminde dağıtılıyorsa 1+1 biçiminde birleştirilmesine izin verilsin mi?</span>
	                  <select value={autoSettings.splitTwoHourAsOnePlusOne} onChange={event => setAutoSettings({ ...autoSettings, splitTwoHourAsOnePlusOne: event.target.value })}>
	                    <option value="H">H</option>
	                    <option value="E">E</option>
	                  </select>
	                </label>
	                <label>
	                  <span>6 - Bir öğretmene günde en fazla kaç saat ders verilsin?</span>
	                  <input type="number" min="1" max="18" value={autoSettings.maxDailyTeacherHours} onChange={event => setAutoSettings({ ...autoSettings, maxDailyTeacherHours: Number(event.target.value) })} />
	                </label>
	              </div>
	              <div className="hp-auto-settings-days">
	                <strong>Gün Kapat / Aç</strong>
	                {localDays.map(day => (
	                  <label key={day.dayIndex}>
	                    <input
	                      type="checkbox"
	                      checked={Boolean(day.isActive)}
	                      onChange={event => updateLocalDay(day.dayIndex, { isActive: event.target.checked })}
	                    />
	                    <span>{day.name}</span>
	                    <input
	                      type="number"
	                      min="1"
	                      max="18"
	                      value={day.periodCount}
	                      onChange={event => updateLocalDay(day.dayIndex, { periodCount: Number(event.target.value) })}
	                    />
	                  </label>
	                ))}
	              </div>
	              <div className="hp-auto-settings-actions">
	                <button type="button" onClick={saveAutoSettings}>✓ Tamam</button>
	                <button type="button" onClick={() => setAutoSettingsOpen(false)}>✕ İptal</button>
	                <button type="button" onClick={() => toast('Ayrıntılar ekranı ayrıca backend kurallarıyla bağlanabilir')}>Ayrıntılar</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	    </WindowModal>
  );
}

function MiniList({ title, items, icon, onManage }) {
  return (
    <div className="hp-mini-panel">
      <div className="hp-panel-title">{icon} {title}<button type="button" onClick={onManage}>Yönet</button></div>
      <div className="hp-mini-list">
        {items.slice(0, 12).map(item => <span key={item.id}>{item.shortName || item.name}</span>)}
      </div>
    </div>
  );
}

function CellInspector({ selectedCell, cellData, subjects, teachers, classes, subjectMap, teacherMap, classMap, updateTimetableCell, setSelectedCell, viewClassId }) {
  const [form, setForm] = useState({ classId: '', subjectId: '', teacherId: '', isFixed: true });

  useEffect(() => {
    if (!selectedCell) return;
    setForm({
      classId: cellData?.classId || viewClassId || String(classes[0]?.id || ''),
      subjectId: String(cellData?.cell?.subjectId || subjects[0]?.id || ''),
      teacherId: String(cellData?.cell?.teacherId || cellData?.cell?.teacherIds?.[0] || teachers[0]?.id || ''),
      isFixed: Boolean(cellData?.cell?.isFixed ?? true),
    });
  }, [cellData, classes, selectedCell, subjects, teachers, viewClassId]);

  if (!selectedCell) {
    return (
      <div className="hp-mini-panel hp-cell-panel">
        <div className="hp-panel-title">Seçili Hücre</div>
        <p className="hp-muted">Düzenlemek için grid üzerinde bir hücre seçin.</p>
      </div>
    );
  }

  const save = () => {
    const key = slotKey(selectedCell.day, selectedCell.period);
    const teacherId = String(form.teacherId || '');
    updateTimetableCell(form.classId, key, {
      subjectId: String(form.subjectId),
      teacherId: teacherId || null,
      teacherIds: teacherId ? [teacherId] : [],
      teacherless: !teacherId,
      isFixed: form.isFixed,
    });
    toast.success('Hücre güncellendi');
  };

  const remove = () => {
    if (cellData) updateTimetableCell(cellData.classId, cellData.key, null);
    setSelectedCell(null);
  };

  return (
    <div className="hp-mini-panel hp-cell-panel">
      <div className="hp-panel-title">Seçili Hücre</div>
      <div className="hp-cell-summary">
        <strong>{DAY_NAMES[selectedCell.day]} / {selectedCell.period + 1}. Saat</strong>
        <span>{cellData ? `${entityName(subjectMap, cellData.cell.subjectId)} - ${lessonTeacherLabel(cellData.cell, teacherMap)}` : 'Boş'}</span>
        {cellData && <small>{entityName(classMap, cellData.classId)}</small>}
      </div>
      <div className="hp-inline-form">
        <select value={form.classId} onChange={e => setForm({ ...form, classId: e.target.value })}>
          {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={form.subjectId} onChange={e => setForm({ ...form, subjectId: e.target.value })}>
          {subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={form.teacherId} onChange={e => setForm({ ...form, teacherId: e.target.value })}>
          {teachers.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <label className="hp-check"><input type="checkbox" checked={form.isFixed} onChange={e => setForm({ ...form, isFixed: e.target.checked })} /> Sabit</label>
      </div>
      <div className="hp-row-actions">
        <button type="button" onClick={save}>Yaz</button>
        <button type="button" className="danger" onClick={remove}>Sil</button>
      </div>
    </div>
  );
}

function SubjectsWindow({ subjects, setSubjects, refresh, onClose }) {
  const [form, setForm] = useState(emptyForm.subject);
  const [editingId, setEditingId] = useState(null);

  const selectSubject = (subject) => {
    setEditingId(subject.id);
    setForm({
      ...emptyForm.subject,
      ...subject,
      weeklyHours: subject.weeklyHours || '',
      distributionFormat: subject.distributionFormat || '',
    });
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Ders adı gerekli');
    const distributionFormat = form.distributionFormat || String(Number(form.weeklyHours) || 1);
    const distributionTotal = sumDistributionFormat(distributionFormat);
    const weeklyHours = distributionTotal || Number(form.weeklyHours) || 1;
    const payload = {
      name: form.name,
      ad: form.name,
      dersAdi: form.name,
      ders_adi: form.name,
      dersinAdi: form.name,
      dersin_adi: form.name,
      shortName: form.shortName,
      short_name: form.shortName,
      kisaAd: form.shortName,
      kisa_ad: form.shortName,
      kisaAdi: form.shortName,
      kisa_adi: form.shortName,
      weeklyHours,
      weekly_hours: weeklyHours,
      haftalikDersSaati: weeklyHours,
      haftalik_ders_saati: weeklyHours,
      haftalikSaat: weeklyHours,
      haftalik_saat: weeklyHours,
      HDS: weeklyHours,
      hds: weeklyHours,
      distributionFormat,
      distribution_format: distributionFormat,
      placementFormat: distributionFormat,
      placement_format: distributionFormat,
      yerlesimBicimi: distributionFormat,
      yerlesim_bicimi: distributionFormat,
      yerlesmeBicimi: distributionFormat,
      yerlesme_bicimi: distributionFormat,
      yerBicimi: distributionFormat,
      yer_bicimi: distributionFormat,
      yerBic: distributionFormat,
      yerlesim: distributionFormat,
      yer_bic: distributionFormat,
      color: form.color,
      renk: form.color,
    };
    if (editingId) await axios.put(`${API}/dersler/${editingId}`, payload);
    else await axios.post(`${API}/dersler`, payload);
    setForm(emptyForm.subject);
    setEditingId(null);
    await refresh();
    toast.success('Ders kaydedildi');
  };

  const remove = async (id) => {
    if (!window.confirm('Ders silinsin mi?')) return;
    await axios.delete(`${API}/dersler/${id}`);
    setSubjects(subjects.filter(item => String(item.id) !== String(id)));
    toast.success('Ders silindi');
  };

  return (
    <WindowModal title="Dersler" width={1080} onClose={onClose}>
      <div className="hp-window-grid two">
        <div className="hp-form-card">
          <Field label="Ders adı"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Kısa adı"><input value={form.shortName} onChange={e => setForm({ ...form, shortName: e.target.value })} /></Field>
          <Field label="Haftalık ders saati"><input type="number" min="1" value={form.weeklyHours} onChange={e => setForm({ ...form, weeklyHours: e.target.value })} /></Field>
          <Field label="Yerleşim biçimi"><input placeholder="2+2+1" value={form.distributionFormat} onChange={e => {
            const nextFormat = e.target.value;
            const total = sumDistributionFormat(nextFormat);
            setForm({ ...form, distributionFormat: nextFormat, ...(total ? { weeklyHours: total } : {}) });
          }} /></Field>
          <Field label="Renk"><input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} /></Field>
          <div className="hp-row-actions">
            <button onClick={submit}><FaRegSave /> Kaydet</button>
            <button onClick={() => { setEditingId(null); setForm(emptyForm.subject); }}>Yeni</button>
          </div>
        </div>
        <DenseTable headers={['No', 'Dersin Adı', 'Kısa Adı', 'HDS', 'Yerleşim Biçimi', 'İşlem']}>
          {subjects.map((subject, index) => (
            <tr key={subject.id}>
              <td>{index + 1}</td>
              <td>{subject.name}</td>
              <td>{subject.shortName}</td>
              <td>{subject.weeklyHours}</td>
              <td>{subject.distributionFormat}</td>
              <td>
                <button className="hp-link-btn" onClick={() => selectSubject(subject)}>Düzenle</button>
                <button className="hp-link-btn danger" onClick={() => remove(subject.id)}>Sil</button>
              </td>
            </tr>
          ))}
        </DenseTable>
      </div>
    </WindowModal>
  );
}

function ClassesWindow({ classes, setClasses, refresh, onClose }) {
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);

  const submit = async () => {
    if (!name.trim()) return toast.error('Sınıf adı gerekli');
    if (editingId) await axios.put(`${API}/siniflar/${editingId}`, { name });
    else await axios.post(`${API}/siniflar`, { name });
    setName('');
    setEditingId(null);
    await refresh();
    toast.success('Sınıf kaydedildi');
  };

  const remove = async (id) => {
    if (!window.confirm('Sınıf silinsin mi?')) return;
    await axios.delete(`${API}/siniflar/${id}`);
    setClasses(classes.filter(item => String(item.id) !== String(id)));
  };

  return (
    <WindowModal title="Sınıflar" width={760} onClose={onClose}>
      <div className="hp-window-grid two narrow">
        <div className="hp-form-card">
          <Field label="Sınıf adı"><input value={name} onChange={e => setName(e.target.value)} /></Field>
          <div className="hp-row-actions">
            <button onClick={submit}><FaRegSave /> Kaydet</button>
            <button onClick={() => toast('Sıralama backend order alanı ile korunur')}>Yukarı</button>
            <button onClick={() => toast('Sıralama backend order alanı ile korunur')}>Aşağı</button>
          </div>
        </div>
        <DenseTable headers={['No', 'Sınıfın Adı', 'İşlem']} minWidth={420}>
          {classes.map((klass, index) => (
            <tr key={klass.id}>
              <td>{index + 1}</td>
              <td>{klass.name}</td>
              <td>
                <button className="hp-link-btn" onClick={() => { setEditingId(klass.id); setName(klass.name); }}>Düzenle</button>
                <button className="hp-link-btn danger" onClick={() => remove(klass.id)}>Sil</button>
              </td>
            </tr>
          ))}
        </DenseTable>
      </div>
    </WindowModal>
  );
}

function SettingsWindow({ settings, setSettings, onClose }) {
  const [local, setLocal] = useState(settings);
  const [activeDay, setActiveDay] = useState(0);
  const day = local[activeDay] || local[0];

  const updateDay = (patch) => {
    setLocal(prev => prev.map(item => item.dayIndex === activeDay ? { ...item, ...patch } : item));
  };

  const updateSlot = (index, patch) => {
    updateDay({ timeSlots: day.timeSlots.map((slot, i) => i === index ? { ...slot, ...patch } : slot) });
  };

  const save = async () => {
    await axios.put(`${API}/ayarlar`, { dailyConfigs: local, daily_configs: local });
    setSettings(local);
    toast.success('Günler ve saatler kaydedildi');
    onClose();
  };

  return (
    <WindowModal title="Günler ve Saatler" width={1040} onClose={onClose}>
      <div className="hp-window-grid settings">
        <div className="hp-list-box">
          {local.map(item => (
            <button key={item.dayIndex} className={item.dayIndex === activeDay ? 'active' : ''} onClick={() => setActiveDay(item.dayIndex)}>
              <strong>{item.name}</strong><span>{item.shortName}</span>
            </button>
          ))}
        </div>
        <div className="hp-form-card">
          <div className="hp-settings-head">
            <Field label="Gün adı"><input value={day.name} onChange={e => updateDay({ name: e.target.value })} /></Field>
            <Field label="Kısa adı"><input value={day.shortName} onChange={e => updateDay({ shortName: e.target.value })} /></Field>
            <Field label="Aktif slot"><input type="number" min="1" max="18" value={day.periodCount} onChange={e => updateDay({ periodCount: Number(e.target.value) })} /></Field>
            <label className="hp-check"><input type="checkbox" checked={day.isActive} onChange={e => updateDay({ isActive: e.target.checked })} /> Aktif</label>
          </div>
          <DenseTable headers={['Slot', 'Başlangıç', 'Bitiş']} minWidth={420}>
            {Array.from({ length: 18 }, (_, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td><input type="time" value={day.timeSlots[index]?.start || '00:00'} onChange={e => updateSlot(index, { start: e.target.value })} /></td>
                <td><input type="time" value={day.timeSlots[index]?.end || '00:00'} onChange={e => updateSlot(index, { end: e.target.value })} /></td>
              </tr>
            ))}
          </DenseTable>
          <div className="hp-row-actions"><button onClick={save}><FaRegSave /> Kaydet</button></div>
        </div>
      </div>
    </WindowModal>
  );
}

function ClassSubjectsWindow({ classes, subjects, classSubjects, setClassSubjects, refresh, onClose }) {
  const [classId, setClassId] = useState(String(classes[0]?.id || ''));
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargetClassId, setCopyTargetClassId] = useState('');
  const [commonOpen, setCommonOpen] = useState(false);
  const [commonSubjectId, setCommonSubjectId] = useState('');
  const [commonPickedClassIds, setCommonPickedClassIds] = useState([]);
  const [commonGroups, setCommonGroups] = useState([]);
  const [selectedCommonGroupId, setSelectedCommonGroupId] = useState('');
  const currentSubjectIds = new Set(classSubjects.filter(item => String(item.classId) === String(classId)).map(item => String(item.subjectId)));
  const selectedClassSubjectRows = classSubjects
    .filter(item => String(item.classId) === String(classId))
    .map(item => {
      const subject = subjects.find(subjectItem => String(subjectItem.id) === String(item.subjectId)) || {};
      return {
        ...item,
        subject,
        name: subject.name || '-',
        shortName: subject.shortName || '',
        weeklyHours: Number(item.weeklyHours || subject.weeklyHours || 0),
        distributionFormat: item.distributionFormat || subject.distributionFormat || '',
      };
    });
  const selectedSubjects = subjects.filter(item => currentSubjectIds.has(String(item.id)));
  const availableSubjects = subjects;
  const selectedClass = classes.find(item => String(item.id) === String(classId));
  const selectedSubject = subjects.find(item => String(item.id) === String(selectedSubjectId));
  const selectedWeeklyTotal = selectedClassSubjectRows.reduce((total, item) => total + Number(item.weeklyHours || 0), 0);

  useEffect(() => {
    if (!classId && classes[0]) setClassId(String(classes[0].id));
  }, [classId, classes]);

  const addSubject = async (subjectId) => {
    if (!classId) return toast.error('Önce sınıf seçin');
    if (!subjectId) return toast.error('Ders seçin');
    if (currentSubjectIds.has(String(subjectId))) return toast('Bu ders zaten sınıfa atanmış');

    const subject = subjects.find(item => String(item.id) === String(subjectId));
    await axios.post(`${API}/sinif-dersleri`, {
      classId,
      subjectId,
      sinifId: classId,
      dersId: subjectId,
      weeklyHours: subject?.weeklyHours || 0,
      haftalikSaat: subject?.weeklyHours || 0,
      distributionFormat: subject?.distributionFormat || '',
      yerlesimBicimi: subject?.distributionFormat || '',
      teacherless: true,
      planTeacherless: true,
      ogretmensiz: true,
    });
    setClassSubjects([
      ...classSubjects,
      {
        id: `local-${classId}-${subjectId}`,
        classId: String(classId),
        subjectId: String(subjectId),
        weeklyHours: Number(subject?.weeklyHours || 0),
        distributionFormat: subject?.distributionFormat || '',
        teacherless: true,
        planTeacherless: true,
      },
    ]);
    setSelectedSubjectId('');
    refresh();
    toast.success('Ders sınıfa atandı');
  };

  const removeSubject = async (subjectId) => {
    await axios.delete(`${API}/siniflar/${classId}/dersler/${subjectId}`);
    setClassSubjects(classSubjects.filter(item => !(String(item.classId) === String(classId) && String(item.subjectId) === String(subjectId))));
    toast.success('Ders kaldırıldı');
  };

  const updateClassSubjectRow = (subjectId, patch) => {
    setClassSubjects(classSubjects.map(item => (
      String(item.classId) === String(classId) && String(item.subjectId) === String(subjectId)
        ? { ...item, ...patch, teacherless: true, planTeacherless: true }
        : item
    )));
  };

  const saveClassSubjectRow = async (row) => {
    const distributionFormat = String(row.distributionFormat || '').replace(/\s+/g, '');
    const distributionTotal = sumDistributionFormat(distributionFormat);
    const weeklyHours = distributionTotal || Number(row.weeklyHours || 0);
    if (!weeklyHours) return toast.error('Haftalık saat girin');
    if (distributionFormat && !distributionTotal) return toast.error('Yerleşme biçimi 2+1 gibi olmalı');
    const payload = {
      classId: row.classId,
      subjectId: row.subjectId,
      sinifId: row.classId,
      dersId: row.subjectId,
      weeklyHours,
      haftalikSaat: weeklyHours,
      distributionFormat: distributionFormat || String(weeklyHours),
      distribution_format: distributionFormat || String(weeklyHours),
      placementFormat: distributionFormat || String(weeklyHours),
      placement_format: distributionFormat || String(weeklyHours),
      yerlesimBicimi: distributionFormat || String(weeklyHours),
      yerlesim_bicimi: distributionFormat || String(weeklyHours),
      teacherless: true,
      planTeacherless: true,
      ogretmensiz: true,
    };
    try {
      await axios.put(`${API}/siniflar/${row.classId}/dersler/${row.subjectId}`, payload);
      setClassSubjects(classSubjects.map(item => (
        String(item.classId) === String(row.classId) && String(item.subjectId) === String(row.subjectId)
          ? { ...item, weeklyHours, distributionFormat: payload.distributionFormat, teacherless: true, planTeacherless: true }
          : item
      )));
      toast.success('Ders plan bilgisi kaydedildi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ders plan bilgisi kaydedilemedi');
    }
  };

  const saveAll = async () => {
    const subjectIds = Array.from(currentSubjectIds);
    await axios.put(`${API}/siniflar/${classId}/dersler`, { subjectIds, dersler: subjectIds });
    toast.success('Sınıf dersleri kaydedildi');
  };

  const openCopy = () => {
    const firstTarget = classes.find(item => String(item.id) !== String(classId));
    setCopyTargetClassId(String(firstTarget?.id || ''));
    setCopyOpen(true);
  };

  const copyLessons = async () => {
    if (!classId) return toast.error('Kaynak sınıf seçin');
    if (!copyTargetClassId) return toast.error('Kopyalanacak sınıf seçin');
    if (String(classId) === String(copyTargetClassId)) return toast.error('Aynı sınıfa kopyalama yapılamaz');

    const subjectIds = Array.from(currentSubjectIds);
    await axios.put(`${API}/siniflar/${copyTargetClassId}/dersler`, { subjectIds, dersler: subjectIds });
    const nextRows = classSubjects.filter(item => String(item.classId) !== String(copyTargetClassId));
    const copiedRows = selectedClassSubjectRows.map(row => ({
      id: `local-${copyTargetClassId}-${row.subjectId}`,
      classId: String(copyTargetClassId),
      subjectId: String(row.subjectId),
      weeklyHours: Number(row.weeklyHours || 0),
      distributionFormat: row.distributionFormat || '',
    }));
    setClassSubjects([...nextRows, ...copiedRows]);
    setCopyOpen(false);
    toast.success('Dersler kopyalandı');
    refresh();
  };

  const openCommonLessons = () => {
    const initialSubjectId = selectedSubjectId || subjects[0]?.id || '';
    setCommonSubjectId(String(initialSubjectId));
    setCommonPickedClassIds([]);
    setSelectedCommonGroupId('');
    setCommonOpen(true);
  };

  const subjectClassRows = classSubjects
    .filter(item => String(item.subjectId) === String(commonSubjectId))
    .map(item => ({
      ...item,
      className: classes.find(klass => String(klass.id) === String(item.classId))?.name || '-',
      subjectName: subjects.find(subject => String(subject.id) === String(item.subjectId))?.name || '-',
      weeklyHours: item.weeklyHours || subjects.find(subject => String(subject.id) === String(item.subjectId))?.weeklyHours || 0,
    }));
  const pickedRows = subjectClassRows.filter(item => commonPickedClassIds.includes(String(item.classId)));
  const activeCommonGroups = commonGroups.filter(group => String(group.subjectId) === String(commonSubjectId));
  const groupedRows = activeCommonGroups.flatMap(group => group.classIds.map(groupClassId => {
    const row = subjectClassRows.find(item => String(item.classId) === String(groupClassId));
    return row ? { ...row, groupId: group.id } : null;
  }).filter(Boolean));

  const toggleCommonClass = (targetClassId) => {
    setCommonPickedClassIds(prev => (
      prev.includes(String(targetClassId))
        ? prev.filter(id => id !== String(targetClassId))
        : [...prev, String(targetClassId)]
    ));
  };

  const combineCommonLessons = () => {
    if (!commonSubjectId) return toast.error('Ders seçin');
    if (commonPickedClassIds.length < 2) return toast.error('En az iki sınıf seçin');
    const groupId = `common-${commonSubjectId}-${Date.now()}`;
    const nextGroups = [
      ...commonGroups.filter(group => !(String(group.subjectId) === String(commonSubjectId) && group.classIds.some(id => commonPickedClassIds.includes(String(id))))),
      { id: groupId, subjectId: String(commonSubjectId), classIds: commonPickedClassIds },
    ];
    setCommonGroups(nextGroups);
    setClassSubjects(classSubjects.map(item => (
      String(item.subjectId) === String(commonSubjectId) && commonPickedClassIds.includes(String(item.classId))
        ? { ...item, commonGroupId: groupId }
        : item
    )));
    setSelectedCommonGroupId(groupId);
    toast.success('Ortak ders grubu oluşturuldu');
  };

  const dissolveCommonLesson = () => {
    if (!selectedCommonGroupId) return toast.error('Çözülecek ortak grubu seçin');
    setCommonGroups(commonGroups.filter(group => String(group.id) !== String(selectedCommonGroupId)));
    setClassSubjects(classSubjects.map(item => (
      String(item.commonGroupId) === String(selectedCommonGroupId)
        ? { ...item, commonGroupId: '' }
        : item
    )));
    setSelectedCommonGroupId('');
    toast.success('Ortak ders grubu çözüldü');
  };

  return (
    <WindowModal title="Sınıflarda Okutulan Dersler" width={1280} onClose={onClose}>
      <div className="hp-class-subjects">
        <div className="hp-class-col">
          <div className="hp-panel-title">Sınıflar</div>
          <DenseTable headers={['Sınıfın Adı', 'Alan', 'Top.']} minWidth={250}>
            {classes.map(item => {
              const itemSubjectIds = new Set(classSubjects.filter(row => String(row.classId) === String(item.id)).map(row => String(row.subjectId)));
              const total = subjects
                .filter(subject => itemSubjectIds.has(String(subject.id)))
                .reduce((sum, subject) => sum + Number(subject.weeklyHours || 0), 0);
              const isActive = String(item.id) === String(classId);
              return (
                <tr
                  key={item.id}
                  className={isActive ? 'selected' : ''}
                  onClick={() => {
                    setClassId(String(item.id));
                    setSelectedSubjectId('');
                  }}
                >
                  <td>{item.name}</td>
                  <td>{valueOf(item, ['area', 'alan'], '') || '-'}</td>
                  <td>{total}</td>
                </tr>
              );
            })}
          </DenseTable>
        </div>

        <div className="hp-assigned-col">
          <div className="hp-panel-title">Sınıfta Okutulan Dersler</div>
          <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS', 'Yer.Biç.', 'Plan', '']} minWidth={650}>
            {selectedClassSubjectRows.length === 0 ? (
              <tr><td colSpan={6} className="hp-empty-cell">{selectedClass?.name || '-'} için ders atanmadı</td></tr>
            ) : selectedClassSubjectRows.map(item => (
              <tr key={item.id || `${item.classId}-${item.subjectId}`}>
                <td>{selectedClass?.name || '-'}</td>
                <td>{item.name}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    value={item.weeklyHours || ''}
                    onChange={(event) => updateClassSubjectRow(item.subjectId, { weeklyHours: Number(event.target.value) })}
                    onBlur={() => saveClassSubjectRow(item)}
                  />
                </td>
                <td>
                  <input
                    value={item.distributionFormat || ''}
                    placeholder="2+1"
                    onChange={(event) => updateClassSubjectRow(item.subjectId, { distributionFormat: event.target.value })}
                    onBlur={() => saveClassSubjectRow(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                  />
                </td>
                <td><span className="hp-teacherless-plan">Bu ders öğretmensiz planlanabilir</span></td>
                <td><button className="hp-link-btn danger" onClick={() => removeSubject(item.subjectId)}>Sil</button></td>
              </tr>
            ))}
          </DenseTable>
        </div>

        <div className="hp-subject-col">
          <div className="hp-panel-title">Dersler</div>
          <DenseTable headers={['Dersin Adı', 'HDS', '']} minWidth={360}>
            {availableSubjects.length === 0 ? (
              <tr><td colSpan={3} className="hp-empty-cell">Atanacak ders kalmadı</td></tr>
            ) : availableSubjects.map(item => (
              <tr
                key={item.id}
                className={String(item.id) === String(selectedSubjectId) ? 'selected' : ''}
                onClick={() => setSelectedSubjectId(String(item.id))}
                onDoubleClick={() => addSubject(item.id)}
              >
                <td>{item.name}</td>
                <td>{item.weeklyHours || ''}</td>
                <td><button className="hp-link-btn" onClick={(event) => { event.stopPropagation(); addSubject(item.id); }}>Dersi Ata</button></td>
              </tr>
            ))}
          </DenseTable>
        </div>

        <div className="hp-class-subject-actions">
          <button className="hp-wide-btn" onClick={openCopy}>Ders Kopyala</button>
          <button className="hp-wide-btn" onClick={() => addSubject(selectedSubjectId)} disabled={!selectedSubjectId}>Dersi Ata</button>
          <button className="hp-wide-btn" onClick={saveAll}>Kaydet</button>
          <button className="hp-wide-btn" onClick={openCommonLessons}>Ortak Ders Tanımla</button>
          <button className="hp-wide-btn" onClick={onClose}>Kapat</button>
        </div>

        <div className="hp-class-subject-status">
          <span>F4 = Ders Kopyala</span>
          <span>F6 = Ortak Dersler Tanımla</span>
          <span>F7 = Sil</span>
          <span>Enter / Çift tık = Ders Ver</span>
          <strong>{selectedClass?.name || '-'} Toplam HDS: {selectedWeeklyTotal}</strong>
          {selectedSubject && <span>Seçili ders: {selectedSubject.name}</span>}
        </div>

        {copyOpen && (
          <div className="hp-copy-dialog">
            <div className="hp-copy-window">
              <div className="hp-window-titlebar">
                <div><strong>Ders Kopyala</strong></div>
                <button className="hp-icon-btn" type="button" onClick={() => setCopyOpen(false)}>x</button>
              </div>
              <div className="hp-copy-body">
                <div className="hp-copy-list">
                  <DenseTable headers={['No', 'Sınıfın Adı']} minWidth={220}>
                    {classes.filter(item => String(item.id) !== String(classId)).map((item, index) => (
                      <tr
                        key={item.id}
                        className={String(item.id) === String(copyTargetClassId) ? 'selected' : ''}
                        onClick={() => setCopyTargetClassId(String(item.id))}
                        onDoubleClick={copyLessons}
                      >
                        <td>{index + 1}</td>
                        <td>{item.name}</td>
                      </tr>
                    ))}
                  </DenseTable>
                </div>
                <div className="hp-copy-summary">
                  <div><span>Bu sınıftaki dersler</span><strong>{selectedClass?.name || '-'}</strong></div>
                  <div><span>Bu sınıfa kopyalanacak</span><strong>{classes.find(item => String(item.id) === String(copyTargetClassId))?.name || '-'}</strong></div>
                  <small>{selectedSubjects.length} ders, toplam {selectedWeeklyTotal} HDS</small>
                  <button className="hp-wide-btn" onClick={copyLessons}>Tamam</button>
                  <button className="hp-wide-btn" onClick={() => setCopyOpen(false)}>İptal</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {commonOpen && (
          <div className="hp-common-dialog">
            <div className="hp-common-window">
              <div className="hp-window-titlebar">
                <div><strong>Ortak Ders Tanımlama</strong></div>
                <button className="hp-icon-btn" type="button" onClick={() => setCommonOpen(false)}>x</button>
              </div>
              <div className="hp-common-body">
                <div className="hp-common-left">
                  <DenseTable headers={['No', 'Dersin Adı', 'HDS']} minWidth={320}>
                    {subjects.map((subject, index) => (
                      <tr
                        key={subject.id}
                        className={String(subject.id) === String(commonSubjectId) ? 'selected' : ''}
                        onClick={() => {
                          setCommonSubjectId(String(subject.id));
                          setCommonPickedClassIds([]);
                          setSelectedCommonGroupId('');
                        }}
                      >
                        <td>{index + 1}</td>
                        <td>{subject.name}</td>
                        <td>{subject.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>

                  <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={320}>
                    {subjectClassRows.length === 0 ? (
                      <tr><td colSpan={3} className="hp-empty-cell">Bu dersi alan sınıf yok</td></tr>
                    ) : subjectClassRows.map(row => (
                      <tr
                        key={`${row.classId}-${row.subjectId}`}
                        className={commonPickedClassIds.includes(String(row.classId)) ? 'selected' : ''}
                        onClick={() => toggleCommonClass(row.classId)}
                        onDoubleClick={combineCommonLessons}
                      >
                        <td>{row.className}</td>
                        <td>{row.subjectName}</td>
                        <td>{row.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>
                </div>

                <div className="hp-common-middle">
                  <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={300}>
                    {pickedRows.length === 0 ? (
                      <tr><td colSpan={3} className="hp-empty-cell">Birleştirilecek sınıfları seçin</td></tr>
                    ) : pickedRows.map(row => (
                      <tr key={`${row.classId}-${row.subjectId}`}>
                        <td>{row.className}</td>
                        <td>{row.subjectName}</td>
                        <td>{row.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>
                  <button className="hp-wide-btn" onClick={combineCommonLessons}>Birleştir</button>
                </div>

                <div className="hp-common-right">
                  <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={320}>
                    {groupedRows.length === 0 ? (
                      <tr><td colSpan={3} className="hp-empty-cell">Ortak ders grubu yok</td></tr>
                    ) : groupedRows.map(row => (
                      <tr
                        key={`${row.groupId}-${row.classId}`}
                        className={String(row.groupId) === String(selectedCommonGroupId) ? 'selected' : ''}
                        onClick={() => setSelectedCommonGroupId(String(row.groupId))}
                      >
                        <td>{row.className}</td>
                        <td>{row.subjectName}</td>
                        <td>{row.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>
                  <div className="hp-common-actions">
                    <button className="hp-wide-btn" onClick={dissolveCommonLesson}>Çöz</button>
                    <button className="hp-wide-btn" onClick={() => setCommonOpen(false)}>Kapat</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </WindowModal>
  );
}

function DailyLimitsWindow({ classes, settings, dailyLimits, setDailyLimits, onClose }) {
  const [classId, setClassId] = useState(String(classes[0]?.id || ''));
  const [local, setLocal] = useState({});

  useEffect(() => {
    const row = dailyLimits.find(item => String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')) === String(classId)) || {};
    const limits = row.limits || row.gunler || {};
    const next = {};
    settings.forEach(day => {
      const current = limits[day.dayIndex] || limits[day.name] || {};
      next[day.dayIndex] = {
        start: Number(valueOf(current, ['start', 'baslangic', 'baslangicSlot'], 1)),
        end: Number(valueOf(current, ['end', 'bitis', 'bitisSlot'], day.periodCount)),
      };
    });
    setLocal(next);
  }, [classId, dailyLimits, settings]);

  const save = async () => {
    const payload = { classId, sinifId: classId, limits: local, classDailyLimits: local };
    await axios.put(`${API}/siniflar/${classId}/gunluk-ders-sayilari`, payload);
    const next = dailyLimits.filter(item => String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')) !== String(classId));
    setDailyLimits([...next, payload]);
    toast.success('Günlük ders sayıları kaydedildi');
  };

  return (
    <WindowModal title="Günlük Ders Sayıları" width={940} onClose={onClose}>
      <div className="hp-window-grid settings">
        <div className="hp-list-box">
          {classes.map(item => <button key={item.id} className={String(item.id) === String(classId) ? 'active' : ''} onClick={() => setClassId(String(item.id))}>{item.name}</button>)}
        </div>
        <div className="hp-form-card">
          <DenseTable headers={['Gün', 'Başlangıç Slotu', 'Bitiş Slotu']} minWidth={520}>
            {settings.map(day => (
              <tr key={day.dayIndex}>
                <td>{day.name}</td>
                <td><input type="number" min="1" max="18" value={local[day.dayIndex]?.start || 1} onChange={e => setLocal({ ...local, [day.dayIndex]: { ...local[day.dayIndex], start: Number(e.target.value) } })} /></td>
                <td><input type="number" min="1" max="18" value={local[day.dayIndex]?.end || day.periodCount} onChange={e => setLocal({ ...local, [day.dayIndex]: { ...local[day.dayIndex], end: Number(e.target.value) } })} /></td>
              </tr>
            ))}
          </DenseTable>
          <div className="hp-row-actions"><button onClick={save}><FaRegSave /> Kaydet</button></div>
        </div>
      </div>
    </WindowModal>
  );
}

function TeachersWindow({ teachers, setTeachers, settings, setTimetableResult, setWarnings, refresh, onClose }) {
  const [form, setForm] = useState(emptyForm.teacher);
  const [editingId, setEditingId] = useState(null);
  const [unavailableTeacher, setUnavailableTeacher] = useState(null);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Öğretmen adı gerekli');
    const payload = {
      name: form.name,
      shortName: form.shortName,
      branch: form.branch,
      homeroomClass: form.homeroomClass,
      club: form.club,
      email: form.email,
    };
    if (editingId) await axios.put(`${API}/ogretmenler/${editingId}`, payload);
    else await axios.post(`${API}/ogretmenler`, payload);
    setForm(emptyForm.teacher);
    setEditingId(null);
    await refresh();
    toast.success('Öğretmen kaydedildi');
  };

  const remove = async (id) => {
    if (!window.confirm('Öğretmen silinsin mi?')) return;
    await axios.delete(`${API}/ogretmenler/${id}`);
    setTeachers(teachers.filter(item => String(item.id) !== String(id)));
  };

  return (
    <WindowModal title="Öğretmenler" width={1180} onClose={onClose}>
      <div className="hp-window-grid two">
        <div className="hp-form-card">
          <Field label="Öğretmen adı"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Kısa adı"><input value={form.shortName} onChange={e => setForm({ ...form, shortName: e.target.value })} /></Field>
          <Field label="Branş"><input value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} /></Field>
          <Field label="Sınıf öğretmeni"><input value={form.homeroomClass} onChange={e => setForm({ ...form, homeroomClass: e.target.value })} /></Field>
          <Field label="Sosyal kulübü"><input value={form.club} onChange={e => setForm({ ...form, club: e.target.value })} /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
          <div className="hp-row-actions"><button onClick={submit}><FaRegSave /> Kaydet</button><button onClick={() => { setEditingId(null); setForm(emptyForm.teacher); }}>Yeni</button></div>
        </div>
        <DenseTable headers={['No', 'Branşı', 'Adı Soyadı', 'Sınıf Öğr.', 'Sosyal Kulübü', 'İşlem']} minWidth={760}>
          {teachers.map((teacher, index) => (
            <tr key={teacher.id}>
              <td>{index + 1}</td>
              <td>{teacher.branch}</td>
              <td>{teacher.name}</td>
              <td>{teacher.homeroomClass}</td>
              <td>{teacher.club}</td>
              <td>
                <button className="hp-link-btn" onClick={() => { setEditingId(teacher.id); setForm(teacher); }}>Düzenle</button>
                <button className="hp-link-btn" onClick={() => setUnavailableTeacher(teacher)}>Kapalı Saat</button>
                <button className="hp-link-btn danger" onClick={() => remove(teacher.id)}>Sil</button>
              </td>
            </tr>
          ))}
        </DenseTable>
      </div>
      {unavailableTeacher && (
        <UnavailableGrid
          teacher={unavailableTeacher}
          settings={settings}
          setTimetableResult={setTimetableResult}
          setWarnings={setWarnings}
          onClose={() => setUnavailableTeacher(null)}
          onSaved={async () => { setUnavailableTeacher(null); await refresh(); }}
        />
      )}
    </WindowModal>
  );
}

function UnavailableGrid({ teacher, settings, setTimetableResult, setWarnings, onClose, onSaved }) {
  const [slots, setSlots] = useState(new Set(asArray(teacher.unavailableSlots)));
  const visibleDays = settings.length ? settings : buildDefaultSettings();
  const maxPeriod = Math.max(1, ...visibleDays.map(day => day.periodCount));

  const toggle = (key) => {
    const next = new Set(slots);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSlots(next);
  };

  const save = async () => {
    try {
      const response = await axios.put(`${API}/ogretmenler/${teacher.id}/unavailable`, {
        unavailableSlots: Array.from(slots),
        kapaliSaatler: Array.from(slots),
      });
      const payload = getPayload(response);
      setTimetableResult(normalizeTimetable(payload.timetableResult));
      setWarnings(asArray(payload.warnings || payload.uyarilar));
      const removedProgramHours = Number(valueOf(payload, [
        'removedProgramHours',
        'removed_program_hours',
        'cikarilanProgramSaati',
      ], 0)) || 0;
      if (removedProgramHours > 0) {
        toast.success(`${removedProgramHours} ders öğretmenin kapalı saatlerinden çıkarıldı ve bekleyen derslere alındı.`);
      } else {
        toast.success('Kapalı saatler kaydedildi');
      }
      await onSaved();
    } catch (error) {
      toast.error(scheduleErrorMessage(error, 'Öğretmenin kapalı saatleri kaydedilemedi'));
    }
  };

  return (
    <div className="hp-nested-modal">
      <div className="hp-window small">
        <div className="hp-window-titlebar"><strong>{teacher.name} Kapalı Saatleri</strong><button onClick={onClose}>x</button></div>
        <div className="hp-grid-scroll">
          <table className="hp-table hp-unavailable">
            <thead><tr><th>Gün</th>{Array.from({ length: maxPeriod }, (_, i) => <th key={i}>{i + 1}</th>)}</tr></thead>
            <tbody>
              {visibleDays.map(day => (
                <tr key={day.dayIndex}>
                  <td>{day.name}</td>
                  {Array.from({ length: maxPeriod }, (_, period) => {
                    const key = slotKey(day.dayIndex, period);
                    return <td key={period} className={period >= day.periodCount ? 'inactive' : ''}><button className={slots.has(key) ? 'closed' : ''} disabled={period >= day.periodCount} onClick={() => toggle(key)}>{slots.has(key) ? 'Kapalı' : 'Açık'}</button></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="hp-row-actions"><button onClick={save}>Kaydet</button></div>
      </div>
    </div>
  );
}

function AssignmentsWindow({ teachers, classes, subjects, classSubjects, emptyClassSubjects, assignments, timetableResult, setAssignments, setEmptyClassSubjects, setTimetableResult, refresh, onClose }) {
  const [teacherId, setTeacherId] = useState(String(teachers[0]?.id || ''));
  const [subjectId, setSubjectId] = useState(String(subjects[0]?.id || ''));
  const [classId, setClassId] = useState(String(classes[0]?.id || ''));
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [selectedEmptyKey, setSelectedEmptyKey] = useState('');
  const teacherMap = useMemo(() => Object.fromEntries(teachers.map(item => [String(item.id), item])), [teachers]);
  const classMap = useMemo(() => Object.fromEntries(classes.map(item => [String(item.id), item])), [classes]);
  const subjectMap = useMemo(() => Object.fromEntries(subjects.map(item => [String(item.id), item])), [subjects]);

  useEffect(() => {
    if (!teacherId && teachers[0]) setTeacherId(String(teachers[0].id));
    if (!subjectId && subjects[0]) setSubjectId(String(subjects[0].id));
    if (!classId && classes[0]) setClassId(String(classes[0].id));
  }, [classId, classes, subjectId, subjects, teacherId, teachers]);

  const currentRows = assignments.filter(item => String(item.teacherId) === String(teacherId));
  const selectedTeacher = teacherMap[String(teacherId)];
  const selectedSubject = subjectMap[String(subjectId)];
  const subjectClassIds = classSubjects
    .filter(item => String(item.subjectId) === String(subjectId))
    .map(item => String(item.classId));
  const classRows = (subjectClassIds.length > 0 ? subjectClassIds.map(id => classMap[id]).filter(Boolean) : classes);
  const teacherWeeklyTotal = currentRows.reduce((total, item) => total + Number(subjectMap[String(item.subjectId)]?.weeklyHours || item.weeklyHours || 0), 0);
  const fallbackEmptyLessons = classSubjects.filter(item => !assignments.some(row => (
    String(row.classId) === String(item.classId) &&
    String(row.subjectId) === String(item.subjectId)
  )));
  const emptyLessonSource = Array.isArray(emptyClassSubjects) ? emptyClassSubjects : fallbackEmptyLessons;
  const emptyLessonRows = emptyLessonSource
    .map(item => ({
      ...item,
      key: `${item.classId}-${item.subjectId}`,
      className: entityName(classMap, item.classId),
      subjectName: entityName(subjectMap, item.subjectId),
      weeklyHours: subjectMap[String(item.subjectId)]?.weeklyHours || item.weeklyHours || 0,
    }));
  const selectedEmptyLesson = emptyLessonRows.find(item => item.key === selectedEmptyKey) || emptyLessonRows[0];

  const add = async (targetClassId = classId, targetSubjectId = subjectId) => {
    if (!teacherId) return toast.error('Öğretmen seçin');
    if (!targetSubjectId) return toast.error('Ders seçin');
    if (!targetClassId) return toast.error('Sınıf seçin');
    const targetSubject = subjectMap[String(targetSubjectId)];
    const exists = assignments.some(item => (
      String(item.teacherId) === String(teacherId) &&
      String(item.subjectId) === String(targetSubjectId) &&
      String(item.classId) === String(targetClassId)
    ));
    if (exists) return toast('Bu atama zaten var');

    try {
      const response = await axios.post(`${API}/atamalar`, {
        teacherId,
        subjectId: targetSubjectId,
        classId: targetClassId,
        ogretmenId: teacherId,
        dersId: targetSubjectId,
        sinifId: targetClassId,
        weeklyHours: targetSubject?.weeklyHours || 0,
        haftalikSaat: targetSubject?.weeklyHours || 0,
        timetableResult,
      });
      const payload = getPayload(response);
      if (payloadHasTimetable(payload)) {
        setTimetableResult(normalizeTimetable(payload.timetableResult || payload.timetable_result || payload.program || payload));
      } else if (payload?.updatedProgramCells || payload?.updated_program_cells) {
        setTimetableResult(current => mergeUpdatedProgramCells(current, payload.updatedProgramCells || payload.updated_program_cells));
      }
      setAssignments([
        ...assignments,
        {
          id: valueOf(payload, ['id', '_id'], `local-${teacherId}-${targetSubjectId}-${targetClassId}`),
          teacherId: String(teacherId),
          subjectId: String(targetSubjectId),
          classId: String(targetClassId),
          weeklyHours: Number(targetSubject?.weeklyHours || 0),
        },
      ]);
      if (Array.isArray(emptyClassSubjects)) {
        setEmptyClassSubjects(emptyClassSubjects.filter(item => !(
          String(item.classId) === String(targetClassId) &&
          String(item.subjectId) === String(targetSubjectId)
        )));
      }
      await refresh();
      toast.success('Atama kaydedildi');
    } catch (error) {
      toast.error(scheduleErrorMessage(error, 'Atama kaydedilemedi'));
    }
  };

  const planTeacherless = async (targetClassId = classId, targetSubjectId = subjectId) => {
    if (!targetSubjectId) return toast.error('Ders seçin');
    if (!targetClassId) return toast.error('Sınıf seçin');
    const targetSubject = subjectMap[String(targetSubjectId)];
    const targetClassSubject = classSubjects.find(item => (
      String(item.subjectId) === String(targetSubjectId) &&
      String(item.classId) === String(targetClassId)
    ));
    const exists = assignments.some(item => (
      String(item.subjectId) === String(targetSubjectId) &&
      String(item.classId) === String(targetClassId)
    ));
    if (exists) return toast('Bu ders için program ataması zaten var');
    const weeklyHours = Number(targetClassSubject?.weeklyHours || targetSubject?.weeklyHours || 0);
    const distributionFormat = targetClassSubject?.distributionFormat || targetSubject?.distributionFormat || '';
    try {
      const response = await axios.post(`${API}/atamalar`, {
        classId: targetClassId,
        subjectId: targetSubjectId,
        teacherId: null,
        teacherIds: [],
        teacherless: true,
        planTeacherless: true,
        weeklyHours,
        distributionFormat,
      });
      const payload = getPayload(response);
      if (payloadHasTimetable(payload)) {
        setTimetableResult(normalizeTimetable(payload.timetableResult || payload.timetable_result || payload.program || payload));
      }
      if (Array.isArray(emptyClassSubjects)) {
        setEmptyClassSubjects(emptyClassSubjects.filter(item => !(
          String(item.classId) === String(targetClassId) &&
          String(item.subjectId) === String(targetSubjectId)
        )));
      }
      await refresh();
      toast.success('Öğretmensiz program ataması kaydedildi');
    } catch (error) {
      toast.error(scheduleErrorMessage(error, 'Öğretmensiz ders kaydedilemedi'));
    }
  };

  const remove = async (id) => {
    const removed = assignments.find(item => String(item.id) === String(id));
    await axios.delete(`${API}/atamalar/${id}`);
    const nextAssignments = assignments.filter(item => String(item.id) !== String(id));
    setAssignments(nextAssignments);
    if (removed && Array.isArray(emptyClassSubjects)) {
      const stillAssigned = nextAssignments.some(item => (
        String(item.classId) === String(removed.classId) &&
        String(item.subjectId) === String(removed.subjectId)
      ));
      const alreadyEmpty = emptyClassSubjects.some(item => (
        String(item.classId) === String(removed.classId) &&
        String(item.subjectId) === String(removed.subjectId)
      ));
      const classSubject = classSubjects.find(item => (
        String(item.classId) === String(removed.classId) &&
        String(item.subjectId) === String(removed.subjectId)
      ));
      if (!stillAssigned && !alreadyEmpty && classSubject) {
        setEmptyClassSubjects([...emptyClassSubjects, classSubject]);
      }
    }
    toast.success('Atama silindi');
  };

  const openEmptyLessons = () => {
    setSelectedEmptyKey(emptyLessonRows[0]?.key || '');
    setEmptyOpen(true);
  };

  const goEmptyLesson = async (lesson = selectedEmptyLesson) => {
    if (!lesson) return toast.error('Boş ders yok');
    setSelectedEmptyKey(lesson.key);
    setSubjectId(String(lesson.subjectId));
    setClassId(String(lesson.classId));
    setEmptyOpen(false);
    toast.success('Boş ders seçildi');
  };

  return (
    <WindowModal title="Öğretmenlere Ders Atanması" width={1240} onClose={onClose}>
      <div className="hp-teacher-assign-layout">
        <div className="hp-teacher-left">
          <div className="hp-panel-title">Öğretmenler</div>
          <DenseTable headers={['No', 'Adı Soyadı', 'Branşı', 'Top.']} minWidth={420}>
            {teachers.map((item, index) => {
              const total = assignments
                .filter(row => String(row.teacherId) === String(item.id))
                .reduce((sum, row) => sum + Number(subjectMap[String(row.subjectId)]?.weeklyHours || row.weeklyHours || 0), 0);
              return (
                <tr
                  key={item.id}
                  className={String(item.id) === String(teacherId) ? 'selected' : ''}
                  onClick={() => setTeacherId(String(item.id))}
                >
                  <td>{index + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.branch || '-'}</td>
                  <td>{total || ''}</td>
                </tr>
              );
            })}
          </DenseTable>

          <div className="hp-panel-title">Öğretmenin Okuttuğu Dersler</div>
          <DenseTable headers={['Sınıf', 'Dersin Adı', 'Kısa Adı', 'H', '']} minWidth={420}>
            {currentRows.length === 0 ? (
              <tr><td colSpan={5} className="hp-empty-cell">{selectedTeacher?.name || '-'} için atama yok</td></tr>
            ) : currentRows.map(row => {
              const subject = subjectMap[String(row.subjectId)];
              return (
                <tr key={row.id}>
                  <td>{entityName(classMap, row.classId)}</td>
                  <td>{entityName(subjectMap, row.subjectId)}</td>
                  <td>{subject?.shortName || '-'}</td>
                  <td>{subject?.weeklyHours || row.weeklyHours || ''}</td>
                  <td><button className="hp-link-btn danger" onClick={() => remove(row.id)}>Sil</button></td>
                </tr>
              );
            })}
          </DenseTable>
        </div>

        <div className="hp-teacher-subjects">
          <div className="hp-panel-title">Dersler</div>
          <DenseTable headers={['Dersin Adı', 'HDS']} minWidth={320}>
            {subjects.map(item => (
              <tr
                key={item.id}
                className={String(item.id) === String(subjectId) ? 'selected' : ''}
                onClick={() => {
                  setSubjectId(String(item.id));
                  const firstSubjectClass = classSubjects.find(row => String(row.subjectId) === String(item.id));
                  setClassId(String(firstSubjectClass?.classId || classes[0]?.id || ''));
                }}
              >
                <td>{item.name}</td>
                <td>{item.weeklyHours || ''}</td>
              </tr>
            ))}
          </DenseTable>
        </div>

        <div className="hp-teacher-classes">
          <div className="hp-panel-title">Dersin Okutulduğu Sınıflar</div>
          <DenseTable headers={['No', 'Sınıf', 'Öğretmen']} minWidth={360}>
            {classRows.map((item, index) => {
              const rowAssignment = assignments.find(row => String(row.subjectId) === String(subjectId) && String(row.classId) === String(item.id));
              const hasClassSubject = classSubjects.some(row => String(row.subjectId) === String(subjectId) && String(row.classId) === String(item.id));
              return (
                <tr
                  key={item.id}
                  className={String(item.id) === String(classId) ? 'selected' : ''}
                  onClick={() => setClassId(String(item.id))}
                  onDoubleClick={() => add(item.id)}
                >
                  <td>{index + 1}</td>
                  <td>{item.name}</td>
                  <td>{rowAssignment ? entityName(teacherMap, rowAssignment.teacherId) : hasClassSubject ? 'Öğretmen atanmadı' : ''}</td>
                </tr>
              );
            })}
          </DenseTable>
        </div>
      </div>

      <div className="hp-teacher-assign-actions">
        <button className="hp-wide-btn" onClick={() => remove(currentRows[currentRows.length - 1]?.id)} disabled={currentRows.length === 0}>Sil</button>
        <button className="hp-wide-btn">Yazdır</button>
        <button className="hp-wide-btn" onClick={openEmptyLessons}>Boş Dersler</button>
        <button className="hp-wide-btn" onClick={() => planTeacherless()}>Öğretmensiz Planla</button>
        <button className="hp-wide-btn">Dersliklere Ders Atanması</button>
        <button className="hp-wide-btn">Yardım</button>
        <button className="hp-wide-btn" onClick={onClose}>Kapat</button>
      </div>

      <div className="hp-teacher-assign-status">
        <span>F1 = Yardım</span>
        <span>F2 = Yazdır</span>
        <span>F6 = Boş Dersler</span>
        <span>F7 = Sil</span>
        <span>Enter / Çift tık = Ders Ver</span>
        <strong>{selectedTeacher?.name || '-'} Toplam HDS: {teacherWeeklyTotal}</strong>
      </div>

      {emptyOpen && (
        <div className="hp-empty-lessons-dialog">
          <div className="hp-empty-lessons-window">
            <div className="hp-window-titlebar">
              <div><strong>Boş Dersler</strong></div>
              <button className="hp-icon-btn" type="button" onClick={() => setEmptyOpen(false)}>x</button>
            </div>
            <div className="hp-empty-lessons-body">
              <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={420}>
                {emptyLessonRows.length === 0 ? (
                  <tr><td colSpan={3} className="hp-empty-cell">Atanmamış ders yok</td></tr>
                ) : emptyLessonRows.map(row => (
                  <tr
                    key={row.key}
                    className={row.key === selectedEmptyKey ? 'selected' : ''}
                    onClick={() => setSelectedEmptyKey(row.key)}
                    onDoubleClick={() => goEmptyLesson(row)}
                  >
                    <td>{row.className}</td>
                    <td>{row.subjectName}</td>
                    <td>{row.weeklyHours || ''}</td>
                  </tr>
                ))}
              </DenseTable>
              <div className="hp-empty-lessons-actions">
                <button className="hp-wide-btn" onClick={() => goEmptyLesson()}>Git</button>
                <button className="hp-wide-btn" onClick={() => setEmptyOpen(false)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </WindowModal>
  );
}

function ProgramListWindow({ timetableResult, onClose }) {
  const [programs, setPrograms] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const draftLessonCount = Object.values(timetableResult?.assignments || {}).reduce(
    (total, slots) => total + Object.values(slots || {}).filter(Boolean).length,
    0
  );

  useEffect(() => {
    axios.get(`${API}/sablonlar`).then((response) => {
      const payload = getPayload(response);
      setPrograms(Array.isArray(payload) ? payload : asArray(payload.programs || payload.sablonlar || payload.list || payload.items));
    }).catch((error) => toast.error(scheduleErrorMessage(error, 'Program listesi alınamadı')));
  }, []);

  const formatProgramDate = (item) => {
    const rawDate = valueOf(item, ['createdAt', 'created_at', 'tarih', 'date'], '');
    if (!rawDate) return '-';
    const parsed = new Date(rawDate);
    return Number.isNaN(parsed.getTime()) ? String(rawDate) : parsed.toLocaleString('tr-TR');
  };

  const deleteProgram = async (item) => {
    const id = item?.id;
    if (id === undefined || id === null || id === '') return toast.error('Silinecek programın kimliği bulunamadı');
    const name = valueOf(item, ['name', 'ad'], 'bu program');
    if (!window.confirm(`${name} silinsin mi?`)) return;

    setDeletingId(String(id));
    try {
      const response = await axios.delete(`${API}/sablonlar/${encodeURIComponent(id)}`);
      getPayload(response);
      setPrograms(current => current.filter(program => String(program?.id) !== String(id)));
      toast.success('Kayıtlı program silindi');
    } catch (error) {
      toast.error(scheduleErrorMessage(error, 'Program silinemedi'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <WindowModal title="Programlar" width={860} onClose={onClose}>
      <div className="hp-current-draft-row">
        <strong>Şu An Çalışılan Program — TASLAK</strong>
        <span>{draftLessonCount} ders saati</span>
      </div>
      <DenseTable headers={['No', 'Program', 'Tarih', 'İşlem']} minWidth={620}>
        {programs.map((item, index) => (
          <tr key={item.id || index}>
            <td>{index + 1}</td>
            <td>{item.name || item.ad || `Program ${index + 1}`}</td>
            <td>{formatProgramDate(item)}</td>
            <td>
              <button
                className="hp-link-btn danger"
                type="button"
                disabled={deletingId === String(item.id)}
                onClick={() => deleteProgram(item)}
              >
                {deletingId === String(item.id) ? 'Siliniyor…' : 'Sil'}
              </button>
            </td>
          </tr>
        ))}
        {programs.length === 0 && <tr><td colSpan={4}>Kayıtlı eski program bulunamadı.</td></tr>}
      </DenseTable>
    </WindowModal>
  );
}

const styles = `
.hp-page { color:#111827; font-family: Tahoma, Arial, sans-serif; }
.pragmatic-page { min-height:calc(100vh - 96px); background:#d9d9d9; padding:0; }
.prag-shell { position:relative; min-height:760px; background:#f8f8f6; border:3px solid #1d222b; box-shadow:0 6px 18px rgba(0,0,0,.25); overflow:hidden; }
.prag-titlebar { height:28px; display:flex; align-items:center; gap:8px; padding:0 8px; background:linear-gradient(#55585c,#2f3339); color:#e8edf2; font-size:15px; font-weight:700; }
.prag-app-dot { width:17px; height:17px; display:inline-flex; align-items:center; justify-content:center; background:#dae8ff; color:#243a69; border:1px solid #8aa0bf; font-size:12px; }
.prag-window-controls { margin-left:auto; letter-spacing:8px; font-size:20px; line-height:1; }
.prag-content { display:grid; grid-template-columns:250px minmax(0,1fr); min-height:710px; padding:26px 20px 70px 18px; }
.prag-brand { display:flex; flex-direction:column; align-items:center; padding-top:10px; color:#111; text-align:center; }
.prag-asder-logo { width:132px; height:132px; object-fit:contain; margin-bottom:10px; filter:drop-shadow(0 10px 16px rgba(15,23,42,.16)); }
.prag-logo-text { font-size:36px; font-weight:900; letter-spacing:.2px; line-height:1.05; color:#2563eb; }
.prag-dna { margin-top:9px; max-width:210px; font-size:15px; letter-spacing:2px; text-transform:uppercase; color:#64748b; line-height:1.45; font-weight:800; }
.prag-version { color:#b03030; font-size:18px; font-weight:800; margin:4px 0 2px; }
.prag-brand a { color:#4f46e5; font-size:16px; text-decoration:underline; }
.prag-main { position:relative; min-width:0; }
.prag-top-row { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; min-height:115px; }
.prag-toolbar { display:flex; align-items:stretch; gap:8px; flex-wrap:wrap; }
.prag-toolbar-tile { width:106px; height:96px; border:2px solid #c9c9c9; background:linear-gradient(#f7f7f7,#dedede); color:#222; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; cursor:pointer; box-shadow:inset 0 1px 0 #fff; }
.prag-toolbar-tile.active { border-color:#4d87c8; box-shadow:inset 0 0 0 1px #97c2ef; }
.prag-toolbar-tile.muted { opacity:.55; }
.prag-toolbar-tile span { font-size:30px; color:#456a9c; }
.prag-toolbar-tile b { font-size:14px; line-height:1.1; text-align:center; white-space:normal; }
.prag-contact { width:116px; height:76px; border:2px solid #d0d0d0; background:linear-gradient(#fff,#dfdfdf); display:flex; align-items:center; justify-content:center; gap:8px; color:#222; font-size:26px; font-weight:800; cursor:pointer; }
.prag-contact span { font-size:16px; }
.prag-support { position:absolute; right:16px; top:54px; width:230px; height:48px; border:2px solid #ddd; border-radius:7px; background:#f5f5f5; color:#9a9a9a; font-size:15px; font-weight:900; opacity:.9; }
.prag-heading { margin:6px 0 0; text-align:center; color:#1d1d1d; font-size:26px; font-weight:900; text-decoration:underline; letter-spacing:.5px; }
.prag-heading span { font-weight:800; }
.prag-school { text-align:center; margin:20px 0 18px; font-size:22px; font-weight:900; letter-spacing:.7px; color:#161616; }
.prag-module-grid { display:grid; grid-template-columns:repeat(5, minmax(120px, 1fr)); column-gap:54px; row-gap:32px; align-items:start; padding:0 18px; }
.prag-module { min-height:150px; border:0; background:transparent; display:flex; flex-direction:column; align-items:center; justify-content:flex-start; gap:10px; color:#222; cursor:pointer; text-align:center; }
.prag-module:hover b { text-decoration:underline; }
.prag-module b { font-family:Georgia, 'Times New Roman', serif; font-size:18px; line-height:1.18; font-weight:800; text-shadow:0 1px 0 #fff; white-space:normal; }
.prag-module-icon { position:relative; width:112px; height:96px; display:flex; align-items:center; justify-content:center; border-radius:20px; background:linear-gradient(145deg,#f8fbff,#e7eef8); color:#2563eb; box-shadow:0 14px 26px rgba(15,23,42,.12), inset 0 1px 0 #fff; border:1px solid #dbe6f4; }
.prag-module-icon svg { width:54px; height:54px; filter:drop-shadow(0 5px 8px rgba(37,99,235,.18)); }
.prag-module-icon.class, .prag-module-icon.classSchedule { color:#0f9f8f; background:linear-gradient(145deg,#f5fffd,#dff7f3); }
.prag-module-icon.room, .prag-module-icon.roomSchedule { color:#64748b; background:linear-gradient(145deg,#fbfcfe,#e7ebf0); }
.prag-module-icon.teachers, .prag-module-icon.teacherSchedule, .prag-module-icon.assignment { color:#7c3aed; background:linear-gradient(145deg,#fbf8ff,#ede5ff); }
.prag-module-icon.daily, .prag-module-icon.duty { color:#e11d48; background:linear-gradient(145deg,#fff8fa,#ffe4eb); }
.prag-module-icon.classSubjects, .prag-module-icon.newTable { color:#0891b2; background:linear-gradient(145deg,#f3fcff,#dff6fb); }
.prag-module-icon.extra { color:#ea580c; background:linear-gradient(145deg,#fff9f2,#ffead2); }
.prag-module-icon.board { color:#b45309; background:linear-gradient(145deg,#fffaf0,#f7e5bd); }
.prag-module-icon.examSetup { color:#5b8dd4; background:linear-gradient(145deg,#f3f9ff,#dfefff); }
.prag-module-icon.backup { color:#0284c7; background:linear-gradient(145deg,#f3fbff,#dcedfb); }
.prag-bottom-left { position:absolute; left:14px; bottom:14px; }
.prag-bottom-left button { width:102px; height:48px; border:2px solid #d0d0d0; background:linear-gradient(#f8f8f8,#dedede); color:#333; font-size:12px; font-weight:800; }
.prag-bottom-actions { position:absolute; right:16px; bottom:16px; display:flex; gap:14px; }
.prag-bottom-actions button { min-width:210px; height:48px; border:2px solid #bdbdbd; background:linear-gradient(#f7f7f7,#dbdbdb); color:#1d1d1d; font-size:16px; font-weight:900; cursor:pointer; }
.pragmatic-warning { position:absolute; left:280px; right:280px; top:160px; z-index:1; background:#fff5d6; border:1px solid #d1b456; color:#7a4b00; padding:6px 10px; font-size:12px; }

.exam-desktop { min-height:650px; background:#fff; padding:20px 24px 72px; position:relative; color:#111; }
.exam-toolbar { display:flex; align-items:flex-start; gap:8px; min-height:118px; }
.exam-brand { width:190px; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding-top:4px; }
.exam-brand img { width:78px; height:78px; object-fit:contain; filter:drop-shadow(0 8px 12px rgba(15,23,42,.14)); }
.exam-brand strong { margin-top:6px; font-size:32px; line-height:1; color:#2563eb; font-weight:900; letter-spacing:-.02em; }
.exam-brand span { margin-top:5px; font-size:13px; color:#64748b; font-weight:900; letter-spacing:1.8px; text-transform:uppercase; }
.exam-contact { margin-left:auto; width:116px; height:70px; border:2px solid #d0d0d0; background:linear-gradient(#fff,#dfdfdf); color:#222; font-size:15px; line-height:1.15; font-weight:900; cursor:pointer; }
.exam-desktop h2 { margin:0 0 24px; text-align:center; font-family:Georgia,'Times New Roman',serif; font-size:25px; font-weight:900; letter-spacing:.4px; }
.exam-module-grid { display:grid; grid-template-columns:repeat(3, minmax(150px, 1fr)); justify-items:center; gap:30px 74px; padding:0 110px; }
.exam-module { width:170px; min-height:142px; border:0; background:transparent; cursor:pointer; display:flex; flex-direction:column; align-items:center; justify-content:flex-start; gap:11px; color:#222; text-align:center; }
.exam-module:hover b { text-decoration:underline; }
.exam-module b { font-family:Georgia,'Times New Roman',serif; font-size:18px; line-height:1.16; font-weight:900; text-shadow:0 1px 0 #fff; }
.exam-module-icon { width:92px; height:86px; border-radius:18px; display:flex; align-items:center; justify-content:center; box-shadow:0 12px 24px rgba(15,23,42,.12), inset 0 1px 0 #fff; border:1px solid #dbe6f4; background:#f1f5f9; color:#2563eb; }
.exam-module-icon svg { width:48px; height:48px; filter:drop-shadow(0 5px 8px rgba(37,99,235,.16)); }
.exam-module-icon.dersler { color:#0891b2; background:linear-gradient(145deg,#f3fcff,#dff6fb); }
.exam-module-icon.siniflar { color:#7c3aed; background:linear-gradient(145deg,#fbf8ff,#ede5ff); }
.exam-module-icon.ogrenciler { color:#e11d48; background:linear-gradient(145deg,#fff8fa,#ffe4eb); }
.exam-module-icon.atama { color:#f59e0b; background:linear-gradient(145deg,#fffbeb,#fef3c7); }
.exam-module-icon.ogretmenler { color:#4f46e5; background:linear-gradient(145deg,#eef2ff,#e0e7ff); }
.exam-module-icon.salonlar { color:#10b981; background:linear-gradient(145deg,#f0fdf4,#dcfce7); }
.exam-module-icon.sinav { color:#2563eb; background:linear-gradient(145deg,#eff6ff,#dbeafe); }
.exam-module-icon.sinavmatik { color:#e8334a; background:linear-gradient(145deg,#fff1f2,#ffe4e6); }
.exam-module-icon.yedek { color:#0284c7; background:linear-gradient(145deg,#f3fbff,#dcedfb); }
.exam-bottom-actions { position:absolute; right:24px; bottom:20px; display:flex; gap:12px; }
.exam-bottom-actions button { min-width:190px; height:46px; border:2px solid #bdbdbd; background:linear-gradient(#f7f7f7,#dbdbdb); color:#1d1d1d; font-size:15px; font-weight:900; cursor:pointer; }
.exam-manager-layer { position:fixed; inset:0; z-index:10000; display:flex; align-items:center; justify-content:center; padding:16px; background:rgba(15,23,42,.72); overflow:hidden; isolation:isolate; }
.exam-manager-window { width:min(1280px, calc(100vw - 32px)); height:min(720px, calc(100vh - 32px)); max-width:1280px; max-height:calc(100vh - 32px); min-height:0; background:#dcdedb; border:4px solid #151b24; box-shadow:0 16px 38px rgba(0,0,0,.36); display:flex; flex-direction:column; overflow:hidden; }
.exam-manager-body { position:relative; flex:1; display:flex; flex-direction:column; gap:10px; padding:10px; overflow:hidden; }
.exam-columns { flex:1; min-height:0; display:grid; grid-template-columns:minmax(360px,2.2fr) minmax(130px,.75fr) minmax(220px,1.25fr) minmax(330px,1.65fr) minmax(150px,.85fr); gap:8px; overflow:hidden; }
.exam-panel { min-width:0; display:flex; flex-direction:column; gap:5px; overflow:hidden; }
.exam-panel h3 { margin:0; height:24px; display:flex; align-items:center; justify-content:center; color:#222; font-size:13px; font-weight:900; text-transform:uppercase; }
.exam-panel .hp-table-wrap { flex:1; min-height:0; max-width:100%; overflow:auto; border:1px solid #b8bdc1; background:#f7f7f7; }
.exam-panel .hp-table { background:#f7f7f7; table-layout:fixed; }
.exam-panel .hp-table th { position:sticky; top:0; z-index:1; background:#a9d0ed; color:#34495e; border-color:#d7d7d7; height:26px; text-align:center; }
.exam-panel .hp-table td { background:#ffffff; border-color:#e5e7eb; color:#5d6270; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.exam-panel .hp-table tr:nth-child(even) td { background:#c8c8c8; color:#444; }
.exam-panel .hp-table tr { cursor:pointer; }
.exam-panel .hp-table tr.selected td { background:#0717f0 !important; color:#fff; }
.exam-panel .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.exam-manager-actions { flex:0 0 auto; min-height:48px; display:flex; align-items:center; gap:8px; overflow-x:auto; overflow-y:hidden; padding:2px 0 4px; }
.exam-manager-actions button { flex:0 0 auto; min-width:102px; min-height:42px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#cfd3d5); color:#222; font-size:12px; font-weight:900; cursor:pointer; box-shadow:inset 0 1px 0 #fff; border-radius:4px; white-space:nowrap; }
.exam-nested-layer { position:fixed; inset:0; z-index:10100; display:flex; align-items:center; justify-content:center; background:rgba(15,23,42,.62); padding:16px; overflow:auto; }
.exam-small-dialog, .exam-student-dialog, .exam-room-dialog { background:#dcdedb; border:4px solid #151b24; box-shadow:0 14px 32px rgba(0,0,0,.38); overflow:hidden; }
.exam-small-dialog { width:430px; }
.exam-form-body { padding:16px; background:#e9e5e1; }
.exam-form-body .hp-field { grid-template-columns:100px 1fr; margin-bottom:12px; }
.exam-form-body .hp-field input { background:#dfe5e7; border:2px solid #a9b1b8; min-height:28px; }
.exam-dialog-actions { display:flex; justify-content:flex-end; gap:18px; padding-top:18px; }
.exam-dialog-actions button, .exam-room-actions button, .exam-student-actions button { min-width:130px; min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#cfd3d5); color:#222; font-size:13px; font-weight:900; cursor:pointer; box-shadow:inset 0 1px 0 #fff; border-radius:5px; }
.exam-student-dialog { width:min(1120px, calc(100vw - 64px)); max-height:calc(100vh - 92px); }
.exam-student-body { display:grid; grid-template-columns:230px 430px minmax(360px,1fr); grid-template-rows:minmax(500px, 1fr) 56px; gap:10px; padding:10px; min-height:min(640px, calc(100vh - 142px)); background:#e9e5e1; overflow:hidden; }
.exam-student-left { min-width:0; display:flex; flex-direction:column; gap:14px; }
.exam-student-left .exam-panel:first-child .hp-table-wrap { max-height:160px; }
.exam-student-left .exam-panel:last-child { flex:1; }
.exam-student-subjects, .exam-student-participants { min-width:0; }
.exam-student-body .hp-table-wrap { border:1px solid #b8bdc1; background:#f7f7f7; max-width:100%; }
.exam-student-body .hp-table { table-layout:fixed; }
.exam-student-body .hp-table th { position:sticky; top:0; z-index:1; background:#a9d0ed; color:#34495e; border-color:#d7d7d7; height:26px; text-align:center; }
.exam-student-body .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.exam-student-body .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.exam-student-body .hp-table tr.selected td { background:#0717f0 !important; color:#fff; }
.exam-student-body .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.exam-student-check { align-self:center; }
.exam-student-total { display:flex; align-items:center; justify-content:center; gap:12px; color:#4b5563; font-size:12px; font-weight:900; }
.exam-student-total strong { min-width:70px; height:28px; display:inline-flex; align-items:center; justify-content:center; background:#f1f5f9; border:2px solid #c4c8cc; color:#444; }
.exam-student-actions { display:flex; justify-content:flex-end; gap:18px; align-items:center; }
.exam-room-dialog { width:min(1180px, calc(100vw - 32px)); max-width:1180px; max-height:calc(100vh - 32px); display:flex; flex-direction:column; }
.exam-room-body { min-height:0; padding:10px; background:#e9e5e1; overflow:auto; }
.exam-room-layout { display:grid; grid-template-columns:minmax(340px,420px) minmax(0,1fr); gap:12px; height:min(620px, calc(100vh - 92px)); min-height:0; }
.exam-room-list-side, .exam-room-plan-side { min-width:0; min-height:0; }
.exam-room-body .hp-table-wrap { max-height:430px; min-height:380px; border:1px solid #b8bdc1; background:#f7f7f7; }
.exam-room-body .hp-table { table-layout:fixed; }
.exam-room-body .hp-table th { position:sticky; top:0; z-index:1; background:#a9d0ed; color:#34495e; border-color:#d7d7d7; height:26px; text-align:center; }
.exam-room-body .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.exam-room-body .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.exam-room-body .hp-table tr.selected td { background:#0717f0 !important; color:#fff; }
.exam-room-body .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.exam-room-actions { display:grid; grid-template-columns:1fr 1fr 1fr; gap:12px; padding-top:12px; }
.exam-room-actions button:last-child { grid-column:3; }
.room-check { width:100%; min-height:22px; border:0; background:transparent; color:inherit; font-weight:900; cursor:pointer; }
.exam-room-plan-side { display:flex; flex-direction:column; gap:8px; padding:8px; border:1px solid #9aa6ad; background:#d7d4ce; }
.exam-room-plan-head { display:flex; align-items:center; justify-content:space-between; gap:14px; min-height:36px; padding:0 10px; color:#263647; font-size:15px; font-weight:900; }
.exam-room-plan-head span { min-width:110px; text-align:center; padding:5px 8px; border:2px solid #b9c0c8; background:#f4f4f4; color:#333; }
.room-plan-cols { display:grid; grid-template-columns:34px repeat(6, 1fr); background:#9fc9e7; border:1px solid #668ca8; border-bottom:0; color:#25384a; font-size:13px; font-weight:900; text-align:center; }
.room-plan-cols b, .room-plan-cols span { min-height:26px; display:flex; align-items:center; justify-content:center; border-right:1px solid #6e94b0; }
.room-plan-grid { flex:1; min-width:max-content; min-height:0; display:grid; grid-auto-rows:minmax(60px, 1fr); grid-auto-columns:minmax(60px,1fr); border:1px solid #668ca8; background:#9fc9e7; overflow:auto; }
.room-row-label { display:flex; align-items:center; justify-content:center; border-right:1px solid #6e94b0; border-bottom:1px solid #6e94b0; color:#25384a; font-size:13px; }
.room-plan-cell { border:1px solid rgba(68,102,91,.64); background:#bee0bc; box-shadow:inset 0 0 0 1px rgba(255,255,255,.25); }
.room-plan-cell:nth-child(7n+4), .room-plan-cell:nth-child(7n+6) { border-left:3px solid #4b83d1; }
.room-plan-cell.blocked { background:#e71323; border-color:#a80913; }
.room-plan-tools { min-height:42px; display:flex; align-items:center; justify-content:center; gap:22px; color:#333; font-size:12px; font-weight:800; }
.room-plan-tools span { color:#555; }

.exam-list-panel .hp-table th:nth-child(1), .exam-list-panel .hp-table td:nth-child(1) { width:42px; }
.exam-list-panel .hp-table th:nth-child(2), .exam-list-panel .hp-table td:nth-child(2) { width:104px; }
.exam-list-panel .hp-table th:nth-child(3), .exam-list-panel .hp-table td:nth-child(3) { width:78px; }
.exam-list-panel .hp-table th:nth-child(5), .exam-list-panel .hp-table td:nth-child(5),
.exam-list-panel .hp-table th:nth-child(6), .exam-list-panel .hp-table td:nth-child(6) { width:54px; }
.exam-panel .hp-table td, .exam-student-body .hp-table td, .exam-room-body .hp-table td { overflow:hidden; text-overflow:ellipsis; }
.exam-student-subjects .hp-table th:nth-child(1), .exam-student-subjects .hp-table td:nth-child(1) { width:54px; }
.exam-student-participants .hp-table th:nth-child(3), .exam-student-participants .hp-table td:nth-child(3) { width:64px; text-align:center; }
.exam-room-body .hp-table th:nth-child(1), .exam-room-body .hp-table td:nth-child(1) { width:46px; }
.exam-room-body .hp-table th:nth-child(4), .exam-room-body .hp-table td:nth-child(4) { width:62px; text-align:center; }
.exam-room-body .hp-table th:nth-child(5), .exam-room-body .hp-table td:nth-child(5) { width:44px; text-align:center; }

.sinavmatik-layer { background:rgba(15,23,42,.76); }
.sinavmatik-window { width:min(1420px, calc(100vw - 32px)); height:min(820px, calc(100vh - 32px)); max-width:1420px; max-height:calc(100vh - 32px); background:#e8e4df; border:4px solid #151b24; box-shadow:0 18px 42px rgba(0,0,0,.4); display:flex; flex-direction:column; overflow:hidden; }
.sinavmatik-body { flex:1; min-height:0; display:grid; grid-template-columns:minmax(360px,420px) minmax(0,1fr); gap:10px; padding:10px; overflow:hidden; }
.sinavmatik-left, .sinavmatik-right { min-width:0; min-height:0; display:flex; flex-direction:column; gap:8px; overflow:hidden; }
.sinavmatik-top-lists { display:grid; grid-template-columns:minmax(330px,1fr) 150px; gap:8px; min-height:220px; max-height:230px; }
.sinavmatik-left > .exam-panel { flex:1; min-height:0; }
.sinavmatik-left .hp-table-wrap, .sinavmatik-dialog .hp-table-wrap { border:1px solid #b8bdc1; background:#f7f7f7; }
.sinavmatik-left .hp-table, .sinavmatik-dialog .hp-table { table-layout:fixed; }
.sinavmatik-left .hp-table th, .sinavmatik-dialog .hp-table th { position:sticky; top:0; z-index:1; background:#a9d0ed; color:#34495e; border-color:#d7d7d7; height:26px; text-align:center; }
.sinavmatik-left .hp-table td, .sinavmatik-dialog .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.sinavmatik-left .hp-table tr:nth-child(even) td, .sinavmatik-dialog .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.sinavmatik-left .hp-table tr.selected td, .sinavmatik-dialog .hp-table tr.selected td { background:#0717f0 !important; color:#fff; }
.sinavmatik-left .hp-table tr:hover td, .sinavmatik-dialog .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.sinavmatik-extra-actions { display:grid; grid-template-columns:1fr 1.5fr; gap:8px; }
.sinavmatik-extra-actions button, .sinavmatik-actions button, .sinavmatik-dialog-actions button { min-height:40px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#cfd3d5); color:#222; font-size:12px; font-weight:900; cursor:pointer; box-shadow:inset 0 1px 0 #fff; border-radius:5px; white-space:nowrap; }
.sinavmatik-extra-actions button:disabled, .sinavmatik-actions button:disabled { color:#9aa1aa; cursor:not-allowed; opacity:.72; }
.sinavmatik-counters { height:36px; display:grid; grid-template-columns:repeat(3, auto) 90px; gap:14px; align-items:center; justify-content:start; color:#333; font-size:13px; font-weight:900; }
.sinavmatik-counters strong, .sinavmatik-counters input { min-width:72px; height:28px; display:inline-flex; align-items:center; justify-content:center; margin-left:6px; padding:0 8px; background:#f2f2f2; border:2px solid #c7c7c7; color:#333; text-align:center; font-weight:900; }
.sinavmatik-seat-grid { flex:1; min-width:0; min-height:0; display:grid; grid-auto-rows:minmax(72px, 1fr); gap:4px; padding:8px; background:#dff0dc; border:2px solid #b8d2b6; overflow:auto; }
.sinav-seat { position:relative; min-width:60px; min-height:68px; padding:6px; border:1px solid rgba(255,255,255,.85); border-right-color:#7898b7; border-bottom-color:#7898b7; background:#c9e7c6; color:#203040; text-align:left; cursor:pointer; overflow:hidden; }
.sinav-aisle { min-width:24px; min-height:78px; background:repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(86,112,139,.28) 10px, rgba(86,112,139,.28) 12px); border-left:2px solid rgba(60,92,135,.35); border-right:2px solid rgba(60,92,135,.35); }
.sinav-seat:nth-child(7n+3), .sinav-seat:nth-child(7n+6) { border-left:2px solid #687fca; }
.sinav-seat.active { outline:3px solid #ffe86a; outline-offset:-3px; }
.sinav-seat.blocked { background:#ff1212; color:#fff; }
.sinav-seat.filled { background:#c9e7c6; color:#253448; }
.sinav-seat.fixed { background:#a20d0d; color:#fff565; }
.sinav-seat.constraint-violated { background:#fed7aa; color:#9a3412; border:2px solid #ea580c; box-shadow:inset 0 0 0 2px rgba(220,38,38,.28); }
.sinav-seat.constraint-violated.fixed { outline:3px solid #f97316; outline-offset:-3px; }
.sinav-seat b, .sinav-seat span, .sinav-seat em { display:block; max-width:100%; font-style:normal; line-height:1.15; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.sinav-seat b { font-size:clamp(8px, .72vw, 12px); font-weight:900; color:inherit; }
.sinav-seat span { margin-top:6px; font-size:clamp(7px, .65vw, 11px); font-weight:900; }
.sinav-seat em { margin-top:10px; font-size:10px; font-weight:900; }
.sinavmatik-unplaced-alert { flex:0 0 auto; display:grid; gap:3px; padding:8px 10px; border:1px solid #b91c1c; border-radius:4px; background:#fee2e2; color:#991b1b; font-size:12px; }
.sinavmatik-unplaced-alert span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.sinavmatik-actions { display:grid; grid-template-columns:repeat(9, minmax(108px,1fr)); gap:8px; padding:9px 10px 10px; border-top:2px solid #c9c9c9; background:#e8e4df; }
.sinavmatik-dialog { background:#e8e4df; border:4px solid #151b24; box-shadow:0 16px 36px rgba(0,0,0,.42); overflow:hidden; }
.sinavmatik-settings { width:min(760px, calc(100vw - 72px)); }
.sinavmatik-settings-body { display:grid; grid-template-columns:1fr 1.35fr; gap:18px; padding:22px 24px; color:#333; }
.sinavmatik-settings-body h3 { margin:0 0 14px; text-align:center; font-size:16px; font-weight:900; }
.seat-rule-grid { display:grid; grid-auto-rows:58px; gap:8px; justify-content:center; }
.seat-rule-grid button { border:2px solid #5c765f; background:#bcd6b9; cursor:pointer; }
.seat-rule-grid button.blocked { background:#8b0808; }
.seat-rule-grid button.student { background:#f3f0ea; font-size:25px; cursor:default; }
.double-seat-rules { display:flex; gap:36px; justify-content:center; }
.sinavmatik-settings-body label { grid-column:1 / -1; display:flex; align-items:center; gap:8px; font-size:14px; font-weight:800; }
.sinavmatik-dialog-actions { display:flex; justify-content:flex-end; gap:14px; padding:12px 18px 18px; }
.sinavmatik-settings .sinavmatik-dialog-actions { grid-column:1 / -1; padding:4px 0 0; }
.sinav-commission { width:min(1050px, calc(100vw - 72px)); max-height:calc(100vh - 96px); }
.sinav-commission-body { display:grid; grid-template-columns:420px minmax(300px,1fr) 230px; gap:12px; min-height:430px; padding:14px; overflow:hidden; }
.sinav-commission-body .exam-panel { min-height:0; }
.sinav-observer { width:min(980px, calc(100vw - 72px)); max-height:calc(100vh - 96px); }
.sinav-observer-head { display:flex; justify-content:center; gap:80px; align-items:center; padding:14px 18px 8px; color:#333; font-size:13px; font-weight:900; }
.sinav-observer-head input { height:28px; min-width:200px; border:2px solid #b9b9b9; background:#f3f3f3; text-align:center; font-weight:900; }
.sinav-observer-head label input { min-width:70px; margin-left:10px; }
.sinav-observer-body { display:grid; grid-template-columns:minmax(520px,1fr) 280px; gap:14px; min-height:430px; padding:0 14px 10px; overflow:hidden; }
.sinav-observer-body .exam-panel { min-height:0; }
.sinav-print-menu { width:390px; }
.sinav-print-menu-body { display:flex; flex-direction:column; gap:10px; padding:14px 16px 18px; background:#e8e4df; }
.sinav-print-menu-body button { min-height:46px; border:1px solid #d5d5d5; background:linear-gradient(#fbfbfb,#e6e6e6); color:#333; font-size:14px; font-weight:900; cursor:pointer; border-radius:5px; box-shadow:inset 0 1px 0 #fff; }
.sinav-print-menu-body button.close { background:linear-gradient(#ffb18b,#ff8a5a); }
.sinav-paper-print { width:min(1040px, calc(100vw - 64px)); height:min(560px, calc(100vh - 86px)); max-height:calc(100vh - 86px); background:#e8e4df; display:flex; flex-direction:column; }
.sinav-paper-body { flex:1; min-height:0; display:grid; grid-template-columns:minmax(0,1fr) 190px; gap:10px; padding:12px; overflow:hidden; }
.sinav-paper-main-table, .sinav-paper-room-table { min-height:0; overflow:hidden; }
.sinav-paper-print .hp-table-wrap { height:100%; min-height:0; border:1px solid #b8bdc1; background:#f7f7f7; }
.sinav-paper-print .hp-table { table-layout:fixed; }
.sinav-paper-print .hp-table th { position:sticky; top:0; z-index:1; background:#a9d0ed; color:#34495e; border-color:#d7d7d7; height:27px; text-align:center; }
.sinav-paper-print .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:26px; padding:3px 7px; font-size:12px; font-weight:800; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.sinav-paper-print .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.sinav-paper-print .hp-table tr.selected td { background:#0717f0 !important; color:#fff; }
.sinav-paper-print .hp-table tr.muted td { color:#8a8a8a; }
.sinav-paper-room-actions { display:grid; grid-template-columns:1fr 1fr; gap:8px; padding-top:10px; }
.sinav-paper-room-actions button, .sinav-paper-actions button, .sinav-paper-actions .file-button { min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#cfd3d5); color:#222; font-size:12px; font-weight:900; cursor:pointer; box-shadow:inset 0 1px 0 #fff; border-radius:5px; white-space:nowrap; display:flex; align-items:center; justify-content:center; }
.sinav-paper-actions { flex:0 0 auto; display:grid; grid-template-columns:repeat(6, minmax(104px,1fr)); gap:8px; padding:0 12px 8px; overflow-x:auto; overflow-y:hidden; }
.sinav-paper-actions .file-button input { display:none; }
.sinav-paper-hint { flex:0 0 auto; padding:0 14px 12px; color:#333; font-size:12px; font-weight:900; }
.sinav-paper-hint span { margin-left:10px; font-weight:800; }
.sinav-paper-main-table .hp-table th:nth-child(1), .sinav-paper-main-table .hp-table td:nth-child(1) { width:42px; }
.sinav-paper-main-table .hp-table th:nth-child(2), .sinav-paper-main-table .hp-table td:nth-child(2) { width:70px; }
.sinav-paper-room-table .hp-table th:nth-child(2), .sinav-paper-room-table .hp-table td:nth-child(2) { width:44px; text-align:center; }

.prag-module-icon .block { position:absolute; width:28px; height:68px; border-radius:4px; transform:skewY(-18deg); box-shadow:0 3px 5px rgba(0,0,0,.16); }
.prag-module-icon .block.blue { left:16px; bottom:8px; background:#4aa3ff; }
.prag-module-icon .block.red { left:50px; bottom:14px; background:#ff6683; height:56px; }
.prag-module-icon .block.green { left:82px; bottom:26px; background:#18d4ac; height:62px; }
.prag-module-icon .block.cap { left:38px; top:0; width:70px; height:30px; background:#2c3b78; transform:rotate(10deg); border-radius:8px 8px 4px 4px; }
.prag-module-icon .board { position:absolute; left:24px; top:16px; width:70px; height:42px; background:#92c765; border:4px solid #805845; border-radius:4px; }
.prag-module-icon .board.small { top:12px; left:38px; width:60px; height:34px; }
.prag-module-icon .head { position:absolute; bottom:8px; width:25px; height:25px; background:#f7d6ad; border-radius:50%; box-shadow:0 22px 0 #59616c; }
.prag-module-icon .head.one { left:22px; }
.prag-module-icon .head.two { left:52px; }
.prag-module-icon .head.three { left:82px; }
.prag-module-icon .cube { position:absolute; width:42px; height:42px; background:#9aa4b2; transform:skewY(-25deg); box-shadow:inset 12px 0 #636e7e; }
.prag-module-icon .cube.a { left:18px; top:30px; }
.prag-module-icon .cube.b { left:50px; top:18px; background:#b7e25c; }
.prag-module-icon .cube.c { left:78px; top:28px; background:#ffd45a; }
.prag-module-icon .avatar { position:absolute; width:48px; height:48px; border-radius:50%; box-shadow:0 24px 0 -8px currentColor; }
.prag-module-icon .avatar.blue { left:36px; top:0; background:#79c9f8; color:#2483c6; }
.prag-module-icon .avatar.green { left:14px; top:38px; background:#daf08c; color:#99bf29; }
.prag-module-icon .avatar.red { left:68px; top:38px; background:#ff8b9a; color:#e5495f; }
.prag-module-icon .book { position:absolute; left:26px; top:24px; width:58px; height:54px; background:#2d9ee4; border-radius:5px; box-shadow:inset 0 -16px #ffd55b; }
.prag-module-icon .clock { position:absolute; right:18px; top:5px; width:42px; height:42px; border-radius:50%; background:#ffd94a; border:4px solid #f05a78; }
.prag-module-icon .teacher { position:absolute; left:14px; top:18px; width:26px; height:26px; background:#f6d2a5; border-radius:50%; box-shadow:0 26px 0 #72a5cb; }
.prag-module-icon .screen, .prag-module-icon .monitor { position:absolute; left:18px; top:18px; width:82px; height:56px; background:#31c9c7; border-radius:5px; box-shadow:0 9px 0 #4b6079; }
.prag-module-icon .teacher.mini, .prag-module-icon .person { position:absolute; right:18px; top:8px; width:20px; height:42px; background:#f4dc47; border-radius:10px 10px 3px 3px; }
.prag-module-icon .calendar { position:absolute; left:24px; top:12px; width:72px; height:68px; background:#8fd5dc; border-radius:5px; box-shadow:inset 0 16px #456c98; }
.prag-module-icon .calendar:after { content:''; position:absolute; inset:24px 8px 8px; background:repeating-linear-gradient(90deg,#fff 0 9px,transparent 9px 14px), repeating-linear-gradient(#fff 0 8px,transparent 8px 14px); opacity:.8; }
.prag-module-icon .building { position:absolute; left:20px; top:8px; width:80px; height:78px; background:#2e78bd; clip-path:polygon(50% 0,100% 22%,78% 100%,22% 100%,0 22%); }
.prag-module-icon .building:after { content:''; position:absolute; left:34px; top:22px; width:16px; height:56px; background:#9dccff; box-shadow:-24px -2px #71a7db,24px -2px #71a7db; }
.prag-module-icon .paper { position:absolute; left:20px; top:22px; width:76px; height:56px; background:#f1f5f9; border-radius:8px; box-shadow:inset 12px 0 #dbe7f3; }
.prag-module-icon .plus { position:absolute; right:18px; top:6px; width:36px; height:36px; background:#2fc9b4; border-radius:50%; color:#fff; }
.prag-module-icon .plus:before { content:'+'; position:absolute; inset:0; text-align:center; line-height:36px; font-size:28px; font-weight:900; }
.prag-module-icon .cap-mini { position:absolute; right:12px; bottom:8px; width:54px; height:38px; background:#344b79; clip-path:polygon(50% 0,100% 35%,50% 70%,0 35%); }
.prag-module-icon .math { position:absolute; left:20px; top:24px; width:78px; height:52px; color:#ff9a34; background:#eaf6ff; border-radius:4px; font-size:30px; font-weight:900; line-height:52px; }
.prag-module-icon .notice { position:absolute; left:20px; top:12px; width:78px; height:66px; background:#c58d50; transform:skewY(-15deg); box-shadow:inset 8px 8px #f4d48a; }
.prag-module-icon .cloud { position:absolute; left:24px; top:26px; width:70px; height:44px; background:#e7e8ea; border-radius:30px; }
.prag-module-icon .sync { position:absolute; left:32px; top:44px; width:58px; height:40px; border:10px solid #2b9ed8; border-left-color:#e94e4e; border-radius:50%; }

.hp-modal-backdrop, .hp-nested-modal { position:fixed; inset:0; z-index:1000; background:rgba(0,0,0,.04); display:flex; align-items:center; justify-content:center; padding:18px; }
.hp-nested-modal { z-index:1002; background:rgba(0,0,0,.12); }
.hp-window { width:100%; max-height:91vh; background:#dcdedb; border:4px solid #151b24; box-shadow:0 16px 38px rgba(0,0,0,.36); display:flex; flex-direction:column; overflow:hidden; }
.hp-window.small { max-width:900px; }
.hp-window-titlebar { min-height:27px; display:flex; align-items:center; justify-content:space-between; gap:10px; padding:0 7px; background:linear-gradient(#3f444a,#20252c); border-bottom:2px solid #11151b; color:#e8edf2; }
.hp-window-titlebar strong { color:#e8edf2; font-size:13px; text-transform:uppercase; letter-spacing:.2px; }
.hp-window-titlebar span { display:none; }
.hp-icon-btn, .hp-window-titlebar button { width:22px; height:22px; border:0; background:transparent; color:#e8edf2; cursor:pointer; font-weight:900; font-size:17px; }
.hp-window-body { padding:10px; overflow:auto; background:#dcdedb; }
.hp-window-grid { display:grid; gap:10px; }
.hp-window-grid.two { grid-template-columns:320px minmax(0,1fr); }
.hp-window-grid.two.narrow { grid-template-columns:250px minmax(0,1fr); }
.hp-window-grid.three { grid-template-columns:210px minmax(0,1fr) minmax(0,1fr); }
.hp-window-grid.settings { grid-template-columns:230px minmax(0,1fr); }
.hp-class-subjects { position:relative; display:grid; grid-template-columns:260px minmax(420px,1fr) 370px; grid-template-rows:minmax(520px,1fr) auto; gap:10px; align-items:stretch; }
.hp-class-col, .hp-assigned-col, .hp-subject-col { min-width:0; overflow:auto; background:#dcdedb; }
.hp-class-subjects .hp-table-wrap { max-height:535px; border:1px solid #b8bdc1; background:#f7f7f7; }
.hp-class-subjects .hp-table { background:#f7f7f7; }
.hp-class-subjects .hp-table th { position:sticky; top:0; z-index:1; background:#6d6d6d; color:#fff; border-color:#d7d7d7; height:28px; }
.hp-class-subjects .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.hp-class-subjects .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.hp-class-subjects .hp-table tr { cursor:pointer; }
.hp-class-subjects .hp-table tr.selected td { background:#4048f2 !important; color:#fff; }
.hp-class-subjects .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.hp-class-subjects .hp-link-btn { margin:0; white-space:nowrap; }
.hp-class-subject-actions { grid-column:1 / -1; display:grid; grid-template-columns:190px 190px 170px 270px 160px; justify-content:center; gap:16px; padding:0 8px; }
.hp-class-subject-actions .hp-wide-btn { min-height:38px; }
.hp-class-subject-actions .hp-wide-btn:disabled { opacity:.45; cursor:not-allowed; }
.hp-class-subject-status { grid-column:1 / -1; display:grid; grid-template-columns:180px 260px 130px 210px minmax(160px,1fr) minmax(180px,1fr); align-items:center; gap:10px; min-height:24px; padding:2px 4px 0; color:#57606a; font-size:12px; font-weight:800; }
.hp-class-subject-status strong { color:#2b3138; }
.hp-teacherless-plan { display:block; max-width:100%; color:#4b5563; font-size:11px; font-weight:800; white-space:normal; line-height:1.15; }
.hp-empty-cell { text-align:center !important; color:#6b7280 !important; background:#f8f8f8 !important; height:70px !important; }
.hp-copy-dialog { position:absolute; inset:0; z-index:3; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.06); }
.hp-copy-window { width:520px; max-width:calc(100vw - 48px); background:#dcdedb; border:4px solid #151b24; box-shadow:0 12px 28px rgba(0,0,0,.36); }
.hp-copy-body { display:grid; grid-template-columns:220px minmax(0,1fr); gap:12px; padding:10px; min-height:320px; }
.hp-copy-list .hp-table-wrap { max-height:300px; }
.hp-copy-summary { display:flex; flex-direction:column; justify-content:center; gap:18px; border:2px solid #b8bdc1; background:#ece9e6; padding:22px; color:#4b5563; font-size:13px; font-weight:800; }
.hp-copy-summary div { display:grid; grid-template-columns:1fr 110px; gap:16px; align-items:center; }
.hp-copy-summary strong { color:#384150; }
.hp-copy-summary small { color:#6b7280; font-size:12px; }
.hp-copy-summary .hp-wide-btn { align-self:flex-end; width:140px; min-height:36px; }
.hp-common-dialog { position:absolute; inset:0; z-index:4; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.08); }
.hp-common-window { width:940px; max-width:calc(100vw - 48px); max-height:calc(100vh - 92px); background:#dcdedb; border:4px solid #151b24; box-shadow:0 14px 32px rgba(0,0,0,.38); display:flex; flex-direction:column; overflow:hidden; }
.hp-common-body { display:grid; grid-template-columns:330px minmax(290px,1fr) 330px; gap:10px; padding:10px; min-height:590px; overflow:auto; }
.hp-common-left, .hp-common-middle, .hp-common-right { min-width:0; display:flex; flex-direction:column; gap:14px; }
.hp-common-left .hp-table-wrap:first-child { max-height:285px; }
.hp-common-left .hp-table-wrap:last-child { max-height:310px; }
.hp-common-middle .hp-table-wrap, .hp-common-right .hp-table-wrap { max-height:390px; min-height:300px; }
.hp-common-middle { justify-content:flex-start; padding-top:0; }
.hp-common-middle > .hp-wide-btn { align-self:center; width:150px; margin-top:4px; }
.hp-common-actions { margin-top:auto; display:flex; justify-content:flex-end; gap:24px; padding-top:14px; }
.hp-common-actions .hp-wide-btn { width:140px; min-height:38px; }
.hp-teacher-assign-layout { display:grid; grid-template-columns:minmax(420px,1.15fr) minmax(300px,.85fr) minmax(380px,1fr); gap:10px; min-height:590px; }
.hp-teacher-left, .hp-teacher-subjects, .hp-teacher-classes { min-width:0; display:flex; flex-direction:column; gap:14px; }
.hp-teacher-left .hp-table-wrap:first-of-type { max-height:265px; }
.hp-teacher-left .hp-table-wrap:last-of-type { max-height:330px; min-height:240px; }
.hp-teacher-subjects .hp-table-wrap, .hp-teacher-classes .hp-table-wrap { max-height:610px; min-height:420px; }
.hp-teacher-assign-layout .hp-table-wrap { border:1px solid #b8bdc1; background:#f7f7f7; }
.hp-teacher-assign-layout .hp-table th { position:sticky; top:0; z-index:1; background:#6d6d6d; color:#fff; border-color:#d7d7d7; height:28px; }
.hp-teacher-assign-layout .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.hp-teacher-assign-layout .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.hp-teacher-assign-layout .hp-table tr { cursor:pointer; }
.hp-teacher-assign-layout .hp-table tr.selected td { background:#4048f2 !important; color:#fff; }
.hp-teacher-assign-layout .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.hp-teacher-assign-actions { display:grid; grid-template-columns:130px 130px 150px 170px 220px 120px 120px; justify-content:center; gap:12px; padding:12px 8px 0; }
.hp-teacher-assign-actions .hp-wide-btn { min-height:38px; }
.hp-teacher-assign-actions .hp-wide-btn:disabled { opacity:.45; cursor:not-allowed; }
.hp-teacher-assign-status { display:grid; grid-template-columns:140px 140px 170px 120px 210px minmax(200px,1fr); align-items:center; gap:10px; min-height:24px; padding:8px 4px 0; color:#57606a; font-size:12px; font-weight:800; }
.hp-teacher-assign-status strong { color:#2b3138; }
.hp-empty-lessons-dialog { position:absolute; inset:0; z-index:5; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.08); }
.hp-empty-lessons-window { width:440px; max-width:calc(100vw - 48px); background:#dcdedb; border:4px solid #151b24; box-shadow:0 12px 28px rgba(0,0,0,.38); overflow:hidden; }
.hp-empty-lessons-body { padding:10px; }
.hp-empty-lessons-body .hp-table-wrap { max-height:420px; min-height:320px; border:1px solid #b8bdc1; background:#f7f7f7; }
.hp-empty-lessons-body .hp-table th { position:sticky; top:0; z-index:1; background:#6d6d6d; color:#fff; border-color:#d7d7d7; height:28px; }
.hp-empty-lessons-body .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.hp-empty-lessons-body .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.hp-empty-lessons-body .hp-table tr { cursor:pointer; }
.hp-empty-lessons-body .hp-table tr.selected td { background:#4048f2 !important; color:#fff; }
.hp-empty-lessons-body .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.hp-empty-lessons-actions { display:flex; justify-content:flex-end; gap:10px; padding-top:10px; }
.hp-empty-lessons-actions .hp-wide-btn { width:120px; min-height:36px; }
.hp-form-card, .hp-transfer-box, .hp-mini-panel { background:#dcdedb; border:0; box-shadow:none; padding:10px; }
.hp-field { display:grid; grid-template-columns:118px 1fr; align-items:center; gap:8px; margin-bottom:8px; }
.hp-field span { color:#222; font-size:12px; font-weight:600; }
.hp-inline-form select, .hp-field input, .hp-field select, .hp-table input { width:100%; border:2px solid #a9b1b8; background:#edf2f4; color:#111827; padding:5px 7px; font-size:12px; outline:none; border-radius:2px; min-height:28px; }
.hp-row-actions { display:grid; gap:13px; width:190px; margin:34px auto 0; }
.hp-row-actions button, .hp-wide-btn, .hp-actions-panel button { min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#cfd3d5); color:#222; font-size:13px; font-weight:800; cursor:pointer; box-shadow:inset 0 1px 0 #fff; border-radius:5px; }
.hp-row-actions .danger, .hp-link-btn.danger { color:#b91c1c; }
.hp-table-wrap, .hp-grid-scroll { overflow:auto; width:100%; }
.hp-table { width:100%; border-collapse:collapse; background:#cfe7f1; }
.hp-table th, .hp-table td { border:2px solid #8aa0ad; padding:5px 7px; font-size:12px; text-align:left; vertical-align:middle; height:26px; white-space:nowrap; }
.hp-table th { background:#3d4855; color:#fff; font-weight:900; text-align:center; }
.hp-table tr:nth-child(even) td { background:#d8edf5; }
.hp-table tr.selected td, .hp-table tr:hover td { background:#062f78 !important; color:#fff; }
.hp-link-btn { border:0; background:transparent; color:#003f9e; font-size:12px; font-weight:900; cursor:pointer; margin-right:8px; }
.hp-current-draft-row { display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:12px; padding:12px 14px; border:1px solid #93c5fd; background:#dbeafe; color:#1e3a8a; }
.hp-current-draft-row strong { font-size:14px; }
.hp-current-draft-row span { font-size:12px; font-weight:700; white-space:nowrap; }
.hp-panel-title { display:flex; align-items:center; justify-content:center; color:#222; font-size:13px; font-weight:900; text-transform:uppercase; margin-bottom:8px; }
.hp-list-box { padding:0; background:#dcdedb; border:0; overflow:auto; max-height:560px; }
.hp-list-box button { width:100%; display:flex; align-items:center; justify-content:space-between; gap:8px; border:1px solid #a9b1b8; background:#cfe7f1; padding:6px 8px; margin:0 0 2px; color:#111; cursor:pointer; text-align:left; font-size:12px; }
.hp-list-box button.active { background:#062f78; color:#fff; }
.hp-list-box button span { color:inherit; opacity:.8; }
.hp-settings-head { display:grid; grid-template-columns:1fr 92px 92px 74px; gap:8px; align-items:end; margin-bottom:10px; }
.hp-transfer-row { display:flex; align-items:center; justify-content:space-between; gap:8px; border:1px solid #9aa8b2; background:#cfe7f1; padding:5px 7px; margin-bottom:2px; font-size:12px; font-weight:700; }
.hp-transfer-row button { border:1px solid #b7bbbf; background:linear-gradient(#f4f5f5,#d0d4d6); padding:4px 8px; cursor:pointer; font-size:11px; font-weight:900; }
.hp-assignment-layout { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; margin-bottom:10px; }
.hp-check { display:flex; align-items:center; gap:6px; font-size:12px; font-weight:800; color:#222; }
.hp-muted { color:#60656a; font-size:12px; margin:0; }
.hp-cell-summary { display:grid; gap:2px; margin-bottom:8px; }
.hp-cell-summary strong { font-size:12px; }
.hp-cell-summary span, .hp-cell-summary small { color:#4b5563; font-size:12px; }
.hp-inline-form { display:grid; grid-template-columns:1fr; gap:5px; }

.prag-program-tabs { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; margin-bottom:8px; }
.prag-program-tabs button { height:48px; border:2px solid #bfc4c8; background:linear-gradient(#ededed,#cfd3d4); color:#333; font-size:13px; font-weight:800; cursor:pointer; }
.prag-program-tabs button.active { background:#e8ecef; border-color:#7d8e9e; box-shadow:inset 0 0 0 1px #fff; }
.hp-program-warnings { display:flex; flex-direction:column; gap:3px; max-height:90px; overflow:auto; margin-bottom:8px; background:#fff5d6; border:1px solid #d1b456; color:#7a4b00; padding:6px 10px; font-size:12px; }
.prag-program-layout { display:grid; grid-template-columns:210px minmax(0,1fr); gap:8px; }
.prag-program-main { min-width:0; }
.prag-program-main.full { display:flex; flex-direction:column; gap:8px; }
.prag-program-list { max-height:500px; }
.prag-program-bottom.wide { display:grid; grid-template-columns:360px minmax(0,1fr) 180px; gap:8px; margin-top:0; align-items:start; }
.prag-program-bottom.wide .hp-table-wrap { max-height:260px; min-height:190px; border:1px solid #9aa8b2; background:#fff; }
.prag-program-bottom.wide .prag-program-list .hp-table-wrap { max-height:260px; }
.prag-program-side { display:flex; flex-direction:column; gap:8px; min-width:0; }
.prag-color-legend { display:grid; grid-template-columns:42px 1fr; border:1px solid #9aa8b2; background:#fff; }
.prag-color-legend div { display:contents; }
.prag-color-legend b, .prag-color-legend span { min-height:28px; border-bottom:1px solid #d1d5db; display:flex; align-items:center; padding:3px 8px; font-size:12px; font-weight:900; }
.prag-color-legend b { justify-content:center; border-right:1px solid #d1d5db; }
.prag-side-actions { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
.prag-side-actions button { min-height:34px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#d7dbde); color:#8a8f96; font-weight:900; border-radius:3px; }
.prag-time-row { display:grid; grid-template-columns:1fr 52px; gap:4px; }
.prag-time-row span, .prag-counts strong { min-height:28px; border:2px solid #c4c8cc; background:#f6f5f2; display:flex; align-items:center; justify-content:center; font-weight:900; color:#4b5563; }
.prag-counts { display:grid; gap:5px; }
.prag-counts label { display:grid; grid-template-columns:1fr 62px; align-items:center; gap:6px; font-size:12px; font-weight:900; color:#4b5563; }
.prag-selected-row { display:grid; grid-template-columns:minmax(0,1fr) 220px; gap:8px; align-items:stretch; }
.prag-selected-row .hp-table-wrap { max-height:58px; overflow:hidden; }
.prag-selected-row strong { display:flex; align-items:center; justify-content:center; background:#dcdedb; color:#222; font-size:13px; font-weight:900; }
.prag-program-actions { display:grid; grid-template-columns:repeat(10, minmax(96px, 1fr)); gap:8px; margin-top:8px; }
.prag-program-actions button { min-height:40px; border:1px solid #c4c8cc; background:linear-gradient(#f9f9f8,#d4d8db); color:#222; font-size:13px; font-weight:900; cursor:pointer; border-radius:5px; box-shadow:inset 0 1px 0 #fff; }
.prag-program-actions button:disabled { color:#9ca3af; background:linear-gradient(#f2f2f2,#d9dcde); cursor:not-allowed; }
.prag-program-shortcuts { display:grid; grid-template-columns:1fr 1fr 1fr; align-items:center; min-height:24px; margin-top:4px; border-top:1px solid #b9bec3; color:#59636e; font-size:12px; font-weight:800; }
.prag-program-shortcuts span { padding:3px 8px; border-right:1px solid #c8ccd0; }
.prag-lesson-menu { position:fixed; z-index:1010; width:210px; background:#d8d4cf; border:3px solid #22272f; box-shadow:0 10px 24px rgba(0,0,0,.42); padding:4px 0; }
.prag-lesson-menu button { width:100%; min-height:28px; border:0; border-bottom:1px solid #b6b0aa; background:transparent; color:#2b2b2b; text-align:left; padding:5px 14px; font-size:12px; font-weight:800; text-transform:uppercase; cursor:pointer; }
.prag-lesson-menu button:hover, .prag-lesson-menu button.active { background:#a04925; color:#fff; }
.hp-placement-window { max-width:520px !important; }
.hp-placement-window .hp-form-card { padding:16px; }
.hp-auto-settings-window { max-width:680px !important; }
.hp-auto-settings-body { display:grid; grid-template-columns:minmax(0,1fr) 190px 130px; gap:14px; padding:14px; background:#ebe8e4; }
.hp-auto-settings-form { display:grid; gap:14px; }
.hp-auto-settings-form label { display:grid; grid-template-columns:minmax(0,1fr) 48px; gap:10px; align-items:center; color:#4b4b4b; font-size:13px; font-weight:700; }
.hp-auto-settings-form input, .hp-auto-settings-form select { width:48px; height:26px; border:2px solid #b9b9b9; background:#f8f8f8; color:#222; text-align:center; font-size:13px; font-weight:800; }
.hp-auto-settings-days { display:flex; flex-direction:column; gap:7px; padding:4px 0; }
.hp-auto-settings-days strong { color:#333; font-size:13px; text-transform:uppercase; }
.hp-auto-settings-days label { display:grid; grid-template-columns:18px 1fr 48px; align-items:center; gap:6px; color:#333; font-size:12px; font-weight:800; }
.hp-auto-settings-days input[type="number"] { width:48px; height:24px; border:1px solid #b9b9b9; background:#fff; text-align:center; font-weight:800; }
.hp-auto-settings-actions { display:flex; flex-direction:column; gap:14px; padding-top:8px; }
.hp-auto-settings-actions button { min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f9f9f8,#d4d8db); color:#222; font-size:13px; font-weight:900; cursor:pointer; border-radius:5px; box-shadow:inset 0 1px 0 #fff; }
.hp-auto-settings-actions button:first-child { background:linear-gradient(#ffb07a,#ef8b52); }
.hp-print-window { max-width:560px !important; }
.hp-print-body { display:grid; grid-template-columns:1fr 270px; gap:22px; padding:14px; background:#ebe8e4; }
.hp-print-options { display:grid; gap:20px; }
.hp-print-group { display:grid; gap:18px; min-height:102px; padding:18px 12px; background:#f2f0ec; border:2px solid #d3d0cc; }
.hp-print-group label { display:flex; align-items:center; gap:9px; color:#333; font-size:13px; font-weight:900; }
.hp-print-group input { width:16px; height:16px; accent-color:#e46f3f; }
.hp-print-actions { display:flex; flex-direction:column; justify-content:flex-start; gap:16px; padding-top:8px; }
.hp-print-actions button { min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f9f9f8,#d4d8db); color:#222; font-size:13px; font-weight:900; cursor:pointer; border-radius:5px; box-shadow:inset 0 1px 0 #fff; white-space:normal; line-height:1.2; padding:8px 10px; }
.hp-schedule-grid { width:100%; min-width:820px; border-collapse:collapse; table-layout:fixed; background:#cfe7f1; }
.hp-schedule-grid th, .hp-schedule-grid td { border:2px solid #6e8799; padding:4px; height:61px; vertical-align:top; }
.hp-schedule-grid th { background:#77b6e5; color:#17324c; font-size:12px; font-weight:900; text-align:left; }
.hp-period-time-button { width:100%; min-height:51px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; border:0; padding:2px; background:transparent; color:inherit; cursor:pointer; }
.hp-period-time-button strong { font-size:14px; line-height:1; }
.hp-period-time-button span { font-size:10px; line-height:1.2; white-space:nowrap; }
.hp-period-time-button:hover { background:rgba(255,255,255,.25); }
.hp-schedule-grid .sticky-day { width:88px; position:sticky; left:0; z-index:2; }
.hp-schedule-grid td.inactive { background:#bfc7c9; }
.hp-schedule-grid td.light { background:#c8e8c4; }
.hp-schedule-grid td.dark { background:#161b24; color:#fff; }
.hp-schedule-grid td.availability-green { background:#34d399 !important; color:#052e1b; box-shadow:inset 0 0 0 3px #047857; cursor:pointer; }
.hp-schedule-grid td.availability-black { background:#020617 !important; color:#fff; box-shadow:inset 0 0 0 3px #475569; cursor:not-allowed; }
.hp-schedule-grid td.availability-red { background:#ef4444 !important; color:#fff; box-shadow:inset 0 0 0 3px #991b1b; cursor:not-allowed; }
.hp-schedule-grid td.class-closed, .hp-schedule-grid td.teacher-closed { background:#ef4444 !important; color:#fff; box-shadow:inset 0 0 0 3px #7f1d1d; cursor:not-allowed; }
.hp-closed-label { display:flex; align-items:center; justify-content:center; height:100%; color:#fff; font-size:12px; font-weight:900; text-transform:uppercase; }
.hp-lesson.pragmatic-lesson { position:relative; min-height:48px; border:0; background:transparent; color:inherit; padding:3px; display:flex; flex-direction:column; gap:1px; cursor:grab; overflow:hidden; min-width:0; }
.hp-lesson.pragmatic-lesson b, .hp-lesson.pragmatic-lesson span, .hp-lesson.pragmatic-lesson small { max-width:100%; font-size:11px; line-height:1.08; color:inherit; font-weight:900; overflow-wrap:anywhere; word-break:normal; white-space:normal; }
.hp-lesson.pragmatic-lesson b, .hp-lesson.pragmatic-lesson small { text-transform:uppercase; }
.hp-lesson.pragmatic-lesson .teacher-full-name { text-transform:none; }
.hp-lesson.pragmatic-lesson.teacherless span { font-size:10px; color:#7f1d1d; }
.hp-schedule-grid td.dark .hp-lesson.pragmatic-lesson.teacherless span { color:#fecaca; }
.lesson-lock { position:absolute; right:2px; bottom:2px; font-size:9px; opacity:.8; }
.prag-period-panel { display:flex; flex-direction:column; gap:7px; padding:8px; border:1px solid #96a3af; background:#f8fafc; color:#1f2937; font-size:12px; font-weight:800; }
.prag-period-panel > strong { font-size:13px; }
.prag-period-list { display:flex; flex-direction:column; gap:5px; max-height:220px; overflow:auto; }
.prag-period-row { display:grid; grid-template-columns:minmax(54px,1fr) 24px 32px 24px; align-items:center; gap:4px; }
.prag-period-row.muted { opacity:.62; }
.prag-period-row label { min-width:0; display:flex; align-items:center; gap:4px; }
.prag-period-row input { width:13px; height:13px; margin:0; }
.prag-period-row span { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.prag-period-row b { min-height:24px; display:flex; align-items:center; justify-content:center; border:1px solid #b6c0ca; background:#fff; color:#111827; }
.prag-period-row button, .prag-period-save { min-height:24px; border:1px solid #9ca3af; background:linear-gradient(#fff,#dbe3ea); color:#111827; border-radius:4px; font-size:12px; font-weight:900; cursor:pointer; }
.prag-period-row button:disabled { cursor:not-allowed; color:#9ca3af; background:#edf0f2; }
.prag-period-save { min-height:28px; }
.prag-closed-panel { display:flex; flex-direction:column; gap:7px; padding:8px; border:1px solid #96a3af; background:#eef2f7; color:#1f2937; font-size:12px; font-weight:800; }
.prag-closed-panel > strong { font-size:13px; }
.prag-closed-panel select { height:28px; border:1px solid #9ca3af; background:#fff; color:#111827; font-size:12px; font-weight:800; }
.prag-closed-days { display:flex; flex-direction:column; gap:6px; max-height:220px; overflow:auto; }
.prag-closed-day { display:grid; grid-template-columns:72px 1fr; gap:6px; align-items:start; }
.prag-closed-day button { min-height:26px; border:1px solid #9ca3af; background:linear-gradient(#fff,#dbe3ea); color:#111827; border-radius:4px; font-size:11px; font-weight:900; cursor:pointer; }
.prag-closed-day button:disabled { opacity:.6; cursor:not-allowed; }
.prag-closed-day > div { display:flex; flex-wrap:wrap; gap:4px; }
.prag-closed-day label { min-width:30px; height:24px; display:flex; align-items:center; justify-content:center; gap:2px; border:1px solid #b6c0ca; background:#fff; border-radius:3px; font-size:11px; font-weight:900; }
.prag-closed-day label.closed { background:#ef4444; border-color:#991b1b; color:#fff; }
.prag-closed-day input { margin:0; width:12px; height:12px; }
.prag-program-bottom { display:grid; grid-template-columns:minmax(0,1fr) 290px; gap:8px; margin-top:8px; }
.prag-statusbar { display:flex; justify-content:space-between; gap:10px; padding:5px 4px 0; color:#333; font-size:12px; font-weight:800; }
.hp-unavailable button { width:100%; border:1px solid #9aa8b2; background:#c8e8c4; color:#064e3b; padding:4px; font-size:11px; font-weight:900; cursor:pointer; }
.hp-unavailable button.closed { background:#161b24; color:#fff; }
.hp-unavailable td.inactive { background:#bfc7c9; }
.hp-loading { min-height:420px; display:flex; align-items:center; justify-content:center; color:#333; font-weight:900; }

@media (max-width: 1200px) {
  .prag-content { grid-template-columns:200px 1fr; }
  .prag-logo-text { font-size:38px; }
  .prag-module-grid { grid-template-columns:repeat(4, minmax(110px,1fr)); gap:24px; }
  .prag-module b { font-size:16px; }
  .prag-toolbar-tile { width:92px; }
}
@media (max-width: 1024px) {
  .exam-manager-layer, .exam-nested-layer { padding:8px; align-items:stretch; }
  .sinavmatik-window, .exam-room-dialog, .exam-manager-window { width:100%; height:100%; max-height:none; }
  .sinavmatik-body { grid-template-columns:1fr; overflow:auto; }
  .sinavmatik-left, .sinavmatik-right { min-height:440px; overflow:visible; }
  .sinavmatik-right { min-height:520px; }
  .sinavmatik-top-lists { grid-template-columns:minmax(360px,1fr) minmax(180px,240px); }
  .sinavmatik-seat-grid { min-height:400px; }
  .sinavmatik-actions { grid-template-columns:repeat(9, minmax(108px,1fr)); overflow-x:auto; }
  .exam-room-body { overflow:auto; }
  .exam-room-layout { grid-template-columns:1fr; height:auto; }
  .exam-room-list-side, .exam-room-plan-side { min-height:480px; }
  .exam-room-body .hp-table-wrap { min-width:560px; }
  .exam-columns { overflow-x:auto; }
}
@media (max-width: 900px) {
  .prag-shell { min-height:auto; }
  .prag-content, .hp-window-grid.two, .hp-window-grid.two.narrow, .hp-window-grid.three, .hp-window-grid.settings, .hp-assignment-layout, .hp-teacher-assign-layout, .prag-program-layout, .prag-program-bottom, .prag-program-bottom.wide, .prag-selected-row { grid-template-columns:1fr; }
  .prag-program-actions, .prag-program-shortcuts { grid-template-columns:1fr; }
  .hp-class-subjects { grid-template-columns:1fr; grid-template-rows:auto; }
  .hp-class-subject-actions, .hp-class-subject-status { grid-column:auto; grid-template-columns:1fr; }
  .hp-teacher-assign-actions, .hp-teacher-assign-status { grid-template-columns:1fr; }
  .hp-copy-body { grid-template-columns:1fr; }
  .hp-copy-summary div { grid-template-columns:1fr; gap:4px; }
  .hp-common-body { grid-template-columns:1fr; min-height:auto; }
  .prag-brand { display:none; }
  .prag-module-grid { grid-template-columns:repeat(2, minmax(120px,1fr)); padding:0; }
  .prag-bottom-actions { position:static; padding:12px; flex-direction:column; }
  .prag-bottom-left, .prag-support { display:none; }
  .hp-settings-head { grid-template-columns:1fr; }
}
`;
